import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams, Link, useLocation } from 'react-router-dom';
import { APP_VERSION, BUILD_DATE } from '../../version';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../../components/ui/dialog';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Switch } from '../../components/ui/switch';
import {
  Rocket, Target, Users, TrendingUp, CheckCircle, ArrowRight, Star, Phone, Mail, MapPin,
  Play, ChevronDown, Sparkles, PenTool, Video, Globe, Smartphone, Menu, X, MessageCircle,
  Calendar, Award, Zap, BarChart3, Shield, Clock, ChevronRight, Download, PhoneCall,
  FileText, ArrowUp, ExternalLink, Quote, Heart, Lightbulb, Palette, Code, Camera, Share2,
  Eye, MousePointer, Layers, Send, Coffee, Briefcase, Building, Search, Monitor, Headphones,
  Package, Settings, Image, Film, Layout, ShoppingBag, CircleDot, ArrowDown, FileDown,
  Cpu, LineChart, PieChart, Gauge, Megaphone, Brush, Wand2, Bot, ChevronLeft, Crosshair,
  Flag, Timer, Trophy, Flame, Focus, Linkedin, Twitter, CreditCard, Truck, RefreshCw,
  Gift, Puzzle, Book, Type, File, HelpCircle, Plus, Home, Facebook, Instagram, Youtube,
  Server, AlertCircle, XCircle, Volume2, ShoppingCart, MessageSquare, BarChart, Pen, Gamepad2, Lock, User
} from 'lucide-react';
import { cn } from '../../lib/utils';
import api from '../../lib/axios';
import { toast } from 'sonner';
import { SEO_SERVICES, BLOG_ARTICLES, GENERAL_FAQ } from './seoData';
import ConfirmationModal from '../../components/ui/ConfirmationModal';
import TeamPage from './TeamPage';
import PortfolioPage from './PortfolioPage';
import { SiteFooter } from '../../components/website/SharedLayout';

// ============================================
// IMAGE URL HELPER
// ============================================
// Helper function to build correct image URL
// Uses current origin and converts old absolute URLs to relative
const getImageUrl = (url) => {
  if (!url) return null;
  
  // Extract relative path if URL contains /api/uploads/
  // This handles old URLs from different domains (emergent.host, emergentagent.com, etc.)
  if (url.includes('/api/uploads/')) {
    const match = url.match(/\/api\/uploads\/[^\s"']+/);
    if (match) {
      // Use current origin to build the URL
      const baseUrl = window.location.origin;
      return `${baseUrl}${match[0]}`;
    }
  }
  
  // If it's already an external URL (like unsplash), use as-is
  if (url.startsWith('http')) return url;
  
  // For relative URLs, prefix with current origin
  const baseUrl = window.location.origin;
  return `${baseUrl}${url.startsWith('/') ? '' : '/'}${url}`;
};

// ============================================
// AGENCY HOURS DATA & STATUS HELPER
// ============================================

// Full schedule with special hours support
// Default schedule (fallback if API fails)
const DEFAULT_SCHEDULE = {
  regular: {
    1: { name: 'Lundi', hours: [{ open: '9h00', close: '18h00' }] },
    2: { name: 'Mardi', hours: [{ open: '9h00', close: '18h00' }] },
    3: { name: 'Mercredi', hours: [{ open: '9h00', close: '18h00' }] },
    4: { name: 'Jeudi', hours: [{ open: '9h00', close: '18h00' }] },
    5: { name: 'Vendredi', hours: [{ open: '9h00', close: '18h00' }] },
    6: { name: 'Samedi', hours: [] },
    0: { name: 'Dimanche', hours: [] },
  },
  special: []
};

// Convert API schedule format to internal format
const convertScheduleFromApi = (apiSchedule) => {
  if (!apiSchedule?.regular) return DEFAULT_SCHEDULE;
  
  const dayMapping = {
    'monday': 1, 'tuesday': 2, 'wednesday': 3, 'thursday': 4,
    'friday': 5, 'saturday': 6, 'sunday': 0
  };
  
  const dayNames = {
    1: 'Lundi', 2: 'Mardi', 3: 'Mercredi', 4: 'Jeudi',
    5: 'Vendredi', 6: 'Samedi', 0: 'Dimanche'
  };
  
  const converted = { regular: {}, special: [] };
  
  Object.entries(apiSchedule.regular).forEach(([dayName, data]) => {
    const dayNum = dayMapping[dayName];
    if (dayNum !== undefined) {
      if (data.closed || !data.open) {
        converted.regular[dayNum] = { name: dayNames[dayNum], hours: [] };
      } else {
        // Handle half-day format
        const hours = [];
        if (data.morning_open && data.morning_close) {
          hours.push({ open: data.morning_open, close: data.morning_close });
        }
        if (data.afternoon_open && data.afternoon_close) {
          hours.push({ open: data.afternoon_open, close: data.afternoon_close });
        }
        // Simple format (open/close)
        if (hours.length === 0 && data.open && data.close) {
          hours.push({ open: data.open, close: data.close });
        }
        converted.regular[dayNum] = { name: dayNames[dayNum], hours };
      }
    }
  });
  
  // Ensure all days exist
  for (let i = 0; i <= 6; i++) {
    if (!converted.regular[i]) {
      converted.regular[i] = DEFAULT_SCHEDULE.regular[i];
    }
  }
  
  return converted;
};

const getTodaySchedule = (schedule = DEFAULT_SCHEDULE) => {
  const today = new Date();
  const dateStr = today.toISOString().split('T')[0];
  
  // Check for special hours first
  const specialDay = schedule.special?.find(s => s.date === dateStr);
  if (specialDay) {
    return { ...specialDay, isToday: true };
  }
  
  // Return regular schedule
  const dayOfWeek = today.getDay();
  return { ...schedule.regular[dayOfWeek], isToday: true, isSpecial: false };
};

const hasSpecialHours = (schedule = DEFAULT_SCHEDULE) => {
  const today = new Date();
  const weekFromNow = new Date(today);
  weekFromNow.setDate(weekFromNow.getDate() + 7);
  
  return (schedule.special || []).some(s => {
    const specialDate = new Date(s.date);
    return specialDate >= today && specialDate <= weekFromNow;
  });
};

const getAgencyStatus = (schedule = DEFAULT_SCHEDULE) => {
  const now = new Date();
  const day = now.getDay(); // 0 = Sunday, 1 = Monday, etc.
  const hours = now.getHours();
  const minutes = now.getMinutes();
  const currentTime = hours * 60 + minutes; // Convert to minutes
  const dateStr = now.toISOString().split('T')[0];
  
  // Helper function to find next open day
  const findNextOpenDay = (startDay) => {
    for (let i = 1; i <= 7; i++) {
      const checkDay = (startDay + i) % 7;
      const daySchedule = schedule.regular[checkDay];
      if (daySchedule && daySchedule.hours && daySchedule.hours.length > 0) {
        return { dayIndex: checkDay, schedule: daySchedule };
      }
    }
    return null;
  };
  
  // Check for special hours first
  const specialDay = (schedule.special || []).find(s => s.date === dateStr);
  if (specialDay) {
    if (specialDay.hours.length === 0) {
      return { status: 'closed', label: 'Fermé', sublabel: specialDay.name, color: 'text-red-500', bg: 'bg-red-50', dot: 'bg-red-500', isSpecial: true };
    }
    const firstSession = specialDay.hours[0];
    const openHour = parseInt(firstSession.open.split('h')[0]);
    const closeHour = parseInt(firstSession.close.split('h')[0]);
    const closeMin = parseInt(firstSession.close.split('h')[1] || '0');
    const specialClose = closeHour * 60 + closeMin;
    
    if (currentTime >= openHour * 60 && currentTime < specialClose) {
      return { status: 'open', label: 'Ouvert', sublabel: `Jusqu'à ${firstSession.close}`, color: 'text-green-600', bg: 'bg-green-50', dot: 'bg-green-500', isSpecial: true };
    }
    return { status: 'closed', label: 'Fermé', sublabel: 'Horaires spéciales', color: 'text-red-500', bg: 'bg-red-50', dot: 'bg-red-500', isSpecial: true };
  }
  
  // Get today's schedule from dynamic data
  const todaySchedule = schedule.regular[day];
  if (!todaySchedule || todaySchedule.hours.length === 0) {
    // Find next open day (not just tomorrow, but the actual next day that's open)
    const nextOpen = findNextOpenDay(day);
    if (nextOpen) {
      const nextDayName = nextOpen.schedule.name;
      const nextOpenTime = nextOpen.schedule.hours[0]?.open || '9h';
      return { status: 'closed', label: 'Fermé', sublabel: `Réouvre ${nextDayName} ${nextOpenTime}`, color: 'text-red-500', bg: 'bg-red-50', dot: 'bg-red-500' };
    }
    return { status: 'closed', label: 'Fermé', sublabel: '', color: 'text-red-500', bg: 'bg-red-50', dot: 'bg-red-500' };
  }
  
  // Parse hours from schedule
  const parseTime = (timeStr) => {
    if (!timeStr) return 0;
    const parts = timeStr.replace('h', ':').split(':');
    return parseInt(parts[0]) * 60 + (parseInt(parts[1]) || 0);
  };
  
  // Check each session
  for (let i = 0; i < todaySchedule.hours.length; i++) {
    const session = todaySchedule.hours[i];
    const sessionOpen = parseTime(session.open);
    const sessionClose = parseTime(session.close);
    
    // Currently in this session
    if (currentTime >= sessionOpen && currentTime < sessionClose) {
      if (currentTime >= sessionClose - 30) {
        return { status: 'closing-soon', label: 'Ferme bientôt', sublabel: `Jusqu'à ${session.close}`, color: 'text-orange-500', bg: 'bg-orange-50', dot: 'bg-orange-500 animate-pulse' };
      }
      return { status: 'open', label: 'Ouvert', sublabel: `Jusqu'à ${session.close}`, color: 'text-green-600', bg: 'bg-green-50', dot: 'bg-green-500' };
    }
    
    // Before this session opens
    if (currentTime < sessionOpen) {
      if (currentTime >= sessionOpen - 60) {
        return { status: 'opening-soon', label: 'Ouvre bientôt', sublabel: `À ${session.open}`, color: 'text-blue-500', bg: 'bg-blue-50', dot: 'bg-blue-500 animate-pulse' };
      }
      // Check if there's a previous session (lunch break)
      if (i > 0) {
        return { status: 'lunch', label: 'Pause', sublabel: `Reprise ${session.open}`, color: 'text-amber-500', bg: 'bg-amber-50', dot: 'bg-amber-500' };
      }
      return { status: 'closed', label: 'Fermé', sublabel: `Ouvre à ${session.open}`, color: 'text-red-500', bg: 'bg-red-50', dot: 'bg-red-500' };
    }
  }
  
  // After all sessions closed - find next open day
  const nextOpen = findNextOpenDay(day);
  if (nextOpen) {
    const nextDayName = nextOpen.schedule.name;
    const nextOpenTime = nextOpen.schedule.hours[0]?.open || '9h';
    return { status: 'closed', label: 'Fermé', sublabel: `Réouvre ${nextDayName} ${nextOpenTime}`, color: 'text-red-500', bg: 'bg-red-50', dot: 'bg-red-500' };
  }
  return { status: 'closed', label: 'Fermé', sublabel: '', color: 'text-red-500', bg: 'bg-red-50', dot: 'bg-red-500' };
};

// ============================================
// ANIMATED COMPONENTS
// ============================================

const AnimatedCounter = ({ end, duration = 2000, suffix = '', prefix = '' }) => {
  const [count, setCount] = useState(0);
  const [started, setStarted] = useState(false);
  const countRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && !started) {
          setStarted(true);
          let start = 0;
          const increment = end / (duration / 16);
          const timer = setInterval(() => {
            start += increment;
            if (start >= end) {
              setCount(end);
              clearInterval(timer);
            } else {
              setCount(Math.floor(start));
            }
          }, 16);
        }
      },
      { threshold: 0.5 }
    );
    if (countRef.current) observer.observe(countRef.current);
    return () => observer.disconnect();
  }, [end, duration, started]);

  return <span ref={countRef}>{prefix}{count}{suffix}</span>;
};

const useScrollReveal = (threshold = 0.1) => {
  const ref = useRef(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [threshold]);

  return [ref, isVisible];
};

const AnimatedSection = ({ children, className, delay = 0 }) => {
  const [ref, isVisible] = useScrollReveal(0.1);
  return (
    <div 
      ref={ref}
      className={cn(
        "transition-all duration-700 ease-out",
        isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8",
        className
      )}
      style={{ transitionDelay: `${delay}ms` }}
    >
      {children}
    </div>
  );
};

const AnimatedCard = ({ children, className, delay = 0, hover = true }) => {
  const [ref, isVisible] = useScrollReveal(0.1);
  return (
    <div 
      ref={ref}
      className={cn(
        "transition-all duration-500 ease-out",
        isVisible ? "opacity-100 translate-y-0 scale-100" : "opacity-0 translate-y-10 scale-95",
        hover && "hover:shadow-2xl hover:-translate-y-2 hover:scale-[1.02]",
        className
      )}
      style={{ transitionDelay: `${delay}ms` }}
    >
      {children}
    </div>
  );
};

// Diagonal Scrolling Banner Component
const DiagonalMarquee = ({ reverse = false, speed = 30 }) => {
  const keywords = [
    "STRATÉGIE", "CRÉATIVITÉ", "DESIGN", "INNOVATION", "DIGITAL", "BRANDING",
    "VISIBILITÉ", "PERFORMANCE", "IMPACT", "COMMUNICATION", "WEB", "VIDÉO",
    "MARKETING", "IDENTITÉ", "SUCCÈS", "CROISSANCE", "ENGAGEMENT", "CONVERSION"
  ];
  
  return (
    <div className={cn(
      "flex whitespace-nowrap",
      reverse ? "animate-marquee-reverse" : "animate-marquee"
    )}>
      {[...keywords, ...keywords, ...keywords].map((word, idx) => (
        <span key={idx} className="mx-8 text-2xl md:text-4xl font-black tracking-wider opacity-20 hover:opacity-50 transition-opacity">
          {word}
          <span className="mx-8 text-cyan-500">✦</span>
        </span>
      ))}
    </div>
  );
};

// Logo Ticker Component
const LogoTicker = ({ clients = [], reverse = false, speed = "normal" }) => {
  const speedClass = speed === "slow" ? "animate-scroll-slow" : speed === "fast" ? "animate-scroll-fast" : "animate-scroll";
  
  // Early return if no clients
  if (!clients || clients.length === 0) return null;
  
  // Triple the clients array to ensure seamless infinite loop
  const extendedClients = [...clients, ...clients, ...clients, ...clients];
  
  return (
    <div className="relative overflow-hidden">
      {/* Blur gradient overlays for depth effect */}
      <div className="absolute left-0 top-0 bottom-0 w-32 bg-gradient-to-r from-white via-white/80 to-transparent z-10 pointer-events-none" />
      <div className="absolute right-0 top-0 bottom-0 w-32 bg-gradient-to-l from-white via-white/80 to-transparent z-10 pointer-events-none" />
      
      <div className={cn(
        "flex items-center gap-16 whitespace-nowrap",
        reverse ? "animate-scroll-reverse" : speedClass
      )}>
        {extendedClients.map((client, idx) => (
          <a 
            key={idx}
            href={client.url || '#'}
            target={client.url && client.url !== '#' ? '_blank' : '_self'}
            rel="noopener noreferrer"
            className="flex-shrink-0 flex items-center justify-center h-20 px-4 grayscale hover:grayscale-0 transition-all duration-500 opacity-60 hover:opacity-100 hover:scale-110"
            title={client.name}
          >
            <img 
              src={client.logo} 
              alt={client.name}
              className="max-h-14 max-w-[140px] object-contain filter drop-shadow-sm"
              onError={(e) => { 
                e.target.style.display = 'none'; 
                if(e.target.nextSibling) e.target.nextSibling.style.display = 'flex'; 
              }}
            />
            <span className="hidden text-xl font-bold text-slate-400 items-center">{client.name}</span>
          </a>
        ))}
      </div>
    </div>
  );
};

// Reviews Carousel Component - Scrolling testimonials
const ReviewsCarousel = ({ reviews = [], speed = "normal" }) => {
  const speedClass = speed === "slow" ? "animate-scroll-slow" : speed === "fast" ? "animate-scroll-fast" : "animate-scroll";
  
  // Need at least some reviews to display
  if (!reviews || reviews.length === 0) return null;
  
  // Triple the reviews for seamless loop
  const extendedReviews = [...reviews, ...reviews, ...reviews];
  
  return (
    <div className="relative overflow-hidden py-4">
      {/* Blur gradient overlays */}
      <div className="absolute left-0 top-0 bottom-0 w-24 bg-gradient-to-r from-white via-white/80 to-transparent z-10 pointer-events-none" />
      <div className="absolute right-0 top-0 bottom-0 w-24 bg-gradient-to-l from-white via-white/80 to-transparent z-10 pointer-events-none" />
      
      <div className={cn("flex items-stretch gap-6 whitespace-nowrap", speedClass)}>
        {extendedReviews.map((review, idx) => (
          <div 
            key={idx}
            className="flex-shrink-0 w-[320px] bg-gradient-to-br from-slate-50 to-white rounded-xl p-5 border border-slate-100 shadow-sm hover:shadow-md transition-shadow"
          >
            <div className="flex items-center gap-3 mb-3">
              {/* Avatar or initial */}
              <div className="w-10 h-10 rounded-full bg-gradient-to-br from-cyan-500 to-blue-600 flex items-center justify-center text-white font-bold text-sm">
                {review.author_name?.charAt(0) || 'A'}
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-semibold text-slate-800 text-sm truncate">{review.author_name || 'Client'}</p>
                <div className="flex items-center gap-1">
                  {[...Array(5)].map((_, i) => (
                    <Star 
                      key={i} 
                      className={cn(
                        "w-3.5 h-3.5",
                        i < (review.rating || 5) ? "fill-yellow-400 text-yellow-400" : "text-slate-200"
                      )} 
                    />
                  ))}
                </div>
              </div>
              {/* Google icon */}
              <svg className="w-5 h-5 text-slate-400" viewBox="0 0 24 24" fill="currentColor">
                <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
              </svg>
            </div>
            <p className="text-slate-600 text-sm whitespace-normal line-clamp-3 leading-relaxed">
              "{review.text || review.content || 'Excellent travail et accompagnement de qualité !'}"
            </p>
          </div>
        ))}
      </div>
    </div>
  );
};

// ============================================
// SERVICES DATA
// ============================================

// TIMELINE DE LA COM' - Chronologie de la communication d'entreprise
const TIMELINE_COM = [
  { id: 1, name: 'Choix du nom', description: 'Naming de votre entreprise/marque', icon: 'Lightbulb', phase: 'Fondation' },
  { id: 2, name: 'Recherche INPI', description: 'Vérification disponibilité de la marque', icon: 'Search', phase: 'Fondation' },
  { id: 3, name: 'Dépôt de marque', description: 'Protection juridique (verbale ou figurative)', icon: 'Shield', phase: 'Fondation' },
  { id: 4, name: 'Création du logo', description: 'Identité visuelle de base', icon: 'Palette', phase: 'Identité' },
  { id: 5, name: 'Charte graphique', description: 'Système visuel complet', icon: 'Book', phase: 'Identité' },
  { id: 6, name: 'Site web', description: 'Présence digitale', icon: 'Globe', phase: 'Digital' },
  { id: 7, name: 'Vidéo de présentation', description: 'Communication audiovisuelle', icon: 'Video', phase: 'Digital' },
  { id: 8, name: 'Tenues personnalisées', description: 'Uniformes, vêtements corporate', icon: 'Users', phase: 'Déploiement' },
  { id: 9, name: 'Supports print', description: 'Cartes de visite, brochures', icon: 'FileText', phase: 'Déploiement' },
  { id: 10, name: 'Campagnes', description: 'Acquisition et branding', icon: 'Megaphone', phase: 'Croissance' }
];

// Graphic Design Solutions - Enriched with Timeline position and detailed content
const GRAPHIC_SOLUTIONS = [
  {
    id: 'logo-simple',
    name: 'Logo Simple',
    icon: Sparkles,
    description: 'Un logo professionnel pour démarrer',
    longDesc: 'Création d\'un logo moderne et mémorable, parfait pour lancer votre activité avec une identité visuelle forte.',
    priceFrom: 350,
    priceRange: '350€ - 600€ HT',
    deliveryTime: '1-2 semaines',
    features: ['3 propositions', 'Fichiers HD (PNG, JPG)', 'Fichier vectoriel (AI, SVG)', '2 révisions incluses'],
    includes: ['Brief créatif', 'Recherches graphiques', 'Livraison formats web & print'],
    // Timeline & Why
    timelinePosition: 4,
    timelinePhase: 'Identité',
    whyDoIt: 'Le logo est le pilier de votre identité visuelle. C\'est la première chose que vos clients voient et mémorisent. Un logo professionnel inspire confiance et crédibilité dès le premier contact.',
    whenToDoIt: 'Dès que vous avez validé votre nom d\'entreprise et effectué les recherches INPI. C\'est la base avant tout autre support de communication.',
    idealFor: ['Créateurs d\'entreprise', 'Auto-entrepreneurs', 'Petits budgets', 'Tests de concept'],
    notIdealFor: ['Entreprises établies cherchant un repositionnement', 'Besoins de déclinaisons multiples'],
    examples: [
      { name: 'Boulangerie Artisanale', image: 'https://images.unsplash.com/photo-1558961363-fa8fdf82db35?w=400', category: 'Commerce' },
      { name: 'Coach Bien-être', image: 'https://images.unsplash.com/photo-1545205597-3d9d02c29597?w=400', category: 'Services' },
      { name: 'Startup Tech', image: 'https://images.unsplash.com/photo-1519389950473-47ba0277781c?w=400', category: 'Tech' }
    ]
  },
  {
    id: 'logo-premium',
    name: 'Logo Premium',
    icon: Award,
    badge: 'Populaire',
    description: 'Un logo avec déclinaisons complètes',
    longDesc: 'Logo principal + versions alternatives (monochrome, horizontal, icône seule) pour tous vos supports.',
    priceFrom: 800,
    priceRange: '800€ - 1 500€ HT',
    deliveryTime: '2-3 semaines',
    features: ['5 propositions', 'Déclinaisons multiples', 'Tous formats', 'Révisions illimitées', 'Planche de présentation'],
    includes: ['Étude concurrentielle', 'Moodboard', 'Guide d\'utilisation basique'],
    timelinePosition: 4,
    timelinePhase: 'Identité',
    whyDoIt: 'Un logo premium avec déclinaisons vous permet d\'être cohérent sur tous vos supports : site web, réseaux sociaux, print, signalétique. Vous gagnez du temps et de l\'argent à long terme.',
    whenToDoIt: 'Recommandé pour toute entreprise qui prévoit de communiquer sur plusieurs canaux. Idéal dès le lancement ou lors d\'un repositionnement.',
    idealFor: ['PME en croissance', 'Startups financées', 'Entreprises multi-supports', 'Franchises'],
    notIdealFor: ['Tests de marché', 'Budget très limité'],
    examples: [
      { name: 'Cabinet Juridique', image: 'https://images.unsplash.com/photo-1589829545856-d10d557cf95f?w=400', category: 'Juridique' },
      { name: 'Agence Immobilière', image: 'https://images.unsplash.com/photo-1560518883-ce09059eeffa?w=400', category: 'Immobilier' },
      { name: 'Restaurant Gastronomique', image: 'https://images.unsplash.com/photo-1414235077428-338989a2e8c0?w=400', category: 'Restauration' }
    ]
  },
  {
    id: 'charte-graphique',
    name: 'Charte Graphique',
    icon: Book,
    description: 'Votre identité visuelle complète',
    longDesc: 'Document de référence définissant votre identité : logo, couleurs, typographies, iconographie et règles d\'utilisation.',
    priceFrom: 1500,
    priceRange: '1 500€ - 3 500€ HT',
    deliveryTime: '3-5 semaines',
    features: ['Logo complet', 'Palette couleurs', 'Typographies', 'Iconographie', 'Templates', 'Règles d\'espacement'],
    includes: ['Document PDF interactif', 'Fichiers sources', 'Formation utilisation'],
    timelinePosition: 5,
    timelinePhase: 'Identité',
    whyDoIt: 'La charte graphique garantit la cohérence de votre image sur tous les supports et dans le temps. Elle facilite le travail avec vos prestataires et protège votre marque.',
    whenToDoIt: 'Après la création du logo, avant de développer votre site web ou vos supports print. Indispensable si vous travaillez avec plusieurs intervenants.',
    idealFor: ['Entreprises avec équipe marketing', 'Franchises', 'Marques ambitieuses', 'Entreprises multi-sites'],
    notIdealFor: ['Micro-entreprises solo', 'Budgets très serrés'],
    examples: [
      { name: 'Marque de Mode', image: 'https://images.unsplash.com/photo-1441986300917-64674bd600d8?w=400', category: 'Mode' },
      { name: 'Groupe Hôtelier', image: 'https://images.unsplash.com/photo-1566073771259-6a8506099945?w=400', category: 'Hôtellerie' },
      { name: 'Réseau Franchise', image: 'https://images.unsplash.com/photo-1556742049-0cfed4f6a45d?w=400', category: 'Retail' }
    ]
  },
  {
    id: 'kit-reseaux-sociaux',
    name: 'Pack Communication',
    icon: Share2,
    description: 'Heures de création de contenus',
    longDesc: 'Un volume d\'heures dédié à la création de contenus variés : visuels réseaux sociaux, supports print, présentations, et tout type de création graphique récurrente.',
    priceFrom: 500,
    priceRange: '500€ - 2 000€ HT/mois',
    deliveryTime: 'Forfait mensuel',
    features: ['10-40h de création/mois', 'Visuels réseaux sociaux', 'Supports print (flyers, affiches)', 'Présentations PowerPoint', 'Bannières web', 'Retouches photos'],
    includes: ['Chef de projet dédié', 'Réunion mensuelle', 'Fichiers sources fournis'],
    timelinePosition: 9,
    timelinePhase: 'Déploiement',
    whyDoIt: 'Externalisez votre création graphique récurrente à coût maîtrisé. Plus besoin de graphiste à temps plein : vous disposez d\'un volume d\'heures flexible adapté à vos besoins du moment.',
    whenToDoIt: 'Quand vous avez des besoins de création réguliers mais pas assez pour embaucher un graphiste. Idéal après le lancement de votre communication.',
    idealFor: ['PME avec besoins réguliers', 'Services marketing', 'Agences en marque blanche', 'Community managers'],
    notIdealFor: ['Besoins ponctuels (préférer devis à la prestation)', 'Entreprises sans ligne graphique définie'],
    examples: [
      { name: 'Community Manager Indépendant', image: 'https://images.unsplash.com/photo-1611162616305-c69b3fa7fbe0?w=400', category: 'Social Media' },
      { name: 'PME Industrielle', image: 'https://images.unsplash.com/photo-1581091226825-a6a2a5aee158?w=400', category: 'Industrie' },
      { name: 'Réseau de Franchises', image: 'https://images.unsplash.com/photo-1556742049-0cfed4f6a45d?w=400', category: 'Retail' }
    ]
  },
  {
    id: 'brochure-catalogue',
    name: 'Brochure / Catalogue',
    icon: FileText,
    description: 'Supports print professionnels',
    longDesc: 'Conception de brochures commerciales, catalogues produits ou rapports annuels avec mise en page soignée.',
    priceFrom: 600,
    priceRange: '600€ - 2 500€ HT',
    deliveryTime: '2-4 semaines',
    features: ['Mise en page sur-mesure', 'Fichiers print HD', 'Maquette interactive', 'Jusqu\'à 20 pages'],
    includes: ['Conception graphique', 'Retouches photos', 'Fichier imprimeur'],
    timelinePosition: 9,
    timelinePhase: 'Déploiement',
    whyDoIt: 'Un support print professionnel laisse une impression durable. Idéal pour les salons, rendez-vous clients, ou présentation de gamme produits.',
    whenToDoIt: 'Une fois votre identité visuelle établie. Particulièrement pertinent avant un salon, un lancement produit ou une campagne commerciale.',
    idealFor: ['Entreprises B2B', 'Commerces avec gamme produits', 'Participants à des salons'],
    notIdealFor: ['Business 100% digital', 'Offres très simples'],
    examples: [
      { name: 'Fabricant Industriel', image: 'https://images.unsplash.com/photo-1581091226825-a6a2a5aee158?w=400', category: 'Industrie' },
      { name: 'Promoteur Immobilier', image: 'https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?w=400', category: 'Immobilier' },
      { name: 'Vignoble AOC', image: 'https://images.unsplash.com/photo-1506377247377-2a5b3b417ebb?w=400', category: 'Vin' }
    ]
  }
].sort((a, b) => a.priceFrom - b.priceFrom);

// Video Solutions - Enriched with Timeline position and detailed content
const VIDEO_SOLUTIONS = [
  {
    id: 'video-interview',
    name: 'Interview Vidéo',
    icon: Users,
    description: 'Témoignages et portraits',
    longDesc: 'Captation d\'interviews clients, collaborateurs ou dirigeants pour humaniser votre communication.',
    priceFrom: 800,
    priceRange: '800€ - 1 500€ HT',
    deliveryTime: '1-2 semaines',
    features: ['1-2h de tournage', 'Montage dynamique', 'Sous-titres', 'Musique libre de droits', 'Format 16:9 + carré'],
    includes: ['Préparation questions', 'Équipement pro', 'Formats réseaux sociaux'],
    // Timeline & Why
    timelinePosition: 7,
    timelinePhase: 'Digital',
    whyDoIt: 'Les témoignages vidéo sont 2x plus convaincants que les avis écrits. Ils humanisent votre entreprise et créent un lien de confiance avec vos prospects. 92% des acheteurs B2B regardent des témoignages avant de décider.',
    whenToDoIt: 'Dès que vous avez des clients satisfaits ! Idéal après 6 mois d\'activité ou suite à un projet réussi. Les témoignages récents ont plus d\'impact.',
    idealFor: ['Entreprises B2B', 'Services à forte valeur ajoutée', 'Coachs et consultants', 'Recrutement (marque employeur)'],
    notIdealFor: ['Produits grand public sans dimension émotionnelle', 'Entreprises très jeunes sans clients'],
    examples: [
      { name: 'Témoignage Client Immobilier', image: 'https://images.unsplash.com/photo-1560518883-ce09059eeffa?w=400', category: 'Immobilier' },
      { name: 'Portrait Dirigeant', image: 'https://images.unsplash.com/photo-1560250097-0b93528c311a?w=400', category: 'Corporate' },
      { name: 'Avis Client E-commerce', image: 'https://images.unsplash.com/photo-1556742049-0cfed4f6a45d?w=400', category: 'E-commerce' }
    ]
  },
  {
    id: 'video-presentation',
    name: 'Vidéo de Présentation',
    icon: Play,
    description: 'Présentez votre entreprise en 2min',
    longDesc: 'Vidéo corporate pour présenter votre entreprise, vos valeurs et votre équipe de manière professionnelle.',
    priceFrom: 1500,
    priceRange: '1 500€ - 3 500€ HT',
    deliveryTime: '2-4 semaines',
    features: ['Script personnalisé', 'Tournage 1 journée', 'Montage professionnel', 'Voix-off option', 'Motion titles'],
    includes: ['Repérage', 'Direction artistique', 'Colorimétrie'],
    // Timeline & Why
    timelinePosition: 7,
    timelinePhase: 'Digital',
    whyDoIt: 'Une vidéo de présentation augmente de 80% le temps passé sur votre site. Elle permet de transmettre votre personnalité, vos valeurs et de créer une connexion émotionnelle impossible avec du texte seul.',
    whenToDoIt: 'Après avoir défini votre identité visuelle (logo, charte). Idéal pour la page d\'accueil de votre site ou votre profil LinkedIn entreprise.',
    idealFor: ['TPE/PME souhaitant se démarquer', 'Entreprises de services', 'Recruteurs', 'Levée de fonds'],
    notIdealFor: ['Entreprises en restructuration', 'Activités confidentielles'],
    examples: [
      { name: 'Présentation Agence', image: 'https://images.unsplash.com/photo-1522071820081-009f0129c71c?w=400', category: 'Corporate' },
      { name: 'Visite Usine', image: 'https://images.unsplash.com/photo-1581091226825-a6a2a5aee158?w=400', category: 'Industrie' },
      { name: 'Film Marque Employeur', image: 'https://images.unsplash.com/photo-1552664730-d307ca884978?w=400', category: 'RH' }
    ]
  },
  {
    id: 'video-evenement',
    name: 'Captation Événement',
    icon: Camera,
    description: 'Immortalisez vos événements',
    longDesc: 'Couverture vidéo de vos événements : conférences, séminaires, lancements produits, soirées.',
    priceFrom: 1200,
    priceRange: '1 200€ - 4 000€ HT',
    deliveryTime: '1-3 semaines',
    features: ['Multi-caméras possible', 'Captation live', 'Teaser + film complet', 'Interviews sur place', 'Photos incluses'],
    includes: ['Équipe mobilisée', 'Montage aftermovie', 'Rushes fournis'],
    // Timeline & Why
    timelinePosition: 10,
    timelinePhase: 'Croissance',
    whyDoIt: 'Prolongez l\'impact de vos événements sur les réseaux sociaux. Un aftermovie génère du contenu pour plusieurs mois et valorise vos investissements événementiels. Les participants adorent se voir et partagent massivement.',
    whenToDoIt: 'Pour tout événement important : séminaire, salon, inauguration, anniversaire d\'entreprise. Prévoir au minimum 2 semaines avant l\'événement.',
    idealFor: ['Organisateurs d\'événements', 'Grandes entreprises', 'Associations', 'Salons professionnels'],
    notIdealFor: ['Événements confidentiels', 'Très petits budgets'],
    examples: [
      { name: 'Séminaire Annuel', image: 'https://images.unsplash.com/photo-1540575467063-178a50c2df87?w=400', category: 'Corporate' },
      { name: 'Inauguration Boutique', image: 'https://images.unsplash.com/photo-1441986300917-64674bd600d8?w=400', category: 'Retail' },
      { name: 'Gala Association', image: 'https://images.unsplash.com/photo-1511795409834-ef04bbd61622?w=400', category: 'Événementiel' }
    ]
  },
  {
    id: 'motion-design',
    name: 'Motion Design',
    icon: Wand2,
    description: 'Animations graphiques percutantes',
    longDesc: 'Vidéos animées pour expliquer un concept, présenter un produit ou dynamiser votre communication.',
    priceFrom: 2000,
    priceRange: '2 000€ - 5 000€ HT',
    deliveryTime: '3-5 semaines',
    features: ['Script & storyboard', 'Animation 2D/3D', 'Sound design', 'Voix-off', 'Durée 30s à 2min'],
    includes: ['Direction artistique', 'Illustrations custom', '3 allers-retours'],
    // Timeline & Why
    timelinePosition: 7,
    timelinePhase: 'Digital',
    whyDoIt: 'Le motion design explique des concepts complexes en quelques secondes. Parfait pour les services immatériels, les process techniques ou les données chiffrées. Taux de mémorisation 3x supérieur aux présentations statiques.',
    whenToDoIt: 'Quand vous devez expliquer quelque chose de complexe simplement : nouveau produit, process interne, chiffres clés. Après avoir une charte graphique établie pour la cohérence.',
    idealFor: ['Startups tech', 'Services B2B complexes', 'Formation', 'Présentations investisseurs'],
    notIdealFor: ['Entreprises artisanales cherchant l\'authenticité', 'Petits budgets'],
    examples: [
      { name: 'Explainer SaaS', image: 'https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=400', category: 'Tech' },
      { name: 'Infographie Animée', image: 'https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=400', category: 'Data' },
      { name: 'Pub Réseaux Sociaux', image: 'https://images.unsplash.com/photo-1611162616305-c69b3fa7fbe0?w=400', category: 'Social' }
    ]
  },
  {
    id: 'serie-video',
    name: 'Série Vidéo Entreprise',
    icon: Film,
    description: 'Contenu régulier pour engager',
    longDesc: 'Programme de vidéos régulières pour alimenter vos réseaux : épisodes thématiques, actualités, coulisses.',
    priceFrom: 3500,
    priceRange: '3 500€ - 8 000€ HT/trimestre',
    deliveryTime: 'Programme continu',
    features: ['4-8 vidéos/trimestre', 'Planning éditorial', 'Formats courts', 'Cohérence graphique', 'Sous-titres auto'],
    includes: ['Stratégie contenu', 'Calendrier éditorial', 'Reporting mensuel'],
    // Timeline & Why
    timelinePosition: 10,
    timelinePhase: 'Croissance',
    whyDoIt: 'La régularité est la clé sur les réseaux sociaux. Une série vidéo construit votre audience, établit votre expertise et maintient votre marque dans l\'esprit de vos cibles. Les entreprises qui publient régulièrement génèrent 67% plus de leads.',
    whenToDoIt: 'Une fois votre stratégie de communication définie et vos fondamentaux en place (logo, site, premiers contenus). Idéal pour passer à la vitesse supérieure sur les réseaux.',
    idealFor: ['Entreprises actives sur les réseaux', 'Marques B2C', 'Influenceurs corporate', 'Marque employeur'],
    notIdealFor: ['Entreprises sans stratégie social media', 'Budgets limités'],
    examples: [
      { name: 'Coulisses Équipe', image: 'https://images.unsplash.com/photo-1522071820081-009f0129c71c?w=400', category: 'Marque employeur' },
      { name: 'Tips & Conseils', image: 'https://images.unsplash.com/photo-1434030216411-0b793f4b4173?w=400', category: 'Expertise' },
      { name: 'Reels Produits', image: 'https://images.unsplash.com/photo-1607083206869-4c7672e72a8a?w=400', category: 'E-commerce' }
    ],
    hasCalendar: true,
    calendarEvents: [
      { date: '01/01', event: 'Nouvel An', type: 'fete', format: 'Story + Post', network: 'Instagram, LinkedIn' },
      { date: '14/02', event: 'Saint-Valentin', type: 'fete', format: 'Reel', network: 'Instagram, TikTok' },
      { date: '08/03', event: 'Journée Femmes', type: 'engagement', format: 'Interview', network: 'LinkedIn, Instagram' },
      { date: '01/04', event: 'Poisson d\'Avril', type: 'fun', format: 'Reel fun', network: 'Instagram, TikTok' },
      { date: '01/05', event: 'Fête du Travail', type: 'fete', format: 'Story', network: 'LinkedIn' },
      { date: '21/06', event: 'Été / Fête Musique', type: 'fete', format: 'Vidéo ambiance', network: 'Instagram, TikTok' },
      { date: '01/09', event: 'Rentrée', type: 'business', format: 'Vidéo bilan/objectifs', network: 'LinkedIn' },
      { date: '31/10', event: 'Halloween', type: 'fun', format: 'Reel thématique', network: 'Instagram, TikTok' },
      { date: '25/12', event: 'Noël', type: 'fete', format: 'Story + Voeux', network: 'Tous' },
      { date: 'Mensuel', event: 'Coulisses équipe', type: 'marque-employeur', format: 'Reel/Story', network: 'Instagram, LinkedIn' },
      { date: 'Mensuel', event: 'Tips métier', type: 'expertise', format: 'Carrousel/Reel', network: 'LinkedIn, Instagram' },
      { date: 'Trimestriel', event: 'Témoignage client', type: 'social-proof', format: 'Interview', network: 'LinkedIn, YouTube' }
    ]
  }
].sort((a, b) => a.priceFrom - b.priceFrom);

// Marketing Digital Solutions - Enriched with Timeline position and detailed content
const MARKETING_SOLUTIONS = [
  {
    id: 'campagne-email',
    name: 'Campagne Email Marketing',
    icon: Mail,
    description: 'Newsletters et emails commerciaux',
    longDesc: 'Conception et envoi de campagnes email professionnelles : newsletters, emails promotionnels, séquences automatisées de nurturing.',
    priceFrom: 400,
    priceRange: '400€ - 1 500€ HT/campagne',
    deliveryTime: '1-2 semaines',
    features: ['Design responsive', 'Rédaction des textes', 'Intégration HTML', 'Tests A/B', 'Rapport de performance', 'Conformité RGPD'],
    includes: ['Stratégie éditoriale', 'Segmentation audience', 'Analyse des résultats'],
    timelinePosition: 8,
    timelinePhase: 'Digital',
    whyDoIt: 'L\'email marketing génère un ROI de 42€ pour chaque euro investi. C\'est le canal le plus rentable pour fidéliser vos clients et générer des ventes récurrentes.',
    whenToDoIt: 'Dès que vous avez une base de contacts (même petite). Idéal pour annoncer des nouveautés, promotions ou maintenir le lien avec vos clients.',
    idealFor: ['E-commerce', 'Services B2B', 'Infopreneurs', 'Restauration'],
    notIdealFor: ['Base de contacts inexistante', 'Secteurs avec forte réglementation email'],
    examples: [
      { name: 'Newsletter E-commerce', image: 'https://images.unsplash.com/photo-1563986768609-322da13575f3?w=400', category: 'E-commerce' },
      { name: 'Séquence Onboarding', image: 'https://images.unsplash.com/photo-1557200134-90327ee9fafa?w=400', category: 'SaaS' },
      { name: 'Promo Restaurant', image: 'https://images.unsplash.com/photo-1414235077428-338989a2e8c0?w=400', category: 'Restauration' }
    ]
  },
  {
    id: 'campagne-sms',
    name: 'Campagne SMS Marketing',
    icon: Smartphone,
    description: 'SMS promotionnels et rappels',
    longDesc: 'Campagnes SMS ciblées pour vos promotions flash, rappels de rendez-vous, ou notifications importantes. Taux d\'ouverture de 98%.',
    priceFrom: 300,
    priceRange: '300€ - 800€ HT + crédit SMS',
    deliveryTime: '3-5 jours',
    features: ['Rédaction messages', 'Segmentation contacts', 'Programmation envois', 'Personnalisation', 'Suivi performances', 'Conformité RGPD'],
    includes: ['Stratégie SMS', 'Base de test', 'Rapport campagne'],
    timelinePosition: 8,
    timelinePhase: 'Digital',
    whyDoIt: 'Le SMS a un taux d\'ouverture de 98% (vs 20% pour l\'email). Parfait pour les messages urgents, promotions flash ou rappels de RDV. Lu en moins de 3 minutes.',
    whenToDoIt: 'Pour des communications urgentes ou à fort impact : ventes flash, rappels RDV, confirmations. À utiliser avec parcimonie pour ne pas lasser.',
    idealFor: ['Commerces de proximité', 'Restaurants', 'Salons de beauté', 'Médical', 'E-commerce (ventes flash)'],
    notIdealFor: ['Communications longues', 'Entreprises B2B corporate'],
    examples: [
      { name: 'Vente Flash Boutique', image: 'https://images.unsplash.com/photo-1441986300917-64674bd600d8?w=400', category: 'Retail' },
      { name: 'Rappel RDV Médical', image: 'https://images.unsplash.com/photo-1576091160399-112ba8d25d1d?w=400', category: 'Santé' },
      { name: 'Promo Restaurant', image: 'https://images.unsplash.com/photo-1517248135467-4c7edcad34c4?w=400', category: 'Restauration' }
    ]
  },
  {
    id: 'jeu-concours',
    name: 'Jeu Concours Sur Mesure',
    icon: Gift,
    description: 'Jeux interactifs & Quiz engageants',
    longDesc: 'Création de jeux concours 100% personnalisés : quiz métiers, système de points, classements, tirages au sort avec huissier. Solution clé en main avec back-office complet pour gérer joueurs, questions et résultats. Rédaction du règlement incluse.',
    priceFrom: 2500,
    priceRange: 'Sur devis',
    deliveryTime: '3-6 semaines',
    badge: 'Nouveau',
    features: ['Quiz personnalisés métier/produits', 'Gestion des joueurs', 'Classement & points', 'Tirage au sort (huissier possible)', 'Back-office complet', 'Règlement juridique inclus'],
    includes: ['Étude de cas : Le Coffre MSC', 'Formation administration', 'Support technique'],
    timelinePosition: 10,
    timelinePhase: 'Croissance',
    whyDoIt: 'Fidélisez vos clients et formez vos équipes avec des quiz ludiques. Le Coffre MSC a prouvé que la gamification génère un engagement exceptionnel sur le long terme.',
    whenToDoIt: 'Pour fidéliser sur la durée (jeu annuel), animer une communauté, former de façon ludique, ou organiser un grand tirage événementiel.',
    idealFor: ['Marques B2B et B2C', 'Formation interne', 'Fidélisation clients', 'Événements annuels'],
    notIdealFor: ['Opérations très courtes (< 1 mois)', 'Budgets très limités'],
    hasDetailPage: true,
    detailPageUrl: '/site/jeux-concours',
    examples: [
      { name: 'Le Coffre MSC 2026', image: 'https://static.prod-images.emergentagent.com/jobs/0ad8004c-c8ae-44ad-a91e-d4d3309161cb/images/eeadb914c8d323570ea8a1050799fd900dade5ea198622863c88cad7bd24d515.png', category: 'Jeu annuel' },
      { name: 'Quiz Formation', image: 'https://static.prod-images.emergentagent.com/jobs/0ad8004c-c8ae-44ad-a91e-d4d3309161cb/images/d44cef1f4004772e19fa03a20b670d6e5d25a22acf3164c7b2fdfa19a4aa0696.png', category: 'Formation' },
      { name: 'Back-office Admin', image: 'https://static.prod-images.emergentagent.com/jobs/0ad8004c-c8ae-44ad-a91e-d4d3309161cb/images/f665e92e366f49b86a2fd6bd6c933d2aae4a517a923cbbdd1fcde3e0984727ad.png', category: 'Administration' }
    ]
  },
  {
    id: 'google-ads',
    name: 'Campagne Google Ads',
    icon: Search,
    description: 'Publicité sur le réseau Google',
    longDesc: 'Création et gestion de campagnes Google Ads : Search, Display, Shopping, YouTube. Atteignez vos clients au moment où ils vous cherchent.',
    priceFrom: 500,
    priceRange: '500€ - 1 500€ HT/mois + budget pub',
    deliveryTime: 'Mise en ligne sous 1 semaine',
    features: ['Étude de mots-clés', 'Création des annonces', 'Configuration tracking', 'Optimisation continue', 'Rapport mensuel', 'A/B testing'],
    includes: ['Audit compte existant', 'Stratégie d\'enchères', 'Suivi conversions'],
    timelinePosition: 8,
    timelinePhase: 'Digital',
    whyDoIt: 'Google Ads vous place devant des prospects qui cherchent activement vos produits/services. ROI mesurable, résultats immédiats, budget maîtrisé.',
    whenToDoIt: 'Dès que votre site est prêt à convertir (landing page ou e-commerce). Idéal pour tester un marché ou booster rapidement votre acquisition.',
    idealFor: ['E-commerce', 'Services locaux', 'Génération de leads', 'Apps'],
    notIdealFor: ['Budgets très limités (<500€/mois)', 'Marchés très concurrentiels sans différenciation'],
    examples: [
      { name: 'Campagne E-commerce', image: 'https://images.unsplash.com/photo-1556742049-0cfed4f6a45d?w=400', category: 'E-commerce' },
      { name: 'Leads Immobilier', image: 'https://images.unsplash.com/photo-1560518883-ce09059eeffa?w=400', category: 'Immobilier' },
      { name: 'App Mobile', image: 'https://images.unsplash.com/photo-1512941937669-90a1b58e7e9c?w=400', category: 'Tech' }
    ]
  },
  {
    id: 'social-ads',
    name: 'Campagne Social Ads',
    icon: Target,
    description: 'Publicité Facebook, Instagram, LinkedIn',
    longDesc: 'Campagnes publicitaires sur les réseaux sociaux : ciblage précis, créatifs engageants, optimisation des performances.',
    priceFrom: 400,
    priceRange: '400€ - 1 200€ HT/mois + budget pub',
    deliveryTime: 'Mise en ligne sous 1 semaine',
    features: ['Définition audiences', 'Création des visuels', 'Rédaction des textes', 'Pixel/tracking', 'Tests créatifs', 'Optimisation'],
    includes: ['Stratégie réseaux', 'Veille concurrentielle', 'Reporting'],
    timelinePosition: 8,
    timelinePhase: 'Digital',
    whyDoIt: 'Les Social Ads permettent un ciblage ultra-précis (centres d\'intérêt, comportements, lookalike). Idéal pour développer votre notoriété et toucher de nouvelles audiences.',
    whenToDoIt: 'Une fois votre identité visuelle définie et vos réseaux sociaux actifs. Parfait pour amplifier vos meilleurs contenus organiques.',
    idealFor: ['Marques lifestyle', 'E-commerce mode/beauté', 'B2B sur LinkedIn', 'Événementiel'],
    notIdealFor: ['Produits très techniques sans dimension visuelle', 'Marchés de niche très restreints'],
    examples: [
      { name: 'Campagne Instagram', image: 'https://images.unsplash.com/photo-1611162616305-c69b3fa7fbe0?w=400', category: 'Fashion' },
      { name: 'Ads LinkedIn B2B', image: 'https://images.unsplash.com/photo-1552664730-d307ca884978?w=400', category: 'B2B' },
      { name: 'Promo Facebook', image: 'https://images.unsplash.com/photo-1563986768609-322da13575f3?w=400', category: 'E-commerce' }
    ]
  },
  {
    id: 'community-management',
    name: 'Community Management',
    icon: Users,
    description: 'Animation de vos réseaux sociaux',
    longDesc: 'Gestion complète de vos réseaux sociaux : stratégie éditoriale, création de contenus, publication, modération et engagement communautaire.',
    priceFrom: 600,
    priceRange: '600€ - 2 000€ HT/mois',
    deliveryTime: 'Forfait mensuel',
    features: ['Stratégie éditoriale', 'Création de contenus', 'Publication planifiée', 'Modération', 'Veille sectorielle', 'Reporting mensuel'],
    includes: ['Calendrier éditorial', '8-20 posts/mois', 'Interaction communauté'],
    timelinePosition: 9,
    timelinePhase: 'Déploiement',
    whyDoIt: 'Une présence active sur les réseaux sociaux renforce votre image de marque, fidélise vos clients et génère du trafic qualifié. Mais c\'est chronophage !',
    whenToDoIt: 'Dès que votre identité visuelle est prête. Externalisez pour garantir une présence régulière sans y passer des heures.',
    idealFor: ['PME sans équipe marketing', 'Commerces locaux', 'Restaurants', 'Professions libérales'],
    notIdealFor: ['Entreprises avec équipe social media interne', 'Secteurs très techniques B2B'],
    examples: [
      { name: 'Restaurant Tendance', image: 'https://images.unsplash.com/photo-1517248135467-4c7edcad34c4?w=400', category: 'Restauration' },
      { name: 'Boutique Mode', image: 'https://images.unsplash.com/photo-1441986300917-64674bd600d8?w=400', category: 'Mode' },
      { name: 'Coach Business', image: 'https://images.unsplash.com/photo-1552664730-d307ca884978?w=400', category: 'Consulting' }
    ]
  }
].sort((a, b) => a.priceFrom - b.priceFrom);

// Hosting & Maintenance Subscriptions (separate from creation)
const SUBSCRIPTION_OFFERS = [
  {
    id: 'hebergement-essentiel',
    name: 'Hébergement Essentiel',
    icon: Shield,
    description: 'Pour les sites vitrines',
    priceMonthly: 29,
    priceYearly: 290,
    features: ['Hébergement sécurisé', 'SSL gratuit', 'Sauvegardes hebdo', 'Support email', 'Uptime 99.5%'],
    recommended: false
  },
  {
    id: 'hebergement-business',
    name: 'Hébergement Business',
    icon: Rocket,
    description: 'Pour les sites e-commerce',
    priceMonthly: 59,
    priceYearly: 590,
    features: ['Tout Essentiel +', 'Sauvegardes quotidiennes', 'CDN inclus', 'Support prioritaire', 'Uptime 99.9%', 'Monitoring 24/7'],
    recommended: true
  },
  {
    id: 'maintenance-basique',
    name: 'Maintenance Basique',
    icon: Settings,
    description: 'Mises à jour essentielles',
    priceMonthly: 49,
    priceYearly: 490,
    features: ['Mises à jour WordPress/plugins', 'Vérifications mensuelles', 'Rapport mensuel', 'Support email'],
    recommended: false
  },
  {
    id: 'maintenance-premium',
    name: 'Maintenance Premium',
    icon: Zap,
    description: 'Tranquillité totale',
    priceMonthly: 149,
    priceYearly: 1490,
    features: ['Tout Basique +', 'Modifications mineures (2h/mois)', 'Optimisation SEO', 'Sécurité renforcée', 'Support téléphone', 'Intervention sous 24h'],
    recommended: true
  }
];

// Communication Calendar for Blog/Advice section
const COMMUNICATION_CALENDAR = [
  { month: 'Janvier', events: [
    { date: '1', name: 'Nouvel An', format: 'Voeux vidéo/visuel', networks: ['LinkedIn', 'Instagram', 'Facebook'], tip: 'Publiez la veille à 18h pour être vus le jour J' },
    { date: '24', name: 'Journée du Compliment', format: 'Story interactive', networks: ['Instagram'], tip: 'Mettez en avant vos clients/équipes' }
  ]},
  { month: 'Février', events: [
    { date: '14', name: 'Saint-Valentin', format: 'Reel/Visuel thématique', networks: ['Instagram', 'TikTok'], tip: 'Adaptez à votre secteur avec humour' }
  ]},
  { month: 'Mars', events: [
    { date: '8', name: 'Journée des Femmes', format: 'Portrait/Interview', networks: ['LinkedIn', 'Instagram'], tip: 'Mettez en avant les femmes de votre équipe' },
    { date: '20', name: 'Printemps', format: 'Visuel frais', networks: ['Instagram'], tip: 'Nouveau départ, renouveau' }
  ]},
  { month: 'Avril', events: [
    { date: '1', name: 'Poisson d\'Avril', format: 'Reel fun', networks: ['Instagram', 'TikTok'], tip: 'Fausse annonce produit ?' },
    { date: '22', name: 'Jour de la Terre', format: 'Engagements RSE', networks: ['LinkedIn', 'Instagram'], tip: 'Montrez vos actions éco-responsables' }
  ]},
  { month: 'Mai', events: [
    { date: '1', name: 'Fête du Travail', format: 'Story équipe', networks: ['LinkedIn'], tip: 'Valorisez votre équipe' }
  ]},
  { month: 'Juin', events: [
    { date: '21', name: 'Été / Fête Musique', format: 'Vidéo ambiance', networks: ['Instagram', 'TikTok'], tip: 'Playlist d\'entreprise ?' }
  ]},
  { month: 'Septembre', events: [
    { date: '1', name: 'Rentrée', format: 'Vidéo bilan/objectifs', networks: ['LinkedIn'], tip: 'Nouveaux projets, nouvelles ambitions' }
  ]},
  { month: 'Octobre', events: [
    { date: '31', name: 'Halloween', format: 'Reel thématique', networks: ['Instagram', 'TikTok'], tip: 'Déguisez votre logo ou équipe' }
  ]},
  { month: 'Novembre', events: [
    { date: '25', name: 'Black Friday', format: 'Offres/Countdown', networks: ['Tous'], tip: 'Préparez 1 semaine avant' }
  ]},
  { month: 'Décembre', events: [
    { date: '25', name: 'Noël', format: 'Voeux vidéo', networks: ['Tous'], tip: 'Personnalisez avec votre équipe' },
    { date: '31', name: 'Réveillon', format: 'Rétrospective année', networks: ['LinkedIn', 'Instagram'], tip: 'Best-of de l\'année' }
  ]}
];

// Web Solutions Sub-categories with pricing - Enriched with Timeline position and detailed content
const WEB_SOLUTIONS = [
  {
    id: 'landing-page',
    name: 'Landing Page',
    icon: MousePointer,
    description: 'Convertissez vos visiteurs en clients',
    longDesc: 'Une page d\'atterrissage optimisée pour la conversion, parfaite pour vos campagnes marketing et génération de leads.',
    tech: 'WordPress / HTML',
    priceFrom: 800,
    priceRange: '800€ - 2 000€ HT',
    deliveryTime: '1-2 semaines',
    features: ['Design conversion-oriented', 'A/B Testing ready', 'Formulaire optimisé', 'Tracking avancé', 'Mobile-first', 'Vitesse optimisée'],
    includes: ['1 page haute conversion', 'Intégration CRM', 'Pixels tracking', 'Rapport mensuel'],
    examples: [
      { name: 'Formation en ligne Pro', image: 'https://images.unsplash.com/photo-1516321318423-f06f85e504b3?w=400', category: 'Formation' },
      { name: 'App Mobile Fitness', image: 'https://images.unsplash.com/photo-1476480862126-209bfaa8edc8?w=400', category: 'Sport' }
    ],
    // Timeline & Why
    timelinePosition: 6,
    timelinePhase: 'Digital',
    whyDoIt: 'Une landing page bien conçue convertit 5 à 10x mieux qu\'une page classique. C\'est l\'outil indispensable pour rentabiliser vos campagnes publicitaires Google Ads ou Facebook Ads. Chaque euro investi en pub mérite une page optimisée.',
    whenToDoIt: 'Avant de lancer toute campagne publicitaire payante. Aussi idéal pour tester un nouveau produit/service avant de créer un site complet.',
    idealFor: ['Campagnes Google/Facebook Ads', 'Lancements produits', 'Génération de leads', 'Tests de marché'],
    notIdealFor: ['Présence web globale', 'Référencement naturel (SEO)']
  },
  {
    id: 'site-vitrine',
    name: 'Site Vitrine',
    icon: Monitor,
    description: 'Présentez votre entreprise avec élégance',
    longDesc: 'Un site vitrine professionnel pour présenter votre activité, vos services et établir votre crédibilité en ligne.',
    tech: 'WordPress',
    priceFrom: 1500,
    priceRange: '1 500€ - 4 000€ HT',
    deliveryTime: '2-4 semaines',
    features: ['Design personnalisé', 'Responsive mobile', 'SEO de base', 'Formulaire de contact', 'Intégration réseaux sociaux', 'Google Analytics'],
    includes: ['5-10 pages', 'Hébergement 1 an offert', 'Formation utilisation', 'Support 3 mois'],
    examples: [
      { name: 'Cabinet Avocat Martin', image: 'https://images.unsplash.com/photo-1497366216548-37526070297c?w=400', category: 'Juridique' },
      { name: 'Restaurant Le Gourmet', image: 'https://images.unsplash.com/photo-1517248135467-4c7edcad34c4?w=400', category: 'Restauration' },
      { name: 'Architecte Dupont', image: 'https://images.unsplash.com/photo-1487958449943-2429e8be8625?w=400', category: 'Architecture' }
    ],
    // Timeline & Why
    timelinePosition: 6,
    timelinePhase: 'Digital',
    whyDoIt: '81% des consommateurs recherchent une entreprise en ligne avant de la contacter. Un site vitrine professionnel est votre carte de visite digitale 24h/24. Il crédibilise votre activité et rassure vos prospects.',
    whenToDoIt: 'Après avoir défini votre identité visuelle (logo minimum). C\'est souvent la première étape de présence digitale après la création d\'entreprise.',
    idealFor: ['TPE/PME', 'Professions libérales', 'Artisans', 'Associations', 'Restaurants'],
    notIdealFor: ['Vente en ligne (préférer e-commerce)', 'Besoins de fonctionnalités avancées'],
    comparisonNote: 'Le site vitrine est le choix n°1 pour 70% des entreprises qui démarrent leur présence en ligne.'
  },
  {
    id: 'site-reservation',
    name: 'Site de Réservation',
    icon: Calendar,
    description: 'Automatisez vos prises de rendez-vous',
    longDesc: 'Un système de réservation en ligne intelligent pour gérer vos créneaux, confirmations et rappels automatiques.',
    tech: 'WordPress + Plugin',
    priceFrom: 2500,
    priceRange: '2 500€ - 5 000€ HT',
    deliveryTime: '3-5 semaines',
    features: ['Calendrier temps réel', 'Paiement en ligne', 'Rappels SMS/Email', 'Multi-services', 'Gestion équipe', 'Sync Google Calendar'],
    includes: ['Configuration services', 'Passerelle paiement', 'Automatisations', 'Formation équipe'],
    examples: [
      { name: 'Salon Beauté Zen', image: 'https://images.unsplash.com/photo-1560066984-138dadb4c035?w=400', category: 'Beauté' },
      { name: 'Coach Sportif Pro', image: 'https://images.unsplash.com/photo-1571019614242-c5c5dee9f50b?w=400', category: 'Sport' }
    ],
    // Timeline & Why
    timelinePosition: 6,
    timelinePhase: 'Digital',
    whyDoIt: 'Un système de réservation en ligne réduit de 60% les no-shows grâce aux rappels automatiques. Il libère votre temps (plus d\'appels pour prendre RDV) et permet à vos clients de réserver 24h/24, même quand vous dormez.',
    whenToDoIt: 'Dès que vous perdez du temps à gérer les rendez-vous par téléphone ou que vous subissez des no-shows. ROI souvent atteint en 2-3 mois.',
    idealFor: ['Salons de coiffure/esthétique', 'Coachs et consultants', 'Médecins/Thérapeutes', 'Restaurants', 'Locations'],
    notIdealFor: ['Activités sans rendez-vous', 'Clientèle peu digitalisée']
  },
  {
    id: 'boutique-en-ligne',
    name: 'Boutique en Ligne',
    icon: ShoppingBag,
    description: 'Vendez vos produits 24h/24',
    longDesc: 'Une boutique e-commerce complète avec gestion des stocks, paiements sécurisés et expéditions automatisées.',
    tech: 'WooCommerce',
    priceFrom: 3000,
    priceRange: '3 000€ - 8 000€ HT',
    deliveryTime: '4-8 semaines',
    features: ['Catalogue produits illimité', 'Paiement sécurisé (CB, PayPal)', 'Gestion des stocks', 'Expéditions automatisées', 'Factures auto', 'Multi-devises'],
    includes: ['Configuration complète', 'Import produits', 'Formation vendeur', 'Support 6 mois'],
    examples: [
      { name: 'Mode Éthique Paris', image: 'https://images.unsplash.com/photo-1441986300917-64674bd600d8?w=400', category: 'Mode' },
      { name: 'Épicerie Bio Local', image: 'https://images.unsplash.com/photo-1542838132-92c53300491e?w=400', category: 'Alimentaire' },
      { name: 'Bijoux Artisanaux', image: 'https://images.unsplash.com/photo-1515562141207-7a88fb7ce338?w=400', category: 'Artisanat' }
    ],
    // Timeline & Why
    timelinePosition: 6,
    timelinePhase: 'Digital',
    whyDoIt: 'Le e-commerce français croît de 15% par an. Une boutique en ligne vous ouvre un marché national (voire international) sans les coûts d\'un local commercial. Elle génère des ventes même la nuit et les week-ends.',
    whenToDoIt: 'Quand vous avez des produits à vendre et une logistique d\'expédition prête. Idéal en complément d\'une boutique physique ou pour lancer une activité 100% en ligne.',
    idealFor: ['Commerces de détail', 'Artisans créateurs', 'Producteurs locaux', 'Marques de mode'],
    notIdealFor: ['Services (préférer site vitrine)', 'Produits périssables complexes']
  },
  {
    id: 'site-react',
    name: 'Site React / Sur-mesure',
    icon: Code,
    description: 'Performance et flexibilité maximales',
    longDesc: 'Application web moderne développée sur-mesure en React pour des besoins spécifiques dépassant les limites des CMS traditionnels.',
    tech: 'React + Node.js',
    priceFrom: 6000,
    priceRange: 'À partir de 6 000€ HT',
    deliveryTime: '6-12 semaines',
    features: ['Performance optimale', 'Interface ultra-rapide', 'Fonctionnalités sur-mesure', 'API personnalisée', 'Scalabilité', 'Sécurité renforcée'],
    includes: ['Cahier des charges', 'Design UX/UI', 'Développement complet', 'Tests & recette', 'Documentation'],
    isPremium: true,
    advantages: [
      { title: 'Vitesse', desc: 'Chargement quasi-instantané, navigation fluide' },
      { title: 'Flexibilité', desc: 'Aucune limite fonctionnelle, tout est possible' },
      { title: 'Scalabilité', desc: 'Croît avec votre entreprise sans refonte' },
      { title: 'Propriété', desc: 'Code source 100% propriétaire' }
    ],
    vsWordPress: [
      { aspect: 'Performance', react: 'Excellent (SPA)', wordpress: 'Bon (avec cache)' },
      { aspect: 'Personnalisation', react: 'Illimitée', wordpress: 'Limitée aux plugins' },
      { aspect: 'Maintenance', react: 'Minimale', wordpress: 'Mises à jour fréquentes' },
      { aspect: 'Sécurité', react: 'Très haute', wordpress: 'Moyenne (plugins)' },
      { aspect: 'Coût initial', react: 'Plus élevé', wordpress: 'Plus abordable' },
      { aspect: 'Autonomie client', react: 'Via interface admin', wordpress: 'Complète' }
    ],
    examples: [
      { name: 'Dashboard Analytics', image: 'https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=400', category: 'SaaS' },
      { name: 'Plateforme RH', image: 'https://images.unsplash.com/photo-1552664730-d307ca884978?w=400', category: 'Entreprise' }
    ],
    // Timeline & Why
    timelinePosition: 6,
    timelinePhase: 'Digital',
    whyDoIt: 'Quand WordPress atteint ses limites, React prend le relais. Performance 10x supérieure, sécurité renforcée, et possibilités infinies. C\'est un investissement qui vous différencie et s\'adapte à votre croissance sans refonte.',
    whenToDoIt: 'Pour des besoins spécifiques que WordPress ne peut pas gérer : dashboard complexe, intégrations multiples, haute performance requise, ou projet innovant.',
    idealFor: ['Startups tech', 'Plateformes SaaS', 'Projets innovants', 'Gros volumes de données'],
    notIdealFor: ['Sites vitrines simples', 'Petits budgets', 'Besoin d\'autonomie totale sans dev']
  },
  {
    id: 'pwa',
    name: 'PWA - Application Web',
    icon: Smartphone,
    description: 'Le meilleur du web et du mobile',
    longDesc: 'Une Progressive Web App offrant une expérience proche d\'une application native, installable sur mobile sans passer par les stores.',
    tech: 'React + PWA',
    priceFrom: 15000,
    priceRange: 'À partir de 15 000€ HT',
    deliveryTime: '3-6 mois',
    features: ['Installation mobile', 'Mode hors-ligne', 'Notifications push', 'Performance native', 'Mise à jour auto', 'Une seule codebase'],
    includes: ['Étude fonctionnelle', 'Prototype interactif', 'Développement full-stack', 'Déploiement', 'Maintenance 1 an'],
    isPremium: true,
    advantages: [
      { title: 'Pas de store', desc: 'Distribution directe, pas de commission Apple/Google' },
      { title: 'Coût réduit', desc: 'Un seul développement pour web + mobile' },
      { title: 'Hors-ligne', desc: 'Fonctionne même sans connexion' },
      { title: 'Push', desc: 'Notifications pour engager vos utilisateurs' }
    ],
    vsNative: [
      { aspect: 'Coût développement', pwa: '15-30k€', native: '50-100k€' },
      { aspect: 'Délai', pwa: '3-6 mois', native: '6-12 mois' },
      { aspect: 'Maintenance', pwa: '1 codebase', native: '2-3 codebases' },
      { aspect: 'Distribution', pwa: 'Lien web', native: 'App Stores' },
      { aspect: 'Performance', pwa: 'Très bonne', native: 'Excellente' },
      { aspect: 'Accès hardware', pwa: 'Partiel', native: 'Complet' }
    ],
    examples: [
      { name: 'App Eperf', image: 'https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=400', category: 'Gestion', isOurs: true },
      { name: 'SiteFlash', image: 'https://images.unsplash.com/photo-1555421689-491a97ff2040?w=400', category: 'CMS', isOurs: true },
      { name: 'Copilote MSC', image: 'https://images.unsplash.com/photo-1551434678-e076c223a692?w=400', category: 'Agence', isOurs: true }
    ],
    // Timeline & Why
    timelinePosition: 6,
    timelinePhase: 'Digital',
    whyDoIt: 'Une PWA coûte 3x moins cher qu\'une app native (iOS + Android) tout en offrant 90% des fonctionnalités. Elle se met à jour automatiquement, ne prend pas de place sur le téléphone, et n\'a pas besoin d\'approbation des stores.',
    whenToDoIt: 'Quand vous voulez offrir une expérience mobile avancée sans le budget d\'une app native. Idéal pour fidéliser vos utilisateurs avec des notifications push.',
    idealFor: ['Services récurrents (abonnements)', 'Outils métier mobiles', 'E-commerce mobile-first', 'Applications internes'],
    notIdealFor: ['Besoins hardware avancés (GPS précis, Bluetooth)', 'Jeux 3D']
  }
];
// ============================================
// APPLICATION SOLUTIONS - PWA & Mobile
// ============================================
const APP_SOLUTIONS = [
  {
    id: 'pwa-metier',
    name: 'Application Métier (PWA)',
    icon: Smartphone,
    description: 'App web installable sur mobile',
    longDesc: 'Application web progressive installable sur smartphone et tablette, sans passer par les stores. Interface fluide, mode hors-ligne, notifications push. Idéale pour digitaliser vos processus métier.',
    tech: 'React + PWA',
    priceFrom: 16000,
    priceRange: 'À partir de 16 000€ HT',
    deliveryTime: '3-5 mois',
    features: ['Installation mobile sans store', 'Mode hors-ligne', 'Notifications push', 'Interface responsive', 'Mise à jour automatique', 'Back-office d\'administration'],
    includes: ['Étude fonctionnelle', 'Prototype interactif', 'Développement full-stack', 'Déploiement & hébergement', 'Formation utilisateurs', 'Maintenance 1 an'],
    isPremium: true,
    advantages: [
      { title: 'Sans store', desc: 'Pas de commission Apple/Google, distribution directe par lien' },
      { title: 'Coût maîtrisé', desc: 'Un seul développement pour web, iOS et Android' },
      { title: 'Hors-ligne', desc: 'Fonctionne même sans connexion internet' },
      { title: 'Push', desc: 'Notifications pour engager vos utilisateurs' }
    ]
  },
  {
    id: 'app-mobile-native',
    name: 'Application Mobile Native',
    icon: Smartphone,
    description: 'App iOS & Android sur les stores',
    longDesc: 'Application mobile native publiée sur l\'App Store et Google Play. Performances optimales, accès complet au hardware du téléphone (GPS, Bluetooth, caméra, NFC). Pour les projets nécessitant une expérience mobile haut de gamme.',
    tech: 'React Native / Flutter',
    priceFrom: 16000,
    priceRange: 'À partir de 16 000€ HT',
    deliveryTime: '4-8 mois',
    features: ['Publication App Store & Google Play', 'Accès complet hardware', 'Performances natives', 'Expérience utilisateur premium', 'Notifications avancées', 'Intégrations API tierces'],
    includes: ['Cahier des charges détaillé', 'Maquettes UI/UX', 'Développement iOS + Android', 'Tests QA', 'Soumission aux stores', 'Maintenance 1 an'],
    isPremium: true
  },
  {
    id: 'plateforme-saas',
    name: 'Plateforme SaaS / Web App',
    icon: Monitor,
    description: 'Plateforme web complète multi-utilisateurs',
    longDesc: 'Développement d\'une plateforme web complète avec gestion des utilisateurs, abonnements, tableau de bord, API. Idéale pour lancer votre produit SaaS ou digitaliser un service existant.',
    tech: 'React + Node.js / Python',
    priceFrom: 16000,
    priceRange: 'À partir de 16 000€ HT',
    deliveryTime: '4-8 mois',
    features: ['Multi-utilisateurs & rôles', 'Tableau de bord analytics', 'Gestion d\'abonnements', 'API REST/GraphQL', 'Système de paiement', 'Scalable cloud'],
    includes: ['Architecture technique', 'Prototype MVP', 'Développement itératif', 'Infrastructure cloud', 'Documentation API', 'Support technique 1 an'],
    isPremium: true
  }
];

const SERVICES = [
  {
    id: 'creation-graphique',
    title: 'Création Graphique',
    shortTitle: 'Graphique',
    icon: Palette,
    color: 'from-pink-500 to-rose-600',
    bgColor: 'bg-pink-50',
    description: 'Sculptez votre identité visuelle avec nos créations graphiques uniques',
    longDescription: 'Notre équipe de designers passionnés crée des visuels qui marquent les esprits. Du logo à la charte graphique complète, nous donnons vie à votre identité de marque.',
    features: ['Création de logos', 'Charte graphique', 'Visuels réseaux sociaux', 'Brochures & catalogues', 'Packaging', 'Signalétique'],
    hasSolutions: true,
    solutions: GRAPHIC_SOLUTIONS,
    heroMedia: {
      type: 'image',
      url: 'https://images.unsplash.com/photo-1626785774573-4b799315345d?w=1200&q=80'
    },
    process: [
      { step: 1, title: 'Brief créatif', desc: 'Analyse de vos besoins et votre univers' },
      { step: 2, title: 'Recherches', desc: 'Exploration créative et moodboards' },
      { step: 3, title: 'Propositions', desc: '3 pistes créatives présentées' },
      { step: 4, title: 'Finalisation', desc: 'Ajustements et livraison finale' }
    ]
  },
  {
    id: 'creation-web',
    title: 'Création Web',
    shortTitle: 'Web',
    icon: Monitor,
    color: 'from-cyan-500 to-blue-600',
    bgColor: 'bg-cyan-50',
    description: 'Une présence en ligne impactante avec nos créations digitales',
    longDescription: 'Sites vitrines, e-commerce, applications web... Nous concevons des expériences digitales sur mesure, optimisées pour la conversion et le référencement.',
    features: ['Sites vitrines', 'E-commerce', 'Landing pages', 'Applications web', 'Refonte de sites', 'Maintenance'],
    hasSolutions: true,
    solutions: WEB_SOLUTIONS,
    heroMedia: {
      type: 'image',
      url: 'https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=1200&q=80'
    },
    process: [
      { step: 1, title: 'Analyse', desc: 'Étude de vos objectifs et cibles' },
      { step: 2, title: 'Conception', desc: 'Wireframes et maquettes UX/UI' },
      { step: 3, title: 'Développement', desc: 'Intégration et programmation' },
      { step: 4, title: 'Mise en ligne', desc: 'Tests, optimisation et lancement' }
    ]
  },
  {
    id: 'creation-video',
    title: 'Création Vidéo',
    shortTitle: 'Vidéo',
    icon: Film,
    color: 'from-purple-500 to-violet-600',
    bgColor: 'bg-purple-50',
    description: 'Donnez vie à vos projets avec nos réalisations vidéo professionnelles',
    longDescription: 'De la vidéo corporate au motion design, nos réalisateurs et monteurs créent des contenus vidéo qui captent l\'attention et transmettent votre message.',
    features: ['Vidéos corporate', 'Interviews', 'Motion design', 'Tutoriels', 'Réseaux sociaux', 'Événementiel'],
    hasSolutions: true,
    solutions: VIDEO_SOLUTIONS,
    hasCalendar: true,
    heroMedia: {
      type: 'video',
      url: 'https://assets.mixkit.co/videos/preview/mixkit-set-of-plateaus-702-large.mp4'
    },
    process: [
      { step: 1, title: 'Scénarisation', desc: 'Écriture du script et storyboard' },
      { step: 2, title: 'Tournage', desc: 'Captation avec équipe pro' },
      { step: 3, title: 'Montage', desc: 'Post-production et effets' },
      { step: 4, title: 'Livraison', desc: 'Formats adaptés à vos supports' }
    ]
  },
  {
    id: 'marketing-digital',
    title: 'Marketing Digital',
    shortTitle: 'Marketing',
    icon: Megaphone,
    color: 'from-orange-500 to-amber-600',
    bgColor: 'bg-orange-50',
    description: 'Boostez votre visibilité avec nos stratégies digitales',
    longDescription: 'Campagnes email, SMS, jeux concours, publicité en ligne... Nous mettons en place des stratégies d\'acquisition performantes pour développer votre audience et générer des leads.',
    features: ['Campagnes Email', 'SMS Marketing', 'Jeux Concours', 'Google Ads', 'Social Ads', 'Community Management'],
    hasSolutions: true,
    solutions: MARKETING_SOLUTIONS,
    heroMedia: {
      type: 'image',
      url: 'https://images.unsplash.com/photo-1533750349088-cd871a92f312?w=1200&q=80'
    },
    process: [
      { step: 1, title: 'Audit', desc: 'Analyse de votre présence actuelle' },
      { step: 2, title: 'Stratégie', desc: 'Plan d\'actions personnalisé' },
      { step: 3, title: 'Exécution', desc: 'Mise en place des campagnes' },
      { step: 4, title: 'Optimisation', desc: 'Suivi et amélioration continue' }
    ]
  },
  {
    id: 'applications',
    title: 'Applications sur mesure',
    shortTitle: 'Applications',
    icon: Smartphone,
    color: 'from-emerald-500 to-teal-600',
    bgColor: 'bg-emerald-50',
    description: 'Applications PWA et mobiles sur mesure pour digitaliser votre activité',
    longDescription: 'De la PWA installable à l\'application mobile native, nous concevons et développons des applications sur mesure qui répondent précisément à vos besoins métier. Performance, ergonomie et scalabilité au rendez-vous.',
    features: ['Applications PWA', 'Apps mobiles iOS & Android', 'Plateformes SaaS', 'Back-offices sur mesure', 'Intégrations API', 'Maintenance & évolution'],
    hasSolutions: true,
    solutions: APP_SOLUTIONS,
    heroMedia: {
      type: 'image',
      url: 'https://images.unsplash.com/photo-1512941937669-90a1b58e7e9c?w=1200&q=80'
    },
    process: [
      { step: 1, title: 'Cadrage', desc: 'Analyse des besoins et cahier des charges' },
      { step: 2, title: 'Conception', desc: 'Prototype interactif et validation UX' },
      { step: 3, title: 'Développement', desc: 'Sprints itératifs avec démos régulières' },
      { step: 4, title: 'Lancement', desc: 'Tests, déploiement et formation' }
    ]
  },
  {
    id: 'hebergement',
    title: 'Hébergement & Support',
    shortTitle: 'Hébergement',
    icon: Shield,
    color: 'from-cyan-500 to-blue-600',
    bgColor: 'bg-cyan-50',
    description: 'Gardez votre site performant et sécurisé',
    longDescription: 'Hébergement haute performance, sauvegardes automatiques, mises à jour de sécurité... Nous prenons soin de votre site pour que vous puissiez vous concentrer sur votre métier.',
    features: ['Hébergement sécurisé', 'Sauvegardes quotidiennes', 'Mises à jour', 'Support technique', 'Monitoring 24/7', 'SSL gratuit'],
    hasSubscriptions: true,
    subscriptions: SUBSCRIPTION_OFFERS,
    heroMedia: {
      type: 'image',
      url: 'https://images.unsplash.com/photo-1558494949-ef010cbdcc31?w=1200&q=80'
    },
    process: [
      { step: 1, title: 'Migration', desc: 'Transfert sécurisé de votre site' },
      { step: 2, title: 'Configuration', desc: 'Optimisation des performances' },
      { step: 3, title: 'Surveillance', desc: 'Monitoring permanent' },
      { step: 4, title: 'Support', desc: 'Assistance technique réactive' }
    ]
  }
];

// ============================================
// TEAM DATA
// ============================================

const TEAM = [
  { 
    name: "Jeremy", 
    role: "Fondateur & Directeur", 
    bio: "Passionné par l'entrepreneuriat et la communication depuis plus de 15 ans. Jeremy a fondé MSC avec la vision de créer une agence différente, à taille humaine.",
    skills: ["Stratégie", "Direction artistique", "Relations clients"],
    experience: "15+ ans",
    color: "from-blue-500 to-cyan-500"
  },
  { 
    name: "Cyrille", 
    role: "Directeur Commercial", 
    bio: "Expert en développement commercial et relation client. Cyrille accompagne nos clients dans la définition de leurs besoins et objectifs.",
    skills: ["Commercial", "Négociation", "Gestion de projet"],
    experience: "12+ ans",
    color: "from-violet-500 to-purple-500"
  },
  { 
    name: "Anthony", 
    role: "Lead Développeur", 
    bio: "Développeur full-stack passionné par les nouvelles technologies. Anthony supervise tous nos projets web et garantit leur qualité technique.",
    skills: ["React", "Node.js", "DevOps"],
    experience: "10+ ans",
    color: "from-cyan-500 to-blue-500"
  },
  { 
    name: "Julien", 
    role: "Directeur Artistique", 
    bio: "Créatif dans l'âme, Julien donne vie aux identités visuelles de nos clients. Son œil expert garantit des créations uniques et impactantes.",
    skills: ["Design", "Branding", "UI/UX"],
    experience: "8+ ans",
    color: "from-pink-500 to-rose-500"
  },
  { 
    name: "Loan", 
    role: "Vidéaste & Motion Designer", 
    bio: "Réalisateur et monteur, Loan crée des vidéos qui racontent des histoires. Du corporate au motion design, il maîtrise tous les formats.",
    skills: ["Vidéo", "Motion design", "Post-production"],
    experience: "6+ ans",
    color: "from-orange-500 to-amber-500"
  },
  { 
    name: "Jade", 
    role: "Chargée de Communication", 
    bio: "Experte en stratégie digitale et community management. Jade gère la présence en ligne de nos clients avec créativité et rigueur.",
    skills: ["Social Media", "Content", "SEO"],
    experience: "5+ ans",
    color: "from-cyan-500 to-blue-500"
  }
];

// ============================================
// PORTFOLIO DATA
// ============================================
// TESTIMONIALS DATA
// ============================================

const TESTIMONIALS = [
  {
    id: 1,
    name: 'Marie Dupont',
    role: 'Directrice Marketing',
    company: 'Maison Dupont',
    companyLogo: 'https://images.unsplash.com/photo-1599305445671-ac291c95aaa9?w=100&h=100&fit=crop',
    avatar: 'https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=150&h=150&fit=crop',
    quote: 'MSC a transformé notre présence digitale. Notre nouveau site génère 3x plus de leads qu\'avant. L\'équipe est réactive, créative et vraiment à l\'écoute de nos besoins.',
    rating: 5,
    service: 'web',
    projectType: 'Site vitrine + SEO',
    videoUrl: null
  },
  {
    id: 2,
    name: 'Thomas Martin',
    role: 'Fondateur & CEO',
    company: 'TechStart SAS',
    companyLogo: 'https://images.unsplash.com/photo-1560179707-f14e90ef3623?w=100&h=100&fit=crop',
    avatar: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=150&h=150&fit=crop',
    quote: 'La vidéo de présentation réalisée par MSC nous a permis de lever 500K€. Qualité cinématographique, storytelling impactant. Je recommande à 200% !',
    rating: 5,
    service: 'video',
    projectType: 'Vidéo corporate',
    videoUrl: 'https://www.youtube.com/embed/dQw4w9WgXcQ'
  },
  {
    id: 3,
    name: 'Sophie Bernard',
    role: 'Gérante',
    company: 'Boulangerie Bernard',
    companyLogo: 'https://images.unsplash.com/photo-1509440159596-0249088772ff?w=100&h=100&fit=crop',
    avatar: 'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=150&h=150&fit=crop',
    quote: 'Mon nouveau logo et ma charte graphique ont complètement modernisé l\'image de ma boulangerie. Les clients me disent que ça fait plus "pro" !',
    rating: 5,
    service: 'graphique',
    projectType: 'Logo Premium + Charte',
    videoUrl: null
  },
  {
    id: 4,
    name: 'Jean-Pierre Lefebvre',
    role: 'Directeur Général',
    company: 'Groupe Lefebvre Immobilier',
    companyLogo: 'https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?w=100&h=100&fit=crop',
    avatar: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=150&h=150&fit=crop',
    quote: 'Le motion design explicatif de nos services a révolutionné nos présentations commerciales. Nos agents l\'utilisent à chaque rendez-vous client.',
    rating: 5,
    service: 'video',
    projectType: 'Motion Design',
    videoUrl: null
  },
  {
    id: 5,
    name: 'Claire Moreau',
    role: 'Responsable Communication',
    company: 'Association Solidarité Plus',
    companyLogo: 'https://images.unsplash.com/photo-1559027615-cd4628902d4a?w=100&h=100&fit=crop',
    avatar: 'https://images.unsplash.com/photo-1580489944761-15a19d654956?w=150&h=150&fit=crop',
    quote: 'Grâce au pack communication, notre community manager gagne 2h par jour ! Les visuels sont magnifiques et toujours livrés à temps.',
    rating: 5,
    service: 'graphique',
    projectType: 'Pack Communication',
    videoUrl: null
  },
  {
    id: 6,
    name: 'Alexandre Petit',
    role: 'CEO',
    company: 'FitnessPro App',
    companyLogo: 'https://images.unsplash.com/photo-1534438327276-14e5300c3a48?w=100&h=100&fit=crop',
    avatar: 'https://images.unsplash.com/photo-1500648767791-00dcc994a43e?w=150&h=150&fit=crop',
    quote: 'Notre PWA développée par MSC a dépassé toutes nos attentes. 50 000 utilisateurs actifs en 6 mois ! L\'équipe tech est au top.',
    rating: 5,
    service: 'web',
    projectType: 'PWA - Application Web',
    videoUrl: 'https://www.youtube.com/embed/dQw4w9WgXcQ'
  },
  {
    id: 7,
    name: 'Laura Petit',
    role: 'Responsable E-commerce',
    company: 'ModeBio France',
    companyLogo: 'https://images.unsplash.com/photo-1441986300917-64674bd600d8?w=100&h=100&fit=crop',
    avatar: 'https://images.unsplash.com/photo-1487412720507-e7ab37603c6f?w=150&h=150&fit=crop',
    quote: 'Le jeu concours de Noël organisé par MSC nous a permis de collecter 8 000 emails qualifiés en 2 semaines. Un vrai succès viral !',
    rating: 5,
    service: 'marketing',
    projectType: 'Jeu Concours en Ligne',
    videoUrl: null
  },
  {
    id: 8,
    name: 'François Dubois',
    role: 'Gérant',
    company: 'Pizzeria Napoli',
    companyLogo: 'https://images.unsplash.com/photo-1513104890138-7c749659a591?w=100&h=100&fit=crop',
    avatar: 'https://images.unsplash.com/photo-1506794778202-cad84cf45f1d?w=150&h=150&fit=crop',
    quote: 'Les campagnes SMS du mardi midi nous ont permis d\'augmenter notre chiffre d\'affaires de 40%. Simple et ultra efficace !',
    rating: 5,
    service: 'marketing',
    projectType: 'Campagne SMS Marketing',
    videoUrl: null
  }
];

// ============================================
// PORTFOLIO DATA
// ============================================

const PORTFOLIO = [
  {
    id: 1,
    title: "Burger King - Campagne locale",
    client: "Burger King",
    clientLogo: "https://agence-msc.fr/wp-content/uploads/2023/09/Burger-King.jpg",
    category: "graphique",
    description: "Conception et déploiement d'une campagne de communication locale pour l'ouverture d'un nouveau restaurant. Création d'affiches, flyers, habillage vitrine et supports digitaux.",
    image: "https://images.unsplash.com/photo-1568901346375-23c9450c58cd?w=800",
    tags: ["Campagne", "Print", "Digital"],
    slug: "burger-king-campagne-locale",
    results: ["+200% de visites le jour d'ouverture", "15 000 flyers distribués", "Couverture presse locale"]
  },
  {
    id: 2,
    title: "Orpi - Identité visuelle agence",
    client: "Orpi Istres",
    clientLogo: "https://agence-msc.fr/wp-content/uploads/2023/09/604655399ffd0_orpi_logo_rvb.png",
    category: "graphique",
    description: "Refonte de l'identité visuelle d'une agence immobilière avec supports print et digitaux. Création de la signalétique intérieure et extérieure, papeterie et templates réseaux sociaux.",
    image: "https://images.unsplash.com/photo-1560518883-ce09059eeffa?w=800",
    tags: ["Logo", "Charte graphique", "Signalétique"],
    slug: "orpi-identite-visuelle",
    results: ["+35% de notoriété locale", "Image modernisée", "Cohérence multi-supports"]
  },
  {
    id: 3,
    title: "Lions Club - Site événementiel",
    client: "Lions Club",
    clientLogo: "https://agence-msc.fr/wp-content/uploads/2023/09/lions-club-logo-general.png",
    category: "web",
    description: "Création d'un site web pour la gestion des événements et des adhésions du club. Système d'inscription en ligne, galerie photos, et espace membres sécurisé.",
    image: "https://images.unsplash.com/photo-1517245386807-bb43f82c33c4?w=800",
    fullImage: "https://images.unsplash.com/photo-1517245386807-bb43f82c33c4?w=800&h=2000&fit=crop",
    tags: ["Site web", "Responsive", "CMS"],
    slug: "lions-club-site-evenementiel",
    results: ["+300% d'inscriptions en ligne", "Gestion simplifiée", "2000 visiteurs/mois"]
  },
  {
    id: 4,
    title: "IPHB - Vidéo partenariat",
    client: "IPHB",
    clientLogo: "https://agence-msc.fr/wp-content/uploads/2023/09/311515644_104473865794171_9211967251882412042_n.jpg",
    category: "video",
    description: "Réalisation d'une vidéo de présentation du partenariat sportif et des actions menées. Tournage sur 2 jours, interviews des acteurs clés et montage dynamique.",
    image: "https://images.unsplash.com/photo-1546519638-68e109498ffc?w=800",
    videoUrl: "https://assets.mixkit.co/videos/preview/mixkit-basketball-player-scoring-a-goal-511-large.mp4",
    tags: ["Vidéo corporate", "Montage", "Interview"],
    slug: "iphb-video-partenariat",
    results: ["50 000 vues sur YouTube", "Reprise par 3 médias locaux", "Nouveaux sponsors attirés"]
  },
  {
    id: 5,
    title: "Restaurant Le Provençal - E-commerce",
    client: "Le Provençal",
    category: "web",
    description: "Développement d'une boutique en ligne pour la vente de produits locaux et click & collect. Intégration d'un système de réservation de créneaux de retrait.",
    image: "https://images.unsplash.com/photo-1414235077428-338989a2e8c0?w=800",
    fullImage: "https://images.unsplash.com/photo-1414235077428-338989a2e8c0?w=800&h=2000&fit=crop",
    tags: ["E-commerce", "Click & Collect", "Paiement en ligne"],
    slug: "restaurant-provencal-ecommerce",
    results: ["+45% de CA en 6 mois", "500 commandes/mois", "Note client 4.8/5"]
  },
  {
    id: 6,
    title: "Startup Tech - Motion Design",
    client: "InnoTech",
    category: "video",
    description: "Animation explicative en motion design pour présenter une solution SaaS innovante. Script, storyboard, animation 2D et sound design personnalisé.",
    image: "https://images.unsplash.com/photo-1550745165-9bc0b252726f?w=800",
    videoUrl: "https://assets.mixkit.co/videos/preview/mixkit-abstract-technology-network-connections-27619-large.mp4",
    tags: ["Motion design", "Animation", "Explainer"],
    slug: "innotech-motion-design",
    results: ["Vidéo intégrée au pitch deck", "Levée de fonds réussie", "100 000 vues LinkedIn"]
  },
  {
    id: 7,
    title: "Cabinet d'avocats - Branding complet",
    client: "Cabinet Dupont & Associés",
    category: "graphique",
    description: "Création de l'identité visuelle complète : logo, papeterie, signalétique et templates. Image premium et professionnelle pour un cabinet en pleine croissance.",
    image: "https://images.unsplash.com/photo-1589829545856-d10d557cf95f?w=800",
    tags: ["Branding", "Print", "Web"],
    slug: "cabinet-dupont-branding",
    results: ["Image premium établie", "+25% de nouveaux clients", "Reconnaissance sectorielle"]
  },
  {
    id: 8,
    title: "Mairie d'Istres - Communication événementielle",
    client: "Mairie d'Istres",
    clientLogo: "https://agence-msc.fr/wp-content/uploads/2023/09/th-2.jpg",
    category: "graphique",
    description: "Conception des supports de communication pour les événements municipaux annuels. Affiches grand format, programmes, flyers et habillage urbain.",
    image: "https://images.unsplash.com/photo-1569974507005-6dc61f97fb5c?w=800",
    tags: ["Affichage", "Flyers", "Programmes"],
    slug: "mairie-istres-evenementiel",
    results: ["+40% de participation", "500 affiches déployées", "Couverture médias régionaux"]
  },
  {
    id: 9,
    title: "Boutique Mode - Refonte e-commerce",
    client: "Fashion Store",
    category: "web",
    description: "Refonte complète de la boutique en ligne avec optimisation UX et intégration paiement.",
    image: "https://images.unsplash.com/photo-1441986300917-64674bd600d8?w=800",
    fullImage: "https://images.unsplash.com/photo-1441986300917-64674bd600d8?w=800&h=2000&fit=crop",
    tags: ["E-commerce", "UX/UI", "SEO"],
    slug: "fashion-store-ecommerce"
  },
  {
    id: 10,
    title: "ModeBio - Jeu Concours Noël",
    client: "ModeBio France",
    category: "marketing",
    description: "Création d'un jeu concours calendrier de l'avent digital avec 8000 participants.",
    image: "https://images.unsplash.com/photo-1512389142860-9c449e58a814?w=800",
    tags: ["Jeu Concours", "Lead Gen", "Viral"],
    slug: "modebio-jeu-concours"
  },
  {
    id: 11,
    title: "Pizzeria Napoli - Campagne SMS",
    client: "Pizzeria Napoli",
    category: "marketing",
    description: "Mise en place de campagnes SMS hebdomadaires pour booster le CA du mardi.",
    image: "https://images.unsplash.com/photo-1513104890138-7c749659a591?w=800",
    tags: ["SMS Marketing", "ROI +40%", "Fidélisation"],
    slug: "pizzeria-napoli-sms"
  },
  {
    id: 12,
    title: "Groupe Immobilier - Google Ads",
    client: "Lefebvre Immobilier",
    category: "marketing",
    description: "Campagne Google Ads génération de leads pour 5 agences immobilières.",
    image: "https://images.unsplash.com/photo-1560518883-ce09059eeffa?w=800",
    tags: ["Google Ads", "Lead Gen", "Immobilier"],
    slug: "lefebvre-google-ads"
  },
  {
    id: 13,
    title: "Coach Business - LinkedIn Ads",
    client: "Success Coaching",
    category: "marketing",
    description: "Stratégie Social Ads LinkedIn pour générer des demandes de coaching.",
    image: "https://images.unsplash.com/photo-1552664730-d307ca884978?w=800",
    tags: ["LinkedIn Ads", "B2B", "Coaching"],
    slug: "success-coaching-linkedin"
  },
  {
    id: 14,
    title: "Salon de Coiffure - Site Réservation",
    client: "Hair Style Studio",
    category: "web",
    description: "Site vitrine avec système de réservation en ligne et paiement d'acompte.",
    image: "https://images.unsplash.com/photo-1560066984-138dadb4c035?w=800",
    fullImage: "https://images.unsplash.com/photo-1560066984-138dadb4c035?w=800&h=2000&fit=crop",
    tags: ["Réservation", "Paiement", "Beauté"],
    slug: "hair-style-reservation"
  },
  {
    id: 15,
    title: "Festival Musique - Aftermovie",
    client: "Summer Vibes Festival",
    category: "video",
    description: "Captation et montage aftermovie du festival avec 50 000 vues sur YouTube.",
    image: "https://images.unsplash.com/photo-1459749411175-04bf5292ceea?w=800",
    videoUrl: "https://assets.mixkit.co/videos/preview/mixkit-dj-playing-music-at-a-concert-4478-large.mp4",
    tags: ["Événementiel", "Aftermovie", "Drone"],
    slug: "summer-vibes-aftermovie"
  },
  {
    id: 16,
    title: "Vignoble AOC - Brochure Premium",
    client: "Domaine du Soleil",
    category: "graphique",
    description: "Brochure 24 pages pour présenter le vignoble et sa gamme de vins.",
    image: "https://images.unsplash.com/photo-1506377247377-2a5b3b417ebb?w=800",
    tags: ["Brochure", "Premium", "Vin"],
    slug: "domaine-soleil-brochure"
  },
  {
    id: 17,
    title: "Radio Locale - Jingle et Habillage Sonore",
    client: "Radio Sud FM",
    category: "audio",
    description: "Création de l'habillage sonore complet de la radio : jingles, virgules, génériques et identité musicale. Production professionnelle avec mixage et mastering.",
    image: "https://images.unsplash.com/photo-1478737270239-2f02b77fc618?w=800",
    audioUrl: "https://www.soundhelix.com/examples/mp3/SoundHelix-Song-1.mp3",
    tags: ["Jingle", "Habillage", "Radio"],
    slug: "radio-sud-habillage",
    results: ["12 jingles produits", "Identité sonore cohérente", "Diffusion 24h/24"]
  },
  {
    id: 18,
    title: "Podcast Entrepreneur - Production Audio",
    client: "Business Talks",
    category: "audio",
    description: "Production et post-production d'un podcast hebdomadaire. Montage, mixage, sound design et distribution sur toutes les plateformes.",
    image: "https://images.unsplash.com/photo-1590602847861-f357a9332bbc?w=800",
    audioUrl: "https://www.soundhelix.com/examples/mp3/SoundHelix-Song-2.mp3",
    tags: ["Podcast", "Montage", "Distribution"],
    slug: "business-talks-podcast",
    results: ["50 épisodes produits", "10 000 écoutes/mois", "Top 50 Business France"]
  },
  {
    id: 19,
    title: "Tutoriel SaaS - Vidéo YouTube",
    client: "CloudApp Pro",
    category: "video",
    description: "Série de tutoriels vidéo pour YouTube avec captures d'écran, animations et voix off professionnelle. Optimisation SEO YouTube incluse.",
    image: "https://images.unsplash.com/photo-1611162617474-5b21e879e113?w=800",
    youtubeUrl: "https://www.youtube.com/watch?v=dQw4w9WgXcQ",
    tags: ["YouTube", "Tutoriel", "SaaS"],
    slug: "cloudapp-tutoriels",
    results: ["250 000 vues totales", "+500 abonnés/mois", "Réduction support -30%"]
  },
  {
    id: 20,
    title: "E-commerce Mode - Site Premium",
    client: "Élégance Paris",
    category: "web",
    description: "Boutique en ligne haut de gamme avec expérience immersive. Design sur-mesure, animations fluides et système de personnalisation produit.",
    image: "https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=800",
    fullImage: "https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=800&h=2400&fit=crop",
    desktopScreenshot: "https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=1200&h=3000&fit=crop",
    tabletScreenshot: "https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=800&h=2000&fit=crop",
    mobileScreenshot: "https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=400&h=1600&fit=crop",
    websiteUrl: "https://elegance-paris.fr",
    tags: ["E-commerce", "Luxe", "Shopify"],
    slug: "elegance-paris-ecommerce",
    results: ["+180% de conversions", "Panier moyen 150€", "Temps de session +45%"]
  }
];

const CLIENTS = [
  { name: "Burger King", logo: "https://agence-msc.fr/wp-content/uploads/2023/09/Burger-King.jpg", url: "https://www.burgerking.fr" },
  { name: "Orpi", logo: "https://agence-msc.fr/wp-content/uploads/2023/09/604655399ffd0_orpi_logo_rvb.png", url: "https://www.orpi.com" },
  { name: "Lions Club", logo: "https://agence-msc.fr/wp-content/uploads/2023/09/lions-club-logo-general.png", url: "https://www.lions-france.org" },
  { name: "AFPA", logo: "https://agence-msc.fr/wp-content/uploads/2023/09/th-1.jpg", url: "https://www.afpa.fr" },
  { name: "Mairie d'Istres", logo: "https://agence-msc.fr/wp-content/uploads/2023/09/th-2.jpg", url: "https://www.istres.fr" },
  { name: "IPHB", logo: "https://agence-msc.fr/wp-content/uploads/2023/09/311515644_104473865794171_9211967251882412042_n.jpg", url: "#" }
];

// ============================================
// MAIN COMPONENT
// ============================================

const AgencyWebsitePage = ({ pageType }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const params = useParams();
  const { slug, projectSlug } = params;
  
  const [loading, setLoading] = useState(true);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [showScrollTop, setShowScrollTop] = useState(false);
  const [showMegaMenu, setShowMegaMenu] = useState(false);
  const [showCallbackModal, setShowCallbackModal] = useState(false);
  const [showQuoteModal, setShowQuoteModal] = useState(false);
  const [showMissionModal, setShowMissionModal] = useState(false);
  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const [showOnboardingModal, setShowOnboardingModal] = useState(false);
  const [showHostingPopup, setShowHostingPopup] = useState(false);
  const [showTeamVideoModal, setShowTeamVideoModal] = useState(false);
  const [pendingQuote, setPendingQuote] = useState({ service: '', solution: '' });
  const [selectedHostingPlan, setSelectedHostingPlan] = useState(null);
  const [copiloteMode, setCopiloteMode] = useState(false);
  const [portfolioFilter, setPortfolioFilter] = useState('Tous');
  const [selectedTeamMember, setSelectedTeamMember] = useState(null);
  const [callbackForm, setCallbackForm] = useState({ name: '', phone: '', email: '', message: '' });
  const [quoteForm, setQuoteForm] = useState({ name: '', company: '', email: '', phone: '', service: '', solution: '', budget: '', message: '', hosting: '' });
  const [missionForm, setMissionForm] = useState({ company: '', objective: '', timeline: '', budget: '', kpis: '' });
  
  // CMS Data states - must be declared before use
  const [heroData, setHeroData] = useState(null);
  const [cmsTeam, setCmsTeam] = useState(null);
  const [teamPageConfig, setTeamPageConfig] = useState(null);
  const [cmsPortfolio, setCmsPortfolio] = useState(null);
  const [cmsPartners, setCmsPartners] = useState(null);
  const [cmsTestimonials, setCmsTestimonials] = useState(null);
  const [googleReviews, setGoogleReviews] = useState({ config: null, reviews: [] });
  const [cmsTimeline, setCmsTimeline] = useState(null);
  const [agencySchedule, setAgencySchedule] = useState(DEFAULT_SCHEDULE);
  // CMS Services - loaded dynamically from API
  const [cmsServices, setCmsServices] = useState(null);
  
  // Welcome Popup state
  const [welcomePopup, setWelcomePopup] = useState({ show: false, config: null });
  
  // Game Contest Popup state
  const [gamePopup, setGamePopup] = useState({ show: false, game: null });
  const [dontShowGamePopupWeek, setDontShowGamePopupWeek] = useState(false);
  
  // COPILOTE Multi-step form state
  const [copiloteStep, setCopiloteStep] = useState(0);
  const [copiloteForm, setCopiloteForm] = useState({
    // Step 1: Company info
    companyName: '',
    sector: '',
    clientType: '',
    avgBasket: '',
    // Step 2: Communication
    currentChannels: [],
    whoManages: '',
    hasCommercialTeam: null,
    availableTime: null,
    // Step 3: Contact & Objectives
    contactName: '',
    email: '',
    phone: '',
    objectives: [],
    budget: 3000,
    expectations: ''
  });
  
  // Solution tabs for services with sub-categories
  const [activeSolution, setActiveSolution] = useState(null);
  
  // Floating service menu state
  const [showServiceMenu, setShowServiceMenu] = useState(false);
  
  // Confirmation Modal State
  const [confirmationModal, setConfirmationModal] = useState({ 
    isOpen: false, 
    type: 'success', 
    userName: '' 
  });
  
  // Theme settings from backend
  const [themeSettings, setThemeSettings] = useState(null);
  // Agency contact info from backend
  const [agencyInfo, setAgencyInfo] = useState(null);
  // CMS Logos
  const [cmsLogos, setCmsLogos] = useState(null);
  // Site settings for lead pages configuration
  const [siteSettings, setSiteSettings] = useState(null);

  const [socialLinks, setSocialLinks] = useState(null);
  // Key Stats from CMS
  const [keyStats, setKeyStats] = useState([
    { id: "1", value: 15, suffix: "+", label: "Années d'expérience" },
    { id: "2", value: 200, suffix: "+", label: "Clients accompagnés" },
    { id: "3", value: 500, suffix: "+", label: "Créations réalisées" },
    { id: "4", value: 6, suffix: "", label: "Experts passionnés" }
  ]);
  // Site Content from CMS
  const [siteContent, setSiteContent] = useState({
    hero: {
      badge: "Agence de communication depuis 2012",
      title: "Boostons la **visibilité** de votre entreprise",
      subtitle: "Agence de communication créative à Istres",
      cta_text: "Demander un devis gratuit",
      services_line: "Logos • Visuels • Vidéos • Sites web • Marketing"
    },
    contact: {
      response_time: "Réponse sous 24h",
      availability: "Lun-Ven 9h-18h"
    },
    copilote: {
      badge: "Programme Premium",
      title: "COPILOTE MSC",
      subtitle: "Accompagnement stratégique sur 12 mois"
    },
    footer: {
      tagline: "Votre partenaire créatif depuis 2009",
      description: "Votre partenaire communication à Istres depuis 2012. Création web, identité visuelle, production vidéo et marketing digital."
    }
  });
  
  const videoRef = useRef(null);

  // Use CMS services if available, otherwise fallback to static SERVICES
  const activeServices = cmsServices || SERVICES;
  const currentService = slug ? activeServices.find(s => s.id === slug) : null;
  const isProjectPage = !!projectSlug;
  const isHomePage = (!slug || slug === 'accueil') && !isProjectPage && !pageType;
  const isTimelinePage = slug === 'timeline-communication';
  const isTeamPage = pageType === 'equipe';
  const isPortfolioPage = pageType === 'realisations';
  
  const currentProject = projectSlug ? (cmsPortfolio || PORTFOLIO).find(p => p.slug === projectSlug || String(p.id) === projectSlug) : null;

  // Function to handle quote request - shows hosting popup for web services
  const handleQuoteRequest = (serviceName = '', solutionName = '') => {
    const isWebService = serviceName.toLowerCase().includes('web') || 
                         currentService?.id === 'creation-web' ||
                         solutionName?.toLowerCase().includes('site') ||
                         solutionName?.toLowerCase().includes('landing') ||
                         solutionName?.toLowerCase().includes('boutique') ||
                         solutionName?.toLowerCase().includes('pwa');
    
    // For now, skip the hosting popup and go directly to quote modal
    // if (isWebService) {
    //   setPendingQuote({ service: serviceName, solution: solutionName });
    //   setSelectedHostingPlan(null);
    //   setShowHostingPopup(true);
    // } else {
    //   openQuoteModal(serviceName, solutionName);
    // }
    
    // Disabled hosting popup for now - go directly to quote modal
    openQuoteModal(serviceName, solutionName);
  };

  // Continue to quote modal after hosting selection
  const continueToQuote = () => {
    setQuoteForm(prev => ({
      ...prev,
      service: pendingQuote.service || currentService?.title || '',
      solution: pendingQuote.solution,
      hosting: selectedHostingPlan ? SUBSCRIPTION_OFFERS.find(p => p.id === selectedHostingPlan)?.name : ''
    }));
    setShowHostingPopup(false);
    setShowQuoteModal(true);
  };

  // Function to open quote modal directly (without hosting popup)
  const openQuoteModal = (serviceName = '', solutionName = '') => {
    console.log('openQuoteModal called with:', serviceName, solutionName);
    setQuoteForm(prev => ({
      ...prev,
      service: serviceName || currentService?.title || '',
      solution: solutionName
    }));
    setShowQuoteModal(true);
    console.log('showQuoteModal set to true');
  };

  // Function to handle quote action - redirect to external page or open modal
  const handleQuoteAction = (serviceName = '', solutionName = '') => {
    const leadPages = siteSettings?.lead_pages;
    if (leadPages?.use_external_pages && leadPages?.quote_page_url) {
      // Redirect to external landing page
      window.open(leadPages.quote_page_url, '_blank');
    } else {
      // Open internal modal
      handleQuoteRequest(serviceName, solutionName);
    }
  };

  // Function to handle callback action - redirect to external page or open modal
  const handleCallbackAction = () => {
    const leadPages = siteSettings?.lead_pages;
    if (leadPages?.use_external_pages && leadPages?.callback_page_url) {
      // Redirect to external landing page
      window.open(leadPages.callback_page_url, '_blank');
    } else {
      // Open internal modal
      setShowCallbackModal(true);
    }
  };

  // Set default active solution when service changes
  useEffect(() => {
    if (currentService?.hasSolutions && currentService.solutions?.length > 0) {
      setActiveSolution(currentService.solutions[0].id);
    } else {
      setActiveSolution(null);
    }
  }, [currentService]);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
      setShowScrollTop(window.scrollY > 500);
    };
    window.addEventListener('scroll', handleScroll);
    setLoading(false);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Fetch theme settings, agency info and CMS data on mount
  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const [themeRes, agencyRes, cmsRes, logosRes, reviewsRes, siteSettingsRes, keyStatsRes, siteContentRes, socialRes, teamPageConfigRes] = await Promise.all([
          api.get('/settings/theme'),
          api.get('/settings/agency-info'),
          api.get('/cms/all'),
          api.get('/cms/logos'),
          api.get('/cms/google-reviews'),
          api.get('/cms/site-settings').catch(() => ({ data: null })),
          api.get('/settings/key-stats').catch(() => ({ data: null })),
          api.get('/settings/site-content').catch(() => ({ data: null })),
          api.get('/cms/social-links').catch(() => ({ data: null })),
          api.get('/cms/team-page-config').catch(() => ({ data: null }))
        ]);
        if (themeRes.data) {
          // Merge site-settings clientPortal or header_button into themeSettings.clientPortal
          const mergedTheme = { ...themeRes.data };
          // Priority: clientPortal > header_button
          if (siteSettingsRes.data?.clientPortal?.url) {
            mergedTheme.clientPortal = {
              url: siteSettingsRes.data.clientPortal.url,
              label: siteSettingsRes.data.clientPortal.label || 'Espace Client',
              isVisible: true,
              openInNewTab: siteSettingsRes.data.clientPortal.openInNewTab || false,
            };
          } else if (siteSettingsRes.data?.header_button) {
            mergedTheme.clientPortal = {
              url: siteSettingsRes.data.header_button.url || '/login',
              label: siteSettingsRes.data.header_button.text || 'Espace Client',
              isVisible: siteSettingsRes.data.header_button.is_visible !== false,
              openInNewTab: siteSettingsRes.data.header_button.open_new_tab || false,
            };
          }
          setThemeSettings(mergedTheme);
        }
        if (agencyRes.data) {
          setAgencyInfo(agencyRes.data);
        }
        if (logosRes.data) {
          setCmsLogos(logosRes.data);
        }
        if (reviewsRes.data) {
          setGoogleReviews(reviewsRes.data);
        }
        if (keyStatsRes.data?.stats) {
          setKeyStats(keyStatsRes.data.stats);
        }
        if (siteContentRes.data && Object.keys(siteContentRes.data).length > 1) {
          setSiteContent(prev => ({ ...prev, ...siteContentRes.data }));
        }
        if (socialRes.data) {
          setSocialLinks(socialRes.data);
        }
        if (teamPageConfigRes.data) {
          setTeamPageConfig(teamPageConfigRes.data);
        }
        // Store site settings for lead pages configuration
        if (siteSettingsRes.data) {
          setSiteSettings(siteSettingsRes.data);
        }
        if (cmsRes.data) {
          if (cmsRes.data.hero) setHeroData(cmsRes.data.hero);
          if (cmsRes.data.team?.length > 0) setCmsTeam(cmsRes.data.team);
          if (cmsRes.data.portfolio?.length > 0) setCmsPortfolio(cmsRes.data.portfolio);
          if (cmsRes.data.partners?.length > 0) setCmsPartners(cmsRes.data.partners);
          if (cmsRes.data.testimonials?.length > 0) setCmsTestimonials(cmsRes.data.testimonials);
        }
        
        // Load schedule from theme settings
        if (themeRes.data?.schedule) {
          const convertedSchedule = convertScheduleFromApi(themeRes.data.schedule);
          setAgencySchedule(convertedSchedule);
        }
        
        // Load timeline from CMS
        const timelineRes = await api.get('/website/timeline');
        if (timelineRes.data?.length > 0) {
          setCmsTimeline(timelineRes.data);
        }
        
        // Load services from CMS
        const servicesRes = await api.get('/website/services');
        
        // Load solutions from CMS
        const solutionsRes = await api.get('/cms/solutions').catch(() => ({ data: [] }));
        const cmsSolutions = solutionsRes.data || [];
        
        if (servicesRes.data?.length > 0) {
          // Map API services to local format with icon components and solutions
          const mappedServices = servicesRes.data.map(svc => {
            // Map icon string to Lucide component
            const iconMap = {
              'Palette': Palette, 'Globe': Globe, 'Monitor': Monitor, 'Film': Film,
              'Video': Film, 'Megaphone': Megaphone, 'Shield': Shield, 'Target': Target,
              'PenTool': PenTool, 'Code': Code, 'Smartphone': Smartphone
            };
            
            // Icon map for solutions
            const solutionIconMap = {
              'Sparkles': Sparkles, 'Layers': Layers, 'FileText': FileText, 'Target': Target,
              'MousePointer': MousePointer, 'ShoppingCart': ShoppingCart, 'Smartphone': Smartphone,
              'Layout': Layout, 'Code': Code, 'Film': Film, 'Camera': Camera, 'Video': Film,
              'Megaphone': Megaphone, 'Mail': Mail, 'MessageSquare': MessageSquare, 'BarChart': BarChart,
              'Search': Search, 'Globe': Globe, 'Pen': Pen, 'PenTool': PenTool, 'Gift': Gift
            };
            
            // Get solutions from CMS for this service family
            const serviceSolutions = cmsSolutions
              .filter(sol => sol.family_id === svc.slug && sol.is_active !== false)
              .sort((a, b) => (a.order || 0) - (b.order || 0))
              .map(sol => ({
                id: sol.id,
                name: sol.name,
                icon: solutionIconMap[sol.icon] || Sparkles,
                description: sol.description || '',
                longDesc: sol.long_desc || sol.why_do_it || sol.description || '',
                priceRange: sol.price_range || '',
                priceFrom: sol.price_from || 0,
                deliveryTime: sol.delivery_time || '',
                badge: sol.badge || null,
                features: sol.features || [],
                includes: sol.includes || [],
                whyDoIt: sol.why_do_it || '',
                whenToDoIt: sol.when_to_do_it || '',
                idealFor: sol.ideal_for || [],
                notIdealFor: sol.not_ideal_for || [],
                hasDetailPage: sol.has_detail_page || sol.id === 'sol-jeux-concours',
                detailPageUrl: sol.detail_page_url || (sol.id === 'sol-jeux-concours' ? '/site/jeux-concours' : null),
                examples: sol.examples || []
              }));
            
            // Fallback to static solutions if no CMS solutions
            const fallbackSolutionsMap = {
              'creation-graphique': GRAPHIC_SOLUTIONS,
              'creation-web': WEB_SOLUTIONS,
              'creation-video': VIDEO_SOLUTIONS,
              'marketing-digital': MARKETING_SOLUTIONS,
              'applications': APP_SOLUTIONS
            };
            
            const solutions = serviceSolutions.length > 0 
              ? serviceSolutions 
              : (fallbackSolutionsMap[svc.slug] || []);
            
            // Map slug to color gradient
            const colorMap = {
              'creation-graphique': 'from-pink-500 to-rose-600',
              'creation-web': 'from-cyan-500 to-blue-600',
              'creation-video': 'from-purple-500 to-violet-600',
              'marketing-digital': 'from-orange-500 to-amber-600',
              'hebergement': 'from-cyan-500 to-blue-600',
              'applications': 'from-emerald-500 to-teal-600'
            };
            
            const bgColorMap = {
              'creation-graphique': 'bg-pink-50',
              'creation-web': 'bg-cyan-50',
              'creation-video': 'bg-purple-50',
              'marketing-digital': 'bg-orange-50',
              'hebergement': 'bg-cyan-50',
              'applications': 'bg-emerald-50'
            };
            
            return {
              id: svc.slug,
              title: svc.name,
              shortTitle: svc.short_name || svc.name.split(' ')[0],
              icon: iconMap[svc.icon] || Globe,
              color: colorMap[svc.slug] || 'from-slate-500 to-slate-600',
              bgColor: bgColorMap[svc.slug] || 'bg-slate-50',
              description: svc.description || '',
              longDescription: svc.long_description || svc.description || '',
              features: svc.features || [],
              hasSolutions: solutions.length > 0,
              solutions: solutions,
              heroMedia: svc.hero_media || { type: 'image', url: 'https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=1200&q=80' },
              process: svc.process || [
                { step: 1, title: 'Analyse', desc: 'Étude de vos besoins' },
                { step: 2, title: 'Conception', desc: 'Création des maquettes' },
                { step: 3, title: 'Réalisation', desc: 'Production du projet' },
                { step: 4, title: 'Livraison', desc: 'Mise en ligne et suivi' }
              ]
            };
          });
          setCmsServices(mappedServices);
        }
        return siteSettingsRes?.data || null;
      } catch (error) {
        console.error('Error fetching settings:', error);
        return null;
      }
    };
    
    // Fetch popup config
    const fetchPopup = async () => {
      try {
        const res = await api.get('/cms/popup');
        const popup = res.data;
        if (popup?.enabled) {
          // Check if popup should show (date range)
          const now = new Date();
          const startDate = popup.start_date ? new Date(popup.start_date) : null;
          const endDate = popup.end_date ? new Date(popup.end_date) : null;
          
          const isInDateRange = (!startDate || now >= startDate) && (!endDate || now <= endDate);
          
          // Check session storage for "show once" feature
          const popupDismissed = sessionStorage.getItem('popupDismissed');
          const shouldShow = isInDateRange && (!popup.show_once || !popupDismissed);
          
          if (shouldShow) {
            // Delay popup display for better UX
            setTimeout(() => {
              setWelcomePopup({ show: true, config: popup });
            }, 1500);
          }
        }
      } catch (error) {
        console.error('Error fetching popup:', error);
      }
    };
    
    // Fetch main game contest and annual game for promotional popup
    const fetchMainGame = async () => {
      try {
        const currentYear = new Date().getFullYear();
        
        // First, try to get the annual game (priority)
        try {
          const annualRes = await api.get(`/game/annual-game/${currentYear}`);
          if (annualRes.data && annualRes.data.prize) {
            // Check both session storage and localStorage with expiration
            const gamePopupDismissed = sessionStorage.getItem('gamePopupDismissed');
            const gamePopupHiddenUntil = localStorage.getItem('gamePopupHiddenUntil');
            const isHiddenForWeek = gamePopupHiddenUntil && new Date(gamePopupHiddenUntil) > new Date();
            
            if (!gamePopupDismissed && !isHiddenForWeek) {
              setTimeout(() => {
                setGamePopup({ 
                  show: true, 
                  game: {
                    ...annualRes.data,
                    id: `annual-${currentYear}`,
                    title: annualRes.data.name || `Le Coffre MSC ${currentYear}`,
                    description: "Collectez les 24 caractères de la combinaison secrète pour déverrouiller le coffre et remporter le gros lot ! Le premier à ouvrir le coffre le 24 décembre gagne !",
                    is_annual_game: true,
                    time_limit_seconds: 30,
                    points_per_correct: 10
                  },
                  isAnnualGame: true
                });
              }, 3000);
              return; // Don't fetch main game if annual exists
            }
          }
        } catch (annualError) {
          // No annual game, continue to main game
        }
        
        // Fallback to main game
        const res = await api.get('/game/main-game');
        if (res.data && res.data.id) {
          // Check both session storage and localStorage with expiration
          const gamePopupDismissed = sessionStorage.getItem('gamePopupDismissed');
          const gamePopupHiddenUntil = localStorage.getItem('gamePopupHiddenUntil');
          const isHiddenForWeek = gamePopupHiddenUntil && new Date(gamePopupHiddenUntil) > new Date();
          
          if (!gamePopupDismissed && !isHiddenForWeek) {
            setTimeout(() => {
              setGamePopup({ show: true, game: res.data, isAnnualGame: false });
            }, 3000);
          }
        }
      } catch (error) {
        console.debug('No main game available:', error);
      }
    };
    
    // Chain: fetch settings first, then conditionally fetch game popup
    fetchSettings().then((resolvedSettings) => {
      if (resolvedSettings?.game_section?.enabled) {
        fetchMainGame();
      }
    });
    fetchPopup();
    
    // Track page view for analytics
    const trackPageView = async () => {
      try {
        await api.post('/cms/analytics/track', {
          page: window.location.pathname,
          referrer: document.referrer,
          user_agent: navigator.userAgent
        });
      } catch (error) {
        // Silently fail - analytics tracking is not critical
        console.debug('Analytics tracking failed:', error);
      }
    };
    trackPageView();
  }, []);

  const scrollToSection = (id) => {
    // If we're not on the homepage, navigate there first
    if (isTeamPage || isPortfolioPage || currentService) {
      navigate(`/site#${id}`);
      return;
    }
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
    setMobileMenuOpen(false);
    setShowMegaMenu(false);
  };

  // External lead management endpoint
  const LEAD_API_ENDPOINT = 'https://portfolio-preview-104.preview.emergentagent.com/api/leads/embed';

  const handleCallbackSubmit = async (e) => {
    e.preventDefault();
    try {
      const firstName = callbackForm.name.split(' ')[0] || 'vous';
      // Send to external lead management tool
      const response = await fetch(LEAD_API_ENDPOINT, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          form_type: 'callback',
          name: callbackForm.name,
          phone: callbackForm.phone,
          email: callbackForm.email,
          message: callbackForm.message,
          page_url: window.location.href
        })
      });
      
      const result = await response.json();
      if (!result.success) throw new Error(result.message);
      
      setShowCallbackModal(false);
      setCallbackForm({ name: '', phone: '', email: '', message: '' });
      // Show beautiful centered confirmation modal
      setConfirmationModal({ isOpen: true, type: 'callback', userName: firstName });
    } catch (error) {
      console.error('Error submitting callback request:', error);
      toast.error('Erreur lors de l\'envoi. Veuillez réessayer.');
    }
  };

  const handleQuoteSubmit = async (e) => {
    e.preventDefault();
    try {
      const firstName = quoteForm.name.split(' ')[0] || 'vous';
      // Send to external lead management tool
      const response = await fetch(LEAD_API_ENDPOINT, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          form_type: 'quote',
          name: quoteForm.name,
          email: quoteForm.email,
          phone: quoteForm.phone,
          company: quoteForm.company,
          service: quoteForm.service,
          solution: quoteForm.solution,
          hosting: quoteForm.hosting,
          message: quoteForm.message,
          page_url: window.location.href
        })
      });
      
      const result = await response.json();
      if (!result.success) throw new Error(result.message);
      
      setShowQuoteModal(false);
      setQuoteForm({ name: '', email: '', phone: '', company: '', service: '', solution: '', hosting: '', message: '' });
      // Show beautiful centered confirmation modal
      setConfirmationModal({ isOpen: true, type: 'quote', userName: firstName });
    } catch (error) {
      toast.error('Erreur lors de l\'envoi. Veuillez réessayer.');
    }
  };

  const handleMissionSubmit = (e) => {
    e.preventDefault();
    toast.success('✅ Demande COPILOTE envoyée ! Notre équipe évaluera votre éligibilité sous 48h.');
    setShowMissionModal(false);
  };

  // Welcome Popup handlers
  const handleClosePopup = () => {
    setWelcomePopup({ ...welcomePopup, show: false });
    if (welcomePopup.config?.show_once) {
      sessionStorage.setItem('popupDismissed', 'true');
    }
  };

  const handlePopupCTAClick = async () => {
    try {
      await api.post('/cms/popup/click');
    } catch (error) {
      console.error('Error tracking click:', error);
    }
    // Navigate to CTA link
    const link = welcomePopup.config?.cta_link || '#';
    if (link.startsWith('http')) {
      window.open(link, '_blank');
    } else {
      navigate(link);
    }
    handleClosePopup();
  };

  // Game Contest Popup handlers
  const handleCloseGamePopup = () => {
    setGamePopup({ ...gamePopup, show: false });
    sessionStorage.setItem('gamePopupDismissed', 'true');
    
    // If checkbox is checked, hide popup for 1 week
    if (dontShowGamePopupWeek) {
      const oneWeekFromNow = new Date();
      oneWeekFromNow.setDate(oneWeekFromNow.getDate() + 7);
      localStorage.setItem('gamePopupHiddenUntil', oneWeekFromNow.toISOString());
    }
    
    // Reset checkbox state
    setDontShowGamePopupWeek(false);
  };

  const handlePlayGame = () => {
    if (gamePopup.isAnnualGame) {
      // Redirect to player dashboard for annual game
      navigate('/mon-espace-joueur');
    } else if (gamePopup.game?.id) {
      window.open(`/jeu/${gamePopup.game.id}`, '_blank');
    }
    handleCloseGamePopup();
  };

  const getYouTubeEmbedUrl = (url) => {
    if (!url) return null;
    const match = url.match(/(?:youtube\.com\/(?:watch\?v=|embed\/)|youtu\.be\/)([a-zA-Z0-9_-]{11})/);
    if (match) return `https://www.youtube.com/embed/${match[1]}`;
    return null;
  };

  // Brochure download modal state
  const [showBrochureModal, setShowBrochureModal] = useState(false);
  const [brochureForm, setBrochureForm] = useState({ name: '', email: '', phone: '', company: '' });

  const downloadBrochure = () => {
    setShowBrochureModal(true);
  };

  const handleBrochureSubmit = async (e) => {
    e.preventDefault();
    try {
      const firstName = brochureForm.name.split(' ')[0] || 'vous';
      // Save the request
      await api.post('/website/request', {
        type: 'brochure',
        name: brochureForm.name,
        email: brochureForm.email,
        phone: brochureForm.phone,
        company: brochureForm.company,
        source: 'website'
      });
      // Open the PDF brochure in a new tab
      const backendUrl = process.env.REACT_APP_BACKEND_URL || window.location.origin;
      window.open(`${backendUrl}/api/website/brochure/download`, '_blank');
      setShowBrochureModal(false);
      setBrochureForm({ name: '', email: '', phone: '', company: '' });
      // Show beautiful centered confirmation modal
      setConfirmationModal({ isOpen: true, type: 'brochure', userName: firstName });
    } catch (error) {
      toast.error('Erreur lors de l\'envoi.');
    }
  };

  // COPILOTE gradient - pastel blue to violet
  const copiloteGradient = "from-blue-400 via-indigo-400 to-violet-500";
  const copiloteBg = "from-slate-900 via-indigo-950 to-violet-950";

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-900">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-cyan-500 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-white">Chargement...</p>
        </div>
      </div>
    );
  }

  // Get agency status (using dynamic schedule)
  const agencyStatus = getAgencyStatus(agencySchedule);

  return (
    <div className={cn("min-h-screen transition-colors duration-500", copiloteMode ? "bg-slate-950" : "bg-white")}>
      
      {/* ============================================ */}
      {/* NAVIGATION WITH ENHANCED MEGA MENU */}
      {/* ============================================ */}
      <nav className={cn(
        "fixed top-0 left-0 right-0 z-50 transition-all duration-300",
        scrolled || mobileMenuOpen
          ? "bg-white shadow-lg" 
          : isHomePage 
            ? "bg-slate-900/80 backdrop-blur-md"
            : "bg-white/95 backdrop-blur-lg shadow-sm"
      )}>
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex items-center justify-between h-16 lg:h-20">
            {/* Logo - Loaded from theme settings or default */}
            <button 
              onClick={() => { navigate('/site'); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
              className="flex items-center z-50"
            >
              {copiloteMode ? (
                /* Logo COPILOTE - Custom ou texte par défaut */
                <div className="flex items-center gap-2 group cursor-pointer">
                  {themeSettings?.copiloteLogo?.lightUrl ? (
                    /* Logo personnalisé Copilote */
                    <img 
                      src={scrolled || mobileMenuOpen ? (themeSettings.copiloteLogo.darkUrl || themeSettings.copiloteLogo.lightUrl) : themeSettings.copiloteLogo.lightUrl}
                      alt="MSC Copilote"
                      className="h-10 lg:h-12 w-auto object-contain max-w-[140px] transition-all group-hover:scale-105"
                    />
                  ) : (
                    /* Logo texte par défaut */
                    <div className="flex flex-col items-center">
                      <span 
                        className={cn(
                          "font-black text-2xl lg:text-3xl tracking-tight transition-colors",
                          scrolled || mobileMenuOpen 
                            ? "bg-gradient-to-r from-orange-500 via-amber-400 to-yellow-400 bg-clip-text text-transparent"
                            : "text-white"
                        )}
                        style={!scrolled && !mobileMenuOpen ? {} : {filter: 'drop-shadow(0 0 8px rgba(251, 146, 60, 0.3))'}}
                      >
                        MSC
                      </span>
                      <span 
                        className={cn(
                          "text-[10px] lg:text-xs font-semibold -mt-1 tracking-[0.1em] uppercase transition-colors",
                          scrolled || mobileMenuOpen ? "text-orange-500" : "text-white/80"
                        )}
                      >
                        Copilote
                      </span>
                    </div>
                  )}
                </div>
              ) : cmsLogos?.logo_light || cmsLogos?.logo_dark ? (
                /* Logo from CMS - PRIORITY */
                <div className="h-10 lg:h-12 flex items-center">
                  {(() => {
                    // Choose the right logo based on background
                    // Dark background only on homepage when not scrolled and menu closed
                    const isDarkBg = isHomePage && !scrolled && !mobileMenuOpen;
                    const logoPath = isDarkBg ? cmsLogos?.logo_dark : cmsLogos?.logo_light;
                    // Fallback to any available logo
                    const finalLogo = logoPath || cmsLogos?.logo_dark || cmsLogos?.logo_light;
                    // Build full URL - use backend URL or current origin as fallback
                    const baseUrl = process.env.REACT_APP_BACKEND_URL || window.location.origin;
                    const logoUrl = finalLogo?.startsWith('http') 
                      ? finalLogo 
                      : `${baseUrl}${finalLogo?.startsWith('/') ? '' : '/'}${finalLogo}`;
                    
                    return (
                      <img 
                        src={logoUrl}
                        alt={cmsLogos?.logo_alt || "MSC L'Agence"}
                        className="h-full w-auto object-contain max-w-[100px] sm:max-w-[120px] lg:max-w-[150px] transition-all duration-300"
                        style={{ backgroundColor: 'transparent' }}
                        onError={(e) => { 
                          console.error('[Logo Error]', e.target.src);
                          // Show fallback text instead of hiding
                          e.target.style.display = 'none';
                          e.target.parentElement.innerHTML = '<span class="text-white font-bold text-xl">MSC</span>';
                        }}
                      />
                    );
                  })()}
                </div>
              ) : themeSettings?.logo?.lightUrl ? (
                <div className="h-10 sm:h-11 lg:h-12 rounded-lg flex items-center justify-center transition-all hover:scale-105">
                  <img 
                    src={themeSettings.logo.lightUrl} 
                    alt={themeSettings.agency?.name || "Logo"} 
                    className="h-full w-auto object-contain max-w-[100px] sm:max-w-[120px] lg:max-w-[150px] transition-all duration-300"
                  />
                </div>
              ) : (
                <div className={cn(
                  "w-11 h-11 lg:w-12 lg:h-12 rounded-full flex items-center justify-center transition-all border-2 shadow-lg hover:scale-105",
                  "bg-gradient-to-br from-[#0f172a] to-[#1e3a5f] border-cyan-500/30"
                )}>
                  <span className="text-white font-bold text-base">MSC</span>
                </div>
              )}
            </button>

            {/* Mobile: Agency Status (clickable to show schedule) */}
            <button 
              onClick={() => setShowScheduleModal(true)}
              className="flex lg:hidden items-center"
            >
              <div className={cn(
                "flex items-center gap-1.5 px-2.5 py-1.5 rounded-full text-xs transition-all",
                scrolled || mobileMenuOpen ? agencyStatus.bg : "bg-white/20",
                "hover:scale-105"
              )}>
                <Clock className={cn("w-3.5 h-3.5", scrolled || mobileMenuOpen ? agencyStatus.color : "text-white")} />
                <span className={cn("w-2 h-2 rounded-full", agencyStatus.dot)} />
                <span className={cn(
                  "font-medium",
                  scrolled || mobileMenuOpen ? agencyStatus.color : "text-white"
                )}>
                  {agencyStatus.label}
                </span>
                {agencyStatus.isSpecial && (
                  <Sparkles className={cn("w-3.5 h-3.5", scrolled || mobileMenuOpen ? "text-amber-500" : "text-amber-300")} />
                )}
              </div>
            </button>

            {/* Desktop Nav */}
            <div className="hidden lg:flex items-center gap-5">
              <button 
                onClick={() => { navigate('/site'); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
                className={cn(
                  "w-9 h-9 rounded-full flex items-center justify-center transition-all hover:scale-110",
                  scrolled || !isHomePage 
                    ? "bg-slate-100 text-slate-700 hover:bg-cyan-500 hover:text-white" 
                    : "bg-white/20 text-white hover:bg-white/30"
                )}
                title="Accueil"
              >
                <Home className="w-4 h-4" />
              </button>
              
              {/* Services Mega Menu Trigger */}
              <div 
                className="relative"
                onMouseEnter={() => setShowMegaMenu(true)}
                onMouseLeave={() => setShowMegaMenu(false)}
              >
                <button
                  className={cn(
                    "flex items-center gap-1 font-medium transition-colors hover:text-cyan-500 py-2",
                    scrolled || !isHomePage ? "text-slate-700" : "text-white/90"
                  )}
                >
                  Services
                  <ChevronDown className={cn("w-4 h-4 transition-transform", showMegaMenu && "rotate-180")} />
                </button>

                {/* Enhanced Mega Menu - avec zone de connexion invisible */}
                {showMegaMenu && (
                  <>
                    {/* Bridge invisible pour connecter le bouton au menu */}
                    <div className="absolute left-1/2 -translate-x-1/2 top-full w-[1000px] h-6 bg-transparent" />
                    <div className="fixed top-20 left-1/2 -translate-x-1/2 w-[1000px] z-50">
                    <div className="bg-white rounded-2xl shadow-2xl border border-slate-100 overflow-hidden">
                      {/* Services Grid with Media */}
                      <div className="p-6">
                        <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-5 text-center">Nos expertises</p>
                        <div className="grid grid-cols-6 gap-3">
                          {activeServices.map((service) => (
                            <Link
                              key={service.id}
                              to={`/site/${service.id}`}
                              className="group relative overflow-hidden rounded-xl bg-slate-50 hover:bg-slate-100 transition-all"
                              onClick={() => setShowMegaMenu(false)}
                            >
                              {/* Media Banner */}
                              <div className={cn(
                                "h-20 relative overflow-hidden bg-gradient-to-br",
                                service.color
                              )}>
                                {/* Background media from heroMedia */}
                                {service.heroMedia?.type === 'video' ? (
                                  <video 
                                    autoPlay 
                                    loop 
                                    muted 
                                    playsInline
                                    className="absolute inset-0 w-full h-full object-cover opacity-50 group-hover:opacity-70 transition-opacity"
                                  >
                                    <source src={service.heroMedia.url} type="video/mp4" />
                                  </video>
                                ) : service.heroMedia?.type === 'image' ? (
                                  <img 
                                    src={service.heroMedia.url} 
                                    alt=""
                                    className="absolute inset-0 w-full h-full object-cover opacity-40 group-hover:opacity-60 transition-opacity"
                                  />
                                ) : (
                                  <div className="absolute inset-0 bg-gradient-to-br opacity-90" />
                                )}
                                {/* Gradient overlay */}
                                <div className={cn("absolute inset-0 bg-gradient-to-br opacity-70", service.color)} />
                                {/* Icon */}
                                <div className="absolute inset-0 flex items-center justify-center">
                                  <div className="w-10 h-10 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center group-hover:scale-110 transition-transform">
                                    <service.icon className="w-5 h-5 text-white" />
                                  </div>
                                </div>
                              </div>
                              
                              {/* Service Info */}
                              <div className="p-3">
                                <h4 className="font-semibold text-slate-800 text-sm group-hover:text-cyan-600 transition-colors">
                                  {service.shortTitle}
                                </h4>
                                <p className="text-xs text-slate-500 line-clamp-2 mt-1">{service.description.slice(0, 50)}...</p>
                              </div>
                            </Link>
                          ))}
                        </div>
                      </div>
                      
                      {/* COPILOTE Banner - Only when copilote section enabled */}
                      {siteSettings?.copilote_section?.enabled && (
                      <div className={cn(
                        "mx-6 mb-6 rounded-xl overflow-hidden relative",
                        copiloteMode ? `bg-gradient-to-r ${copiloteGradient}` : "bg-gradient-to-r from-slate-800 via-slate-900 to-slate-800"
                      )}>
                        <div className="absolute inset-0 opacity-10">
                          <div className="absolute top-0 right-0 w-32 h-32 bg-white rounded-full blur-3xl" />
                          <div className="absolute bottom-0 left-1/4 w-24 h-24 bg-cyan-400 rounded-full blur-2xl" />
                        </div>
                        <div className="relative flex items-center justify-between p-5">
                          <div className="flex items-center gap-4">
                            <div className="w-12 h-12 rounded-full bg-white/10 backdrop-blur flex items-center justify-center">
                              <Rocket className="w-6 h-6 text-cyan-400" />
                            </div>
                            <div>
                              <div className="flex items-center gap-2">
                                <span className="font-bold text-white text-lg">COPILOTE</span>
                                <span className="px-2 py-0.5 rounded-full bg-cyan-400/20 text-cyan-300 text-xs font-medium">Premium</span>
                              </div>
                              <p className="text-slate-300 text-sm">Pilotage marketing avec ROI garanti • +150% performance</p>
                            </div>
                          </div>
                          <Button 
                            onClick={() => { setShowMegaMenu(false); setShowMissionModal(true); }}
                            className={cn("rounded-full px-6 bg-gradient-to-r shadow-lg", copiloteGradient)}
                          >
                            <Crosshair className="w-4 h-4 mr-2" />
                            Lancer une mission
                          </Button>
                        </div>
                      </div>
                      )}
                      
                      {/* Bottom Bar */}
                      <div className="px-6 py-4 bg-slate-50 border-t flex items-center justify-between">
                        <div className="flex items-center gap-6">
                          <a href="tel:0484492700" className="flex items-center gap-2 text-slate-600 hover:text-cyan-600 transition-colors text-sm">
                            <Phone className="w-4 h-4" /> 04 84 49 27 00
                          </a>
                          <a href="mailto:contact@agence-msc.fr" className="flex items-center gap-2 text-slate-600 hover:text-cyan-600 transition-colors text-sm">
                            <Mail className="w-4 h-4" /> contact@agence-msc.fr
                          </a>
                        </div>
                        <Button variant="ghost" onClick={() => { setShowMegaMenu(false); handleCallbackAction(); }} className="text-cyan-600 text-sm">
                          Être rappelé gratuitement <ArrowRight className="w-4 h-4 ml-2" />
                        </Button>
                      </div>
                    </div>
                  </div>
                  </>
                )}
              </div>

              <Link
                to="/site/realisations"
                className={cn(
                  "font-medium transition-colors hover:text-cyan-500",
                  scrolled || !isHomePage ? "text-slate-700" : "text-white/90"
                )}
              >
                Réalisations
              </Link>
              <Link
                to="/site/equipe"
                className={cn(
                  "font-medium transition-colors hover:text-cyan-500",
                  scrolled || !isHomePage ? "text-slate-700" : "text-white/90"
                )}
              >
                L'équipe
              </Link>
              <button
                onClick={() => scrollToSection('contact')}
                className={cn(
                  "font-medium transition-colors hover:text-cyan-500",
                  scrolled || !isHomePage ? "text-slate-700" : "text-white/90"
                )}
              >
                Contact
              </button>

              {/* Desktop: Agency Status Badge - Clickable for schedule */}
              <button 
                onClick={() => setShowScheduleModal(true)}
                className={cn(
                  "flex items-center gap-2 px-3 py-1.5 rounded-full text-sm hover:scale-105 transition-all cursor-pointer",
                  agencyStatus.bg
                )}
              >
                <span className={cn("w-2 h-2 rounded-full", agencyStatus.dot)} />
                <span className={cn("font-medium", agencyStatus.color)}>
                  {agencyStatus.label}
                </span>
                <span className="text-slate-400 text-xs hidden xl:inline">
                  {agencyStatus.sublabel}
                </span>
              </button>

              {/* COPILOTE Button - Only when copilote section enabled */}
              {siteSettings?.copilote_section?.enabled && (
              <button
                onClick={() => setCopiloteMode(true)}
                className="flex items-center gap-2 px-4 py-2 rounded-full border transition-all cursor-pointer bg-gradient-to-r from-orange-500 to-red-600 border-transparent shadow-lg shadow-orange-500/30 hover:shadow-xl hover:scale-105"
              >
                <Flame className="w-4 h-4 text-white" />
                <span className="text-sm font-semibold text-white">
                  COPILOTE
                </span>
              </button>
              )}

              {/* Nos Outils - Replaces Copilote when hidden */}
              <Link
                to="/site/nos-outils"
                className={cn(
                  "flex items-center gap-2 px-4 py-2 rounded-full border transition-all cursor-pointer",
                  scrolled || !isHomePage
                    ? "bg-gradient-to-r from-cyan-500 to-blue-600 border-transparent shadow-lg shadow-cyan-500/20 hover:shadow-xl hover:scale-105 text-white"
                    : "bg-white/10 backdrop-blur-sm border-white/30 hover:bg-white/20 text-white"
                )}
                data-testid="header-nos-outils-btn"
              >
                <Sparkles className="w-4 h-4" />
                <span className="text-sm font-semibold">Nos Outils</span>
              </Link>
            </div>

            {/* CTA Buttons with Phone */}
            <div className="hidden lg:flex items-center gap-3">
              {/* Phone - Full on desktop */}
              <a 
                href="tel:0484492700" 
                className={cn(
                  "flex items-center gap-2 font-medium transition-colors",
                  scrolled || !isHomePage ? "text-slate-700 hover:text-cyan-600" : "text-white hover:text-cyan-300"
                )}
              >
                <Phone className="w-5 h-5" />
                <span className="hidden md:inline">04 84 49 27 00</span>
              </a>
              
              <Button 
                onClick={() => {
                  const url = themeSettings?.clientPortal?.url || '/login';
                  if (themeSettings?.clientPortal?.openInNewTab) {
                    window.open(url, '_blank');
                  } else {
                    window.location.href = url;
                  }
                }}
                className={cn(
                  "rounded-full px-6",
                  copiloteMode 
                    ? `bg-gradient-to-r ${copiloteGradient}` 
                    : "bg-cyan-500 hover:bg-cyan-600"
                )}
              >
                {themeSettings?.clientPortal?.label || 'Espace Client'}
              </Button>
            </div>

            {/* Mobile: Account + Phone + Menu Button - Larger icons */}
            <div className="flex lg:hidden items-center gap-0.5">
              {/* Account icon - Opens Espace Client */}
              <button 
                onClick={() => {
                  const url = themeSettings?.clientPortal?.url || '/login';
                  if (themeSettings?.clientPortal?.openInNewTab) {
                    window.open(url, '_blank');
                  } else {
                    window.location.href = url;
                  }
                }}
                className={cn(
                  "p-2.5 rounded-full transition-all",
                  scrolled || mobileMenuOpen 
                    ? "text-slate-700 hover:bg-slate-100" 
                    : "text-white hover:bg-white/20"
                )}
              >
                <Users className="w-7 h-7" />
              </button>
              
              {/* Phone icon */}
              <a 
                href="tel:0484492700" 
                className={cn(
                  "p-2.5 rounded-full transition-all",
                  scrolled || mobileMenuOpen 
                    ? "text-cyan-600 hover:bg-cyan-50" 
                    : "text-white hover:bg-white/20"
                )}
              >
                <Phone className="w-7 h-7" />
              </a>
              
              {/* Menu Button with animation */}
              <button 
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className={cn(
                  "p-2.5 z-50 rounded-full transition-all",
                  scrolled || mobileMenuOpen 
                    ? "text-slate-800 hover:bg-slate-100" 
                    : "text-white hover:bg-white/20"
                )}
              >
                <div className="relative w-7 h-7">
                  <X className={cn(
                    "w-7 h-7 absolute inset-0 transition-all duration-300",
                    mobileMenuOpen ? "opacity-100 rotate-0" : "opacity-0 rotate-90"
                  )} />
                  <Menu className={cn(
                    "w-7 h-7 absolute inset-0 transition-all duration-300",
                    mobileMenuOpen ? "opacity-0 -rotate-90" : "opacity-100 rotate-0"
                  )} />
                </div>
              </button>
            </div>
          </div>
        </div>
      </nav>

      {/* Mobile Menu - OUTSIDE nav for proper z-index stacking */}
      <div className={cn(
        "lg:hidden fixed top-16 left-0 right-0 bottom-0 bg-white z-[100] transition-all duration-300 ease-out overflow-y-auto",
        mobileMenuOpen 
          ? "opacity-100 translate-y-0" 
          : "opacity-0 -translate-y-full pointer-events-none"
      )}>
        <div className="p-4 pb-8 space-y-2">
            {/* Quick Actions - Equal height buttons with rounded corners */}
            <div className="flex gap-3 mb-4">
              <button 
                onClick={() => { setMobileMenuOpen(false); downloadBrochure(); }} 
                className="flex-1 flex items-center justify-center gap-2 h-12 bg-cyan-500 hover:bg-cyan-600 text-white rounded-full font-medium transition-colors"
              >
                <Download className="w-5 h-5" /> Brochure
              </button>
              <button 
                onClick={() => { setMobileMenuOpen(false); handleQuoteAction(); }} 
                className="flex-1 flex items-center justify-center gap-2 h-12 bg-slate-800 hover:bg-slate-900 text-white rounded-full font-medium transition-colors"
              >
                Devis gratuit
              </button>
            </div>

            {/* Agency Status in menu */}
            <button
              onClick={() => { setMobileMenuOpen(false); setShowScheduleModal(true); }}
              className={cn(
                "w-full flex items-center justify-center gap-2 py-2.5 rounded-full mb-3",
                agencyStatus.bg
              )}
            >
              <Clock className={cn("w-4 h-4", agencyStatus.color)} />
              <span className={cn("w-2 h-2 rounded-full", agencyStatus.dot)} />
              <span className={cn("font-medium text-sm", agencyStatus.color)}>
                {agencyStatus.label}
              </span>
              <span className="text-slate-400 text-xs">
                • {agencyStatus.sublabel}
              </span>
            </button>

              {/* Navigation Links */}
              <Link to="/site" className="flex items-center gap-3 py-2.5 px-3 rounded-lg hover:bg-slate-50" onClick={() => setMobileMenuOpen(false)}>
                <div className="w-8 h-8 rounded-lg bg-slate-100 flex items-center justify-center">
                  <Building className="w-4 h-4 text-slate-600" />
                </div>
                <span className="font-medium">Accueil</span>
              </Link>

              {/* Services - Compact Grid */}
              <div className="py-2">
                <p className="text-xs text-slate-400 uppercase tracking-wider mb-2 px-3">Services</p>
                <div className="grid grid-cols-2 gap-2">
                  {activeServices.map(service => (
                    <Link
                      key={service.id}
                      to={`/site/${service.id}`}
                      className="flex items-center gap-2 p-2.5 rounded-lg hover:bg-slate-50 border border-slate-100"
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      <div className={cn("w-8 h-8 rounded-lg bg-gradient-to-br flex items-center justify-center flex-shrink-0", service.color)}>
                        <service.icon className="w-4 h-4 text-white" />
                      </div>
                      <span className="text-sm font-medium truncate">{service.shortTitle}</span>
                    </Link>
                  ))}
                </div>
              </div>

              {/* Other Pages */}
              <div className="py-2 border-t">
                <p className="text-xs text-slate-400 uppercase tracking-wider mb-2 px-3">Explorer</p>
                <div className="space-y-1">
                  <Link to="/site/realisations" onClick={() => setMobileMenuOpen(false)} className="flex items-center gap-3 py-2.5 px-3 rounded-lg hover:bg-slate-50">
                    <Image className="w-5 h-5 text-slate-500" />
                    <span>Réalisations</span>
                  </Link>
                  <Link to="/site/equipe" onClick={() => setMobileMenuOpen(false)} className="flex items-center gap-3 py-2.5 px-3 rounded-lg hover:bg-slate-50">
                    <Users className="w-5 h-5 text-slate-500" />
                    <span>L'équipe</span>
                  </Link>
                  <button onClick={() => { setMobileMenuOpen(false); scrollToSection('methode'); }} className="w-full flex items-center gap-3 py-2.5 px-3 rounded-lg hover:bg-slate-50 text-left">
                    <Lightbulb className="w-5 h-5 text-slate-500" />
                    <span>Notre méthode</span>
                  </button>
                  <Link to="/site/blog" onClick={() => setMobileMenuOpen(false)} className="flex items-center gap-3 py-2.5 px-3 rounded-lg hover:bg-slate-50">
                    <FileText className="w-5 h-5 text-slate-500" />
                    <span>Blog</span>
                  </Link>
                  <button onClick={() => { setMobileMenuOpen(false); scrollToSection('contact'); }} className="w-full flex items-center gap-3 py-2.5 px-3 rounded-lg hover:bg-slate-50 text-left">
                    <Mail className="w-5 h-5 text-slate-500" />
                    <span>Contact</span>
                  </button>
                </div>
              </div>

              {/* COPILOTE Mode - Only when copilote section enabled */}
              {siteSettings?.copilote_section?.enabled && (
              <div className="p-4 rounded-xl mt-2 bg-gradient-to-r from-orange-500/20 to-red-600/20 border border-orange-500/30">
                <div className="flex items-center gap-2 mb-2">
                  <Flame className="w-5 h-5 text-orange-400" />
                  <span className="font-bold text-orange-400">COPILOTE MSC</span>
                </div>
                <p className="text-sm mb-3 text-slate-600">Accompagnement stratégique sur 12 mois</p>
                <Button 
                  onClick={() => { setMobileMenuOpen(false); setCopiloteMode(true); }} 
                  className="w-full bg-gradient-to-r from-orange-500 to-red-600 text-white hover:from-orange-600 hover:to-red-700 rounded-lg"
                  size="sm"
                >
                  <Crosshair className="w-4 h-4 mr-2" />
                  Candidater au programme
                </Button>
              </div>
              )}

              {/* Nos Outils - Mobile */}
              <Link
                to="/site/nos-outils"
                onClick={() => setMobileMenuOpen(false)}
                className="flex items-center gap-3 p-4 rounded-xl mt-2 bg-gradient-to-r from-cyan-500/10 to-blue-600/10 border border-cyan-500/20"
              >
                <Sparkles className="w-5 h-5 text-cyan-500" />
                <div>
                  <span className="font-bold text-cyan-600">Nos Outils</span>
                  <p className="text-xs text-slate-500">Applications développées par MSC</p>
                </div>
              </Link>

              {/* Espace Client - Made more prominent */}
              <Button 
                onClick={() => { 
                  setMobileMenuOpen(false); 
                  const url = themeSettings?.clientPortal?.url || '/login';
                  if (themeSettings?.clientPortal?.openInNewTab) {
                    window.open(url, '_blank');
                  } else {
                    window.location.href = url;
                  }
                }} 
                variant="outline" 
                className="w-full rounded-xl mt-4 py-3 border-slate-300 font-semibold"
              >
                <Users className="w-4 h-4 mr-2" />
                {themeSettings?.clientPortal?.label || 'Espace Client'}
              </Button>
            </div>
          </div>

      {/* Mobile Menu Overlay - OUTSIDE nav for proper stacking */}
      {mobileMenuOpen && (
        <div 
          className="lg:hidden fixed inset-0 bg-black/20 z-40"
          onClick={() => setMobileMenuOpen(false)}
        />
      )}

      {/* ============================================ */}
      {/* ============================================ */}
      {/* FLOATING SERVICE MENU - Left Side */}
      {/* ============================================ */}
      <div className="fixed bottom-20 sm:bottom-6 left-4 z-40">
        {/* Service menu toggle button */}
        <div className="relative">
          <button
            onClick={() => setShowServiceMenu(!showServiceMenu)}
            className={cn(
              "w-12 h-12 rounded-full shadow-lg flex items-center justify-center transition-all",
              showServiceMenu 
                ? "bg-slate-900 text-white" 
                : "bg-white text-slate-700 border border-slate-200 hover:scale-110"
            )}
            title="Nos univers de services"
          >
            {showServiceMenu ? <X className="w-5 h-5" /> : <Layers className="w-5 h-5" />}
          </button>
          
          {/* Service menu dropdown */}
          {showServiceMenu && (
            <div className="absolute bottom-full mb-3 left-0 bg-white rounded-2xl shadow-2xl border border-slate-200 overflow-hidden min-w-[200px] animate-in slide-in-from-bottom-2 duration-200">
              <div className="p-2">
                <p className="px-3 py-2 text-xs font-semibold text-slate-400 uppercase tracking-wider">Nos univers</p>
                {SERVICES.map((service) => {
                  const ServiceIcon = service.icon;
                  return (
                    <Link
                      key={service.id}
                      to={`/site/${service.id}`}
                      onClick={() => setShowServiceMenu(false)}
                      className={cn(
                        "flex items-center gap-3 px-3 py-2.5 rounded-xl transition-all",
                        slug === service.id 
                          ? `bg-gradient-to-r ${service.color} text-white` 
                          : "hover:bg-slate-100"
                      )}
                    >
                      <div className={cn(
                        "w-8 h-8 rounded-lg flex items-center justify-center",
                        slug === service.id 
                          ? "bg-white/20" 
                          : `bg-gradient-to-r ${service.color}`
                      )}>
                        <ServiceIcon className={cn("w-4 h-4", slug === service.id ? "text-white" : "text-white")} />
                      </div>
                      <span className={cn(
                        "font-medium text-sm",
                        slug === service.id ? "text-white" : "text-slate-700"
                      )}>
                        {service.shortTitle}
                      </span>
                    </Link>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* ============================================ */}
      {/* FLOATING SCROLL TO TOP BUTTON */}
      {/* ============================================ */}
      <div className="fixed bottom-20 sm:bottom-6 right-4 sm:right-6 z-40 flex flex-col gap-2 sm:gap-3">
        {showScrollTop && (
          <button
            onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
            className="w-10 h-10 sm:w-12 sm:h-12 bg-slate-800 hover:bg-slate-700 text-white rounded-full shadow-lg flex items-center justify-center transition-all"
          >
            <ArrowUp className="w-4 h-4 sm:w-5 sm:h-5" />
          </button>
        )}
      </div>

      {/* ============================================ */}
      {/* MAIN CONTENT */}
      {/* ============================================ */}
      {/* ============================================ */}
      {/* COPILOTE OVERLAY - Full Screen with Blur Background */}
      {/* ============================================ */}
      {siteSettings?.copilote_section?.enabled && copiloteMode && (
        <div className="fixed inset-0 z-[60] overflow-hidden">
          {/* Blurred backdrop */}
          <div 
            className="absolute inset-0 bg-slate-950/80 backdrop-blur-xl"
            onClick={() => setCopiloteMode(false)}
          />
          
          {/* Animated background effects */}
          <div className="absolute inset-0 overflow-hidden pointer-events-none">
            <div className="absolute top-20 left-10 w-96 h-96 bg-orange-600/20 rounded-full blur-3xl animate-pulse" />
            <div className="absolute bottom-20 right-10 w-80 h-80 bg-red-600/15 rounded-full blur-3xl animate-pulse delay-1000" />
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-violet-600/10 rounded-full blur-3xl" />
          </div>
          
          {/* Close button */}
          <button 
            onClick={() => setCopiloteMode(false)}
            className="absolute top-6 right-6 z-20 p-3 rounded-full bg-white/10 hover:bg-white/20 transition-colors"
          >
            <X className="w-6 h-6 text-white" />
          </button>
          
          {/* Form container - centered */}
          <div className="relative z-10 h-full overflow-y-auto py-8 px-4">
            <div className="max-w-2xl mx-auto">
              {/* Header */}
              <div className="text-center mb-8">
                <div className="inline-flex items-center gap-3 px-6 py-3 rounded-full bg-red-500/20 backdrop-blur-sm border border-red-500/30 mb-6">
                  <div className="w-3 h-3 rounded-full bg-red-500 animate-pulse" />
                  <span className="text-red-400 font-bold tracking-wider text-sm uppercase">Programme exclusif</span>
                  <Flame className="w-5 h-5 text-orange-500" />
                </div>
                <h1 className="text-4xl sm:text-5xl font-black text-white mb-4 leading-tight">
                  <span className="text-transparent bg-clip-text bg-gradient-to-r from-orange-500 via-red-500 to-orange-600">
                    COPILOTE
                  </span>
                  {" "}MSC
                </h1>
                <p className="text-lg text-white/70 max-w-xl mx-auto">
                  Accompagnement stratégique sur <span className="text-orange-400 font-semibold">12 mois</span> pour co-piloter votre stratégie marketing
                </p>
              </div>
              
              {/* Candidature Form Card */}
              <div className="bg-slate-900/80 backdrop-blur-sm rounded-2xl border border-orange-500/30 p-6 sm:p-8">
                <h2 className="text-xl font-bold text-white mb-6 flex items-center gap-2">
                  <Target className="w-5 h-5 text-orange-500" />
                  Demande d'éligibilité
                </h2>
                
                {/* Step Indicator */}
                <div className="flex items-center justify-center gap-2 py-4 mb-6">
                  {[
                    { num: 1, title: 'Entreprise', icon: Building },
                    { num: 2, title: 'Communication', icon: Megaphone },
                    { num: 3, title: 'Contact', icon: Target }
                  ].map((step, idx) => (
                    <React.Fragment key={idx}>
                      <button
                        onClick={() => idx < copiloteStep && setCopiloteStep(idx)}
                        className={cn(
                          "flex items-center gap-2 px-3 py-2 rounded-full text-sm transition-all",
                          idx === copiloteStep
                            ? "bg-gradient-to-r from-orange-500 to-red-600 text-white"
                            : idx < copiloteStep
                              ? "bg-cyan-500/20 text-cyan-400 cursor-pointer hover:bg-cyan-500/30"
                              : "bg-slate-800 text-slate-500"
                        )}
                      >
                        {idx < copiloteStep ? (
                          <CheckCircle className="w-4 h-4" />
                        ) : (
                          <step.icon className="w-4 h-4" />
                        )}
                        <span className="hidden sm:inline">{step.title}</span>
                        <span className="sm:hidden">{step.num}</span>
                      </button>
                      {idx < 2 && <ChevronRight className="w-4 h-4 text-slate-600" />}
                    </React.Fragment>
                  ))}
                </div>
                
                {/* Step 1: Company Info */}
                {copiloteStep === 0 && (
                  <div className="space-y-5">
                    <div className="grid sm:grid-cols-2 gap-4">
                      <div>
                        <Label className="text-slate-300">Nom de l'entreprise *</Label>
                        <Input 
                          value={copiloteForm.companyName} 
                          onChange={(e) => setCopiloteForm({...copiloteForm, companyName: e.target.value})} 
                          className="bg-slate-800 border-slate-700 text-white focus:border-orange-500" 
                          placeholder="Votre entreprise"
                        />
                      </div>
                      <div>
                        <Label className="text-slate-300">Secteur d'activité *</Label>
                        <select 
                          className="w-full h-10 border rounded-md px-3 py-2 bg-slate-800 border-slate-700 text-white focus:border-orange-500" 
                          value={copiloteForm.sector} 
                          onChange={(e) => setCopiloteForm({...copiloteForm, sector: e.target.value})}
                        >
                          <option value="">Sélectionner...</option>
                          <option value="commerce">Commerce / Retail</option>
                          <option value="services">Services aux entreprises</option>
                          <option value="artisanat">Artisanat</option>
                          <option value="restauration">Restauration / Hôtellerie</option>
                          <option value="sante">Santé / Bien-être</option>
                          <option value="immobilier">Immobilier</option>
                          <option value="tech">Tech / Digital</option>
                          <option value="industrie">Industrie</option>
                          <option value="autre">Autre</option>
                        </select>
                      </div>
                    </div>
                    
                    <div>
                      <Label className="text-slate-300 mb-2 block">Type de clientèle *</Label>
                      <div className="grid grid-cols-3 gap-2">
                        {[
                          { id: 'b2c', label: 'B2C', desc: 'Particuliers' },
                          { id: 'b2b', label: 'B2B', desc: 'Entreprises' },
                          { id: 'both', label: 'Mixte', desc: 'Les deux' }
                        ].map((opt) => (
                          <button
                            key={opt.id}
                            type="button"
                            onClick={() => setCopiloteForm({...copiloteForm, clientType: opt.id})}
                            className={cn(
                              "p-3 rounded-xl border text-center transition-all",
                              copiloteForm.clientType === opt.id
                                ? "bg-orange-500/20 border-orange-500 text-orange-400"
                                : "bg-slate-800 border-slate-700 text-slate-400 hover:border-slate-600"
                            )}
                          >
                            <div className="font-semibold">{opt.label}</div>
                            <div className="text-xs opacity-70">{opt.desc}</div>
                          </button>
                        ))}
                      </div>
                    </div>
                    
                    <div className="flex justify-end pt-4">
                      <Button 
                        onClick={() => setCopiloteStep(1)} 
                        className="bg-gradient-to-r from-orange-500 to-red-600"
                        disabled={!copiloteForm.companyName || !copiloteForm.sector || !copiloteForm.clientType}
                      >
                        Continuer <ChevronRight className="w-4 h-4 ml-2" />
                      </Button>
                    </div>
                  </div>
                )}
                
                {/* Step 2: Communication */}
                {copiloteStep === 1 && (
                  <div className="space-y-5">
                    <div>
                      <Label className="text-slate-300 mb-2 block">Canaux de communication actuels</Label>
                      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                        {[
                          { id: 'website', label: 'Site web' },
                          { id: 'social', label: 'Réseaux sociaux' },
                          { id: 'google', label: 'Google Ads' },
                          { id: 'email', label: 'Email marketing' },
                          { id: 'print', label: 'Print / Flyers' },
                          { id: 'none', label: 'Aucun' }
                        ].map((channel) => (
                          <button
                            key={channel.id}
                            type="button"
                            onClick={() => {
                              const newChannels = copiloteForm.currentChannels.includes(channel.id)
                                ? copiloteForm.currentChannels.filter(c => c !== channel.id)
                                : [...copiloteForm.currentChannels, channel.id];
                              setCopiloteForm({...copiloteForm, currentChannels: newChannels});
                            }}
                            className={cn(
                              "p-2 rounded-lg border text-sm transition-all",
                              copiloteForm.currentChannels.includes(channel.id)
                                ? "bg-orange-500/20 border-orange-500 text-orange-400"
                                : "bg-slate-800 border-slate-700 text-slate-400 hover:border-slate-600"
                            )}
                          >
                            {channel.label}
                          </button>
                        ))}
                      </div>
                    </div>
                    
                    <div>
                      <Label className="text-slate-300">Qui gère actuellement votre communication ? *</Label>
                      <Input 
                        value={copiloteForm.whoManages} 
                        onChange={(e) => setCopiloteForm({...copiloteForm, whoManages: e.target.value})}
                        className="bg-slate-800 border-slate-700 text-white focus:border-orange-500" 
                        placeholder="En interne, freelance, agence..."
                      />
                    </div>
                    
                    <div className="flex justify-between pt-4">
                      <Button 
                        onClick={() => setCopiloteStep(0)} 
                        variant="outline"
                        className="border-slate-700 text-slate-300"
                      >
                        <ChevronLeft className="w-4 h-4 mr-2" /> Retour
                      </Button>
                      <Button 
                        onClick={() => setCopiloteStep(2)} 
                        className="bg-gradient-to-r from-orange-500 to-red-600"
                        disabled={!copiloteForm.whoManages}
                      >
                        Continuer <ChevronRight className="w-4 h-4 ml-2" />
                      </Button>
                    </div>
                  </div>
                )}
                
                {/* Step 3: Contact & Objectives */}
                {copiloteStep === 2 && (
                  <div className="space-y-5">
                    <div className="grid sm:grid-cols-2 gap-4">
                      <div>
                        <Label className="text-slate-300">Votre nom *</Label>
                        <Input 
                          value={copiloteForm.contactName} 
                          onChange={(e) => setCopiloteForm({...copiloteForm, contactName: e.target.value})} 
                          className="bg-slate-800 border-slate-700 text-white focus:border-orange-500" 
                          placeholder="Prénom Nom"
                        />
                      </div>
                      <div>
                        <Label className="text-slate-300">Email *</Label>
                        <Input 
                          type="email"
                          value={copiloteForm.email} 
                          onChange={(e) => setCopiloteForm({...copiloteForm, email: e.target.value})} 
                          className="bg-slate-800 border-slate-700 text-white focus:border-orange-500" 
                          placeholder="votre@email.com"
                        />
                      </div>
                    </div>
                    
                    <div>
                      <Label className="text-slate-300">Téléphone *</Label>
                      <Input 
                        value={copiloteForm.phone} 
                        onChange={(e) => setCopiloteForm({...copiloteForm, phone: e.target.value})} 
                        className="bg-slate-800 border-slate-700 text-white focus:border-orange-500" 
                        placeholder="06 XX XX XX XX"
                      />
                    </div>
                    
                    <div>
                      <Label className="text-slate-300 mb-2 block">Vos objectifs principaux</Label>
                      <div className="grid grid-cols-2 gap-2">
                        {[
                          { id: 'visibility', label: 'Plus de visibilité' },
                          { id: 'leads', label: 'Générer des leads' },
                          { id: 'sales', label: 'Augmenter les ventes' },
                          { id: 'brand', label: 'Développer ma marque' }
                        ].map((obj) => (
                          <button
                            key={obj.id}
                            type="button"
                            onClick={() => {
                              const newObjs = copiloteForm.objectives.includes(obj.id)
                                ? copiloteForm.objectives.filter(o => o !== obj.id)
                                : [...copiloteForm.objectives, obj.id];
                              setCopiloteForm({...copiloteForm, objectives: newObjs});
                            }}
                            className={cn(
                              "p-2 rounded-lg border text-sm transition-all",
                              copiloteForm.objectives.includes(obj.id)
                                ? "bg-orange-500/20 border-orange-500 text-orange-400"
                                : "bg-slate-800 border-slate-700 text-slate-400 hover:border-slate-600"
                            )}
                          >
                            {obj.label}
                          </button>
                        ))}
                      </div>
                    </div>
                    
                    <div className="flex justify-between pt-4">
                      <Button 
                        onClick={() => setCopiloteStep(1)} 
                        variant="outline"
                        className="border-slate-700 text-slate-300"
                      >
                        <ChevronLeft className="w-4 h-4 mr-2" /> Retour
                      </Button>
                      <Button 
                        onClick={async () => {
                          try {
                            await api.post('/website/request', {
                              type: 'copilote',
                              name: copiloteForm.contactName,
                              email: copiloteForm.email,
                              phone: copiloteForm.phone,
                              company: copiloteForm.companyName,
                              sector: copiloteForm.sector,
                              clientType: copiloteForm.clientType,
                              currentChannels: copiloteForm.currentChannels,
                              whoManages: copiloteForm.whoManages,
                              objectives: copiloteForm.objectives,
                              source: 'copilote-overlay'
                            });
                            toast.success('✅ Demande COPILOTE envoyée ! Notre équipe évaluera votre éligibilité sous 48h.');
                            setCopiloteMode(false);
                            setCopiloteStep(0);
                            setCopiloteForm({
                              companyName: '', sector: '', clientType: '', avgBasket: '',
                              currentChannels: [], whoManages: '', hasCommercialTeam: null, availableTime: null,
                              contactName: '', email: '', phone: '', objectives: [], budget: 3000, expectations: ''
                            });
                          } catch (error) {
                            toast.error('Erreur lors de l\'envoi. Veuillez réessayer.');
                          }
                        }}
                        className="bg-gradient-to-r from-orange-500 to-red-600"
                        disabled={!copiloteForm.contactName || !copiloteForm.email || !copiloteForm.phone}
                      >
                        <Send className="w-4 h-4 mr-2" /> Envoyer ma candidature
                      </Button>
                    </div>
                  </div>
                )}
              </div>
              
              {/* Trust badges */}
              <div className="mt-6 text-center">
                <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/5 border border-white/10">
                  <Shield className="w-4 h-4 text-orange-400" />
                  <span className="text-white/50 text-sm">Solution sous réserve d'éligibilité • Réponse sous 48h</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      <main>
        
        {/* ============================================ */}
        {/* TEAM PAGE */}
        {/* ============================================ */}
        {isTeamPage && (
          <>
            <TeamPage noLayout={true} />
            <SiteFooter />
          </>
        )}
        
        {/* ============================================ */}
        {/* PORTFOLIO PAGE */}
        {/* ============================================ */}
        {isPortfolioPage && (
          <>
            <PortfolioPage noLayout={true} />
            <SiteFooter />
          </>
        )}
        
        {/* ============================================ */}
        {/* HOME PAGE - NORMAL MODE */}
        {/* ============================================ */}
        {isHomePage && (
          <>
            {/* Hero Section with Video */}
            <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
              {/* Video Background */}
              <div className="absolute inset-0">
                {(() => {
                  // Convert YouTube URL to embed format
                  const getVideoSource = () => {
                    const videoUrl = heroData?.video_url;
                    if (!videoUrl) {
                      return { type: 'mp4', url: 'https://assets.mixkit.co/videos/preview/mixkit-team-of-creatives-working-in-an-office-4831-large.mp4' };
                    }
                    
                    // Check if it's a YouTube URL
                    const youtubeMatch = videoUrl.match(/(?:youtube\.com\/(?:watch\?v=|embed\/)|youtu\.be\/)([a-zA-Z0-9_-]{11})/);
                    if (youtubeMatch) {
                      return { type: 'youtube', url: `https://www.youtube.com/embed/${youtubeMatch[1]}?autoplay=1&mute=1&loop=1&playlist=${youtubeMatch[1]}&controls=0&showinfo=0&rel=0&modestbranding=1` };
                    }
                    
                    // Check if it's a Vimeo URL
                    const vimeoMatch = videoUrl.match(/vimeo\.com\/(\d+)/);
                    if (vimeoMatch) {
                      return { type: 'vimeo', url: `https://player.vimeo.com/video/${vimeoMatch[1]}?autoplay=1&muted=1&loop=1&background=1` };
                    }
                    
                    // Default to mp4
                    return { type: 'mp4', url: videoUrl };
                  };
                  
                  const videoSource = getVideoSource();
                  
                  if (videoSource.type === 'youtube' || videoSource.type === 'vimeo') {
                    return (
                      <iframe
                        src={videoSource.url}
                        className="w-full h-full object-cover scale-150"
                        style={{ pointerEvents: 'none' }}
                        allow="autoplay; fullscreen"
                        frameBorder="0"
                        title="Background Video"
                      />
                    );
                  }
                  
                  return (
                    <video
                      ref={videoRef}
                      autoPlay
                      muted
                      loop
                      playsInline
                      className="w-full h-full object-cover"
                      poster="https://images.unsplash.com/photo-1522071820081-009f0129c71c?w=1920"
                    >
                      <source src={videoSource.url} type="video/mp4" />
                    </video>
                  );
                })()}
                <div className="absolute inset-0 bg-gradient-to-br from-[#0f172a]/90 via-[#1e3a5f]/85 to-[#0f172a]/90" />
              </div>

              {/* Animated Shapes */}
              <div className="absolute inset-0 overflow-hidden pointer-events-none">
                <div className="absolute top-20 left-10 w-72 h-72 rounded-full blur-3xl animate-pulse bg-cyan-500/20" />
                <div className="absolute bottom-20 right-20 w-96 h-96 rounded-full blur-3xl animate-pulse delay-1000 bg-orange-500/15" />
              </div>

              {/* Content */}
              <div className="relative z-10 max-w-5xl mx-auto px-4 text-center">
                <AnimatedSection delay={0}>
                  <Badge className="mb-6 bg-white/10 text-white border-white/20 px-4 py-2 text-sm">
                    <Sparkles className="w-4 h-4 mr-2" />
                    {siteContent?.hero?.badge || "Agence de communication depuis 2012"}
                  </Badge>
                </AnimatedSection>
                
                <AnimatedSection delay={100}>
                  <h1 className="text-4xl sm:text-5xl lg:text-7xl font-bold text-white mb-6 leading-tight">
                    {heroData?.title ? (
                      // Parse title with **bold** markers
                      heroData.title.split(/(\*\*[^*]+\*\*)/).map((part, i) => {
                        if (part.startsWith('**') && part.endsWith('**')) {
                          return (
                            <span key={i} className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-orange-400">
                              {part.slice(2, -2)}
                            </span>
                          );
                        }
                        return part;
                      })
                    ) : (
                      <>
                        Boostons la{' '}
                        <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-orange-400">
                          visibilité
                        </span>
                        <br />de votre entreprise !
                      </>
                    )}
                  </h1>
                </AnimatedSection>
                
                <AnimatedSection delay={200}>
                  <p className="text-xl text-white/80 mb-8 max-w-3xl mx-auto">
                    {heroData?.subtitle ? (
                      // Parse subtitle with **bold** markers
                      heroData.subtitle.split(/(\*\*[^*]+\*\*)/).map((part, i) => {
                        if (part.startsWith('**') && part.endsWith('**')) {
                          return (
                            <span key={i} className="text-cyan-400 font-semibold">
                              {part.slice(2, -2)}
                            </span>
                          );
                        }
                        return part;
                      })
                    ) : (
                      <>
                        Logos, visuels, vidéos corporate, sites web et applications mobiles.
                        Une équipe de <span className="text-cyan-400 font-semibold">passionnés</span> pour donner vie à vos projets.
                      </>
                    )}
                  </p>
                </AnimatedSection>

                <AnimatedSection delay={300}>
                  <div className="flex flex-col sm:flex-row gap-4 justify-center mb-12">
                    <Button 
                      size="lg" 
                      onClick={() => handleQuoteAction()}
                      className="bg-gradient-to-r from-cyan-500 to-cyan-600 hover:from-cyan-600 hover:to-cyan-700 text-white rounded-full px-8 text-lg group"
                    >
                      {heroData?.cta_text || 'Demander un devis'}
                      <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
                    </Button>
                    <Button 
                      size="lg" 
                      variant="outline"
                      onClick={() => scrollToSection('services')}
                      className="border-white/30 text-white hover:bg-white/10 rounded-full px-8 text-lg"
                    >
                      <Play className="mr-2 w-5 h-5" />
                      {heroData?.secondary_cta_text || 'Découvrir nos services'}
                    </Button>
                  </div>
                </AnimatedSection>

                <AnimatedSection delay={400}>
                  <div className="flex flex-wrap items-center justify-center gap-8">
                    <Link to="/site/equipe" className="flex items-center gap-2 hover:opacity-80 transition-opacity cursor-pointer group">
                      <div className="flex -space-x-2">
                        {(cmsTeam || TEAM).slice(0, 4).map((member, i) => (
                          <div key={i} className={cn("w-10 h-10 rounded-full bg-gradient-to-br border-2 border-white flex items-center justify-center text-white text-xs font-bold group-hover:scale-105 transition-transform overflow-hidden", member.color || "from-cyan-500 to-blue-500")}>
                            {member.photo_url ? (
                              <img 
                                src={getImageUrl(member.photo_url)} 
                                alt={member.name} 
                                className="w-full h-full object-cover" 
                              />
                            ) : (
                              member.name.charAt(0)
                            )}
                          </div>
                        ))}
                      </div>
                      <span className="text-white/80 text-sm group-hover:text-white transition-colors">Notre équipe</span>
                    </Link>
                    <a 
                      href="https://www.google.com/search?sca_esv=9ea2b7d2a43b4268&rlz=1C1VDKB_frFR1074FR1074&sxsrf=AE3TifOIPsPyn0EeXUTv9F0n0wwek0XCVA:1766745101437&si=AMgyJEtREmoPL4P1I5IDCfuA8gybfVI2d5Uj7QMwYCZHKDZ-Ezi2q-08H0ZKYymjQz9uk2XPcZf7_QuuuRDVO1Ij87DpGKPstnQCIrF4RGekMSa1PBZYmCE_XgHxkt27Gj_5_EqL2aKNBHqI7YUVp5ZG2fZ-G_OkRA%3D%3D&q=Agence+MSC+-+Mon+Service+Com+Avis"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-1 hover:opacity-80 transition-opacity cursor-pointer group"
                    >
                      {[...Array(5)].map((_, i) => (
                        <Star key={i} className="w-5 h-5 fill-yellow-400 text-yellow-400 group-hover:scale-110 transition-transform" />
                      ))}
                      <span className="text-white/80 text-sm ml-2 group-hover:text-white transition-colors">
                        {googleReviews.config?.rating || '4.9'}/5 sur {googleReviews.config?.total_reviews ? `+${googleReviews.config.total_reviews}` : googleReviews.reviews?.length ? `+${googleReviews.reviews.length}` : ''} avis Google
                      </span>
                    </a>
                  </div>
                </AnimatedSection>
              </div>

              {/* Scroll Indicator */}
              <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce">
                <ChevronDown className="w-8 h-8 text-white/50" />
              </div>
            </section>

            {/* Services Grid */}
            <section id="services" className={cn("py-24", copiloteMode ? "bg-slate-900" : "bg-slate-50")}>
              <div className="max-w-7xl mx-auto px-4">
                <AnimatedSection className="text-center mb-16">
                  <Badge className={cn("mb-4", copiloteMode ? `bg-gradient-to-r ${copiloteGradient} text-white` : "bg-cyan-100 text-cyan-700")}>
                    Nos spécialités
                  </Badge>
                  <h2 className={cn("text-3xl sm:text-4xl font-bold mb-4", copiloteMode ? "text-white" : "text-slate-800")}>
                    {copiloteMode ? "Nos armes secrètes" : "La COM' est un OUTIL, pas une OPTION !"}
                  </h2>
                  <p className={cn("text-lg max-w-2xl mx-auto", copiloteMode ? "text-slate-400" : "text-slate-600")}>
                    Découvrez nos solutions sur mesure pour votre entreprise
                  </p>
                </AnimatedSection>

                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                  {activeServices.map((service, idx) => (
                    <AnimatedCard key={service.id} delay={idx * 100}>
                      <Link
                        to={`/site/${service.id}`}
                        className={cn(
                          "block p-8 rounded-2xl border transition-all h-full group",
                          copiloteMode 
                            ? "bg-slate-800/50 border-slate-700 hover:border-violet-500 hover:bg-slate-800" 
                            : "bg-white border-slate-100 hover:border-cyan-200 hover:shadow-xl"
                        )}
                      >
                        <div className={cn(
                          "w-16 h-16 rounded-2xl bg-gradient-to-br flex items-center justify-center mb-6 group-hover:scale-110 transition-transform shadow-lg",
                          service.color
                        )}>
                          <service.icon className="w-8 h-8 text-white" />
                        </div>
                        <h3 className={cn("text-xl font-bold mb-3", copiloteMode ? "text-white" : "text-slate-800")}>
                          {service.title}
                        </h3>
                        <p className={cn("mb-4", copiloteMode ? "text-slate-400" : "text-slate-600")}>
                          {service.description}
                        </p>
                        <span className={cn(
                          "inline-flex items-center gap-2 font-medium group-hover:gap-3 transition-all",
                          copiloteMode ? `text-transparent bg-clip-text bg-gradient-to-r ${copiloteGradient}` : "text-cyan-600"
                        )}>
                          Découvrir <ArrowRight className="w-4 h-4" />
                        </span>
                      </Link>
                    </AnimatedCard>
                  ))}
                </div>
              </div>
            </section>

            {/* CLIENTS TICKER - Scrolling */}
            <section className={cn("py-16 border-b overflow-hidden", copiloteMode ? "bg-slate-950 border-slate-800" : "bg-white border-slate-200")}>
              <div className="text-center mb-10">
                <p className={cn("text-xs font-semibold uppercase tracking-wider mb-2", copiloteMode ? "text-slate-500" : "text-slate-400")}>
                  Ils nous font confiance
                </p>
                <h3 className={cn("text-lg font-medium", copiloteMode ? "text-slate-300" : "text-slate-600")}>
                  {keyStats?.find(s => s.id === 'clients')?.value 
                    ? `+${keyStats.find(s => s.id === 'clients').value} entreprises accompagnées depuis 2012`
                    : 'Des entreprises accompagnées depuis 2012'}
                </h3>
              </div>
              <div className="marquee-container">
                <LogoTicker clients={cmsPartners ? cmsPartners.map(p => ({ 
                  name: p.name, 
                  logo: getImageUrl(p.logo_url), 
                  url: p.website_url || '#' 
                })) : CLIENTS} speed="normal" />
              </div>
              
              {/* Google Reviews Carousel - Linked to stars section */}
              {googleReviews.reviews?.length > 0 && (
                <div className="mt-12">
                  <a 
                    href="https://www.google.com/search?sca_esv=9ea2b7d2a43b4268&rlz=1C1VDKB_frFR1074FR1074&sxsrf=AE3TifOIPsPyn0EeXUTv9F0n0wwek0XCVA:1766745101437&si=AMgyJEtREmoPL4P1I5IDCfuA8gybfVI2d5Uj7QMwYCZHKDZ-Ezi2q-08H0ZKYymjQz9uk2XPcZf7_QuuuRDVO1Ij87DpGKPstnQCIrF4RGekMSa1PBZYmCE_XgHxkt27Gj_5_EqL2aKNBHqI7YUVp5ZG2fZ-G_OkRA%3D%3D&q=Agence+MSC+-+Mon+Service+Com+Avis"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center justify-center gap-2 mb-6 group hover:opacity-80 transition-opacity"
                  >
                    <div className="flex gap-0.5">
                      {[...Array(5)].map((_, i) => (
                        <Star key={i} className="w-5 h-5 fill-yellow-400 text-yellow-400 group-hover:scale-110 transition-transform" />
                      ))}
                    </div>
                    <span className="text-slate-600 font-medium ml-2">
                      {googleReviews.config?.rating || '4.9'}/5 sur {googleReviews.config?.total_reviews ? `+${googleReviews.config.total_reviews}` : `+${googleReviews.reviews.length}`} avis Google
                    </span>
                    <ExternalLink className="w-4 h-4 text-slate-400 group-hover:text-cyan-500 transition-colors" />
                  </a>
                  <ReviewsCarousel reviews={googleReviews.reviews} speed="slow" />
                </div>
              )}
            </section>

            {/* PORTFOLIO / REALISATIONS */}
            <section id="realisations" className={cn("pt-32 pb-24", copiloteMode ? "bg-slate-900" : "bg-slate-50")}>
              <div className="max-w-7xl mx-auto px-4">
                <AnimatedSection className="text-center mb-16">
                  <Badge className={cn("mb-4", copiloteMode ? `bg-gradient-to-r ${copiloteGradient} text-white` : "bg-orange-100 text-orange-700")}>
                    Nos réalisations
                  </Badge>
                  <h2 className={cn("text-3xl sm:text-4xl font-bold mb-4", copiloteMode ? "text-white" : "text-slate-800")}>
                    {copiloteMode ? "Missions accomplies" : "Découvrez nos créations"}
                  </h2>
                  <p className={cn("text-lg max-w-2xl mx-auto", copiloteMode ? "text-slate-400" : "text-slate-600")}>
                    Un aperçu de notre savoir-faire à travers les projets de nos clients
                  </p>
                </AnimatedSection>

                {/* Portfolio Filter */}
                <div className="flex flex-wrap justify-center gap-3 mb-12">
                  {['Tous', 'Graphique', 'Web', 'Vidéo', 'Audio'].map((filter) => (
                    <button
                      key={filter}
                      onClick={() => setPortfolioFilter(filter)}
                      className={cn(
                        "px-5 py-2 rounded-full font-medium transition-all",
                        filter === portfolioFilter
                          ? copiloteMode 
                            ? `bg-gradient-to-r ${copiloteGradient} text-white` 
                            : "bg-cyan-500 text-white"
                          : copiloteMode 
                            ? "bg-slate-800 text-slate-300 hover:bg-slate-700" 
                            : "bg-white text-slate-600 hover:bg-slate-100 border border-slate-200"
                      )}
                    >
                      {filter}
                    </button>
                  ))}
                </div>

                {/* Portfolio Grid */}
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-8">
                  {(cmsPortfolio || PORTFOLIO)
                    .filter(project => {
                      if (portfolioFilter === 'Tous') return true;
                      if (portfolioFilter === 'Graphique') return project.category === 'graphique';
                      if (portfolioFilter === 'Web') return project.category === 'web';
                      if (portfolioFilter === 'Vidéo') return project.category === 'video';
                      if (portfolioFilter === 'Audio') return project.category === 'audio';
                      return true;
                    })
                    .slice(0, 9)
                    .map((project, idx) => (
                    <AnimatedCard key={project.id} delay={idx * 100}>
                      <Link 
                        to={`/site/realisations/${project.slug || project.id}`}
                        className={cn(
                          "group block rounded-2xl overflow-hidden border transition-all",
                          copiloteMode 
                            ? "bg-slate-800 border-slate-700 hover:border-violet-500" 
                            : "bg-white border-slate-200 hover:shadow-xl"
                        )}
                      >
                        {/* Image/Video with hover animations */}
                        <div className="relative aspect-[4/3] overflow-hidden">
                          {/* YouTube projects: show play button with YouTube branding */}
                          {project.youtubeUrl ? (
                            <>
                              <img 
                                src={getImageUrl(project.image)} 
                                alt={project.title}
                                className="w-full h-full object-cover"
                              />
                              {/* YouTube Play overlay */}
                              <div className="absolute inset-0 bg-black/30 flex items-center justify-center group-hover:bg-black/40 transition-colors">
                                <div className="w-16 h-16 rounded-2xl bg-red-600 flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform">
                                  <Play className="w-7 h-7 text-white fill-white ml-1" />
                                </div>
                              </div>
                              {/* YouTube badge */}
                              <div className="absolute top-3 right-3 px-3 py-1 rounded-full bg-red-600 text-white text-xs font-medium flex items-center gap-1">
                                <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
                                  <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
                                </svg>
                                YouTube
                              </div>
                            </>
                          ) : project.audioUrl ? (
                            /* Audio projects: waveform visualization */
                            <>
                              <div className="w-full h-full bg-gradient-to-br from-slate-900 to-slate-800 flex flex-col items-center justify-center p-6">
                                <img 
                                  src={getImageUrl(project.image)} 
                                  alt={project.title}
                                  className="w-20 h-20 rounded-xl object-cover mb-4 shadow-lg"
                                />
                                {/* Animated waveform bars */}
                                <div className="flex items-end justify-center gap-1 h-12">
                                  {[...Array(12)].map((_, i) => (
                                    <div 
                                      key={i}
                                      className="w-2 bg-gradient-to-t from-cyan-500 to-blue-500 rounded-full animate-pulse"
                                      style={{ 
                                        height: `${Math.random() * 100}%`,
                                        animationDelay: `${i * 0.1}s`,
                                        animationDuration: '0.8s'
                                      }}
                                    />
                                  ))}
                                </div>
                              </div>
                              {/* Audio badge */}
                              <div className="absolute top-3 right-3 w-10 h-10 rounded-full bg-gradient-to-r from-cyan-500 to-blue-500 flex items-center justify-center">
                                <Volume2 className="w-5 h-5 text-white" />
                              </div>
                            </>
                          ) : project.videoUrl ? (
                            <>
                              <img 
                                src={getImageUrl(project.image)} 
                                alt={project.title}
                                className="w-full h-full object-cover transition-opacity duration-300 group-hover:opacity-0"
                              />
                              <video
                                src={project.videoUrl}
                                muted
                                loop
                                playsInline
                                className="absolute inset-0 w-full h-full object-cover opacity-0 group-hover:opacity-100 transition-opacity duration-300"
                                onMouseEnter={(e) => e.target.play()}
                                onMouseLeave={(e) => { e.target.pause(); e.target.currentTime = 0; }}
                              />
                              {/* Play icon indicator */}
                              <div className="absolute top-3 right-3 w-10 h-10 rounded-full bg-black/60 flex items-center justify-center group-hover:opacity-0 transition-opacity">
                                <Play className="w-5 h-5 text-white fill-white" />
                              </div>
                            </>
                          ) : project.category === 'web' ? (
                            /* Web projects: scroll animation on hover */
                            <>
                              <div className="w-full h-full overflow-hidden">
                                <img 
                                  src={getImageUrl(project.fullImage || project.image)} 
                                  alt={project.title}
                                  className="w-full object-cover object-top transition-all duration-[3s] ease-linear group-hover:object-bottom"
                                  style={{ height: '180%' }}
                                />
                              </div>
                              {/* Browser mockup indicator */}
                              <div className="absolute top-3 left-3 flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-black/60">
                                <div className="w-2.5 h-2.5 rounded-full bg-red-400" />
                                <div className="w-2.5 h-2.5 rounded-full bg-yellow-400" />
                                <div className="w-2.5 h-2.5 rounded-full bg-green-400" />
                              </div>
                            </>
                          ) : (
                            /* Default: zoom on hover */
                            <img 
                              src={getImageUrl(project.image)} 
                              alt={project.title}
                              className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                            />
                          )}
                          
                          <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
                          <div className="absolute bottom-4 left-4 right-4 opacity-0 group-hover:opacity-100 transition-opacity">
                            <Badge className={cn(
                              "mb-2",
                              project.category === 'graphique' ? "bg-pink-500" :
                              project.category === 'web' ? "bg-cyan-500" : 
                              project.category === 'video' ? "bg-purple-500" :
                              project.category === 'audio' ? "bg-gradient-to-r from-cyan-500 to-blue-500" : "bg-orange-500"
                            )}>
                              {project.category === 'graphique' ? 'Graphique' : 
                               project.category === 'web' ? 'Web' : 
                               project.category === 'video' ? 'Vidéo' :
                               project.category === 'audio' ? 'Audio' : 'Marketing'}
                            </Badge>
                          </div>
                        </div>
                        
                        {/* Content */}
                        <div className="p-6">
                          <p className={cn("text-sm font-medium mb-1", copiloteMode ? `text-transparent bg-clip-text bg-gradient-to-r ${copiloteGradient}` : "text-cyan-600")}>
                            {project.client}
                          </p>
                          <h3 className={cn("font-bold text-lg mb-2", copiloteMode ? "text-white" : "text-slate-800")}>
                            {project.title}
                          </h3>
                          <p className={cn("text-sm mb-4 line-clamp-2", copiloteMode ? "text-slate-400" : "text-slate-600")}>
                            {project.description}
                          </p>
                          {project.tags?.length > 0 && (
                            <div className="flex flex-wrap gap-2">
                              {project.tags.map((tag, tidx) => (
                                <span 
                                  key={tidx}
                                  className={cn(
                                    "px-2 py-1 rounded text-xs",
                                    copiloteMode ? "bg-slate-700 text-slate-300" : "bg-slate-100 text-slate-600"
                                  )}
                                >
                                  {tag}
                                </span>
                              ))}
                            </div>
                          )}
                        </div>
                      </Link>
                    </AnimatedCard>
                  ))}
                </div>

                {/* View More CTA */}
                <AnimatedSection className="text-center mt-12">
                  <Link to="/site/realisations">
                    <Button 
                      size="lg"
                      variant="outline"
                      className={cn(
                        "rounded-full px-8",
                        copiloteMode ? "border-slate-700 text-slate-300 hover:bg-slate-800" : ""
                      )}
                    >
                      Voir toutes nos réalisations
                      <ArrowRight className="w-5 h-5 ml-2" />
                    </Button>
                  </Link>
                </AnimatedSection>
              </div>
            </section>

            {/* TEAM SECTION - Enhanced */}
            <section id="equipe" className={cn("py-24", copiloteMode ? "bg-slate-950" : "bg-white")}>
              <div className="max-w-7xl mx-auto px-4">
                <AnimatedSection className="text-center mb-16">
                  <Badge className={cn("mb-4", copiloteMode ? `bg-gradient-to-r ${copiloteGradient} text-white` : "bg-purple-100 text-purple-700")}>
                    L'équipe
                  </Badge>
                  <h2 className={cn("text-3xl sm:text-4xl font-bold mb-4", copiloteMode ? "text-white" : "text-slate-800")}>
                    {copiloteMode ? "Les agents de votre succès" : "Des créatifs complémentaires"}
                  </h2>
                  <p className={cn("text-lg max-w-2xl mx-auto", copiloteMode ? "text-slate-400" : "text-slate-600")}>
                    Une équipe de passionnés à votre service
                  </p>
                </AnimatedSection>

                {/* Team Video Banner - Only show if configured in CMS */}
                {teamPageConfig?.show_on_homepage && teamPageConfig?.video_url && (
                  <AnimatedSection className="mb-16">
                    <div className={cn(
                      "relative rounded-3xl overflow-hidden cursor-pointer group",
                      copiloteMode ? "bg-slate-900" : "bg-slate-100"
                    )}
                    onClick={() => {
                      if (teamPageConfig?.fullscreen_on_click !== false) {
                        setShowTeamVideoModal(true);
                      }
                    }}
                    >
                      <div className="aspect-video relative overflow-hidden">
                        {/* Autoplay muted video or thumbnail */}
                        {teamPageConfig?.autoplay_muted && getYouTubeEmbedUrl(teamPageConfig.video_url) ? (
                          <iframe
                            src={`${getYouTubeEmbedUrl(teamPageConfig.video_url)}?autoplay=1&mute=1&loop=1&playlist=${getYouTubeEmbedUrl(teamPageConfig.video_url)?.split('/').pop()}&controls=0&showinfo=0&rel=0&modestbranding=1`}
                            className="w-full h-full object-cover scale-110 pointer-events-none"
                            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                            allowFullScreen
                            title="Team Video"
                          />
                        ) : (
                          <img 
                            src={teamPageConfig?.video_thumbnail 
                              ? getImageUrl(teamPageConfig.video_thumbnail)
                              : "https://images.unsplash.com/photo-1522071820081-009f0129c71c?w=1200"} 
                            alt="L'équipe MSC au travail"
                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                          />
                        )}
                        <div className={cn(
                          "absolute inset-0 pointer-events-none",
                          copiloteMode 
                            ? "bg-gradient-to-t from-slate-950 via-transparent to-transparent" 
                            : "bg-gradient-to-t from-slate-900/80 via-transparent to-transparent"
                        )} />
                        {/* Play button overlay */}
                        <div className="absolute inset-0 flex items-center justify-center">
                          <div className="w-20 h-20 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center group-hover:bg-white/30 transition-all group-hover:scale-110">
                            <Play className="w-8 h-8 text-white ml-1 group-hover:scale-110 transition-transform" />
                          </div>
                        </div>
                        <div className="absolute bottom-6 left-6 right-6">
                          <h3 className="text-2xl font-bold text-white mb-2">Découvrez notre équipe</h3>
                          <p className="text-white/80">Une vidéo de présentation de l'agence et de ses talents</p>
                        </div>
                      </div>
                    </div>
                  </AnimatedSection>
                )}

                {/* Team Grid */}
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-8">
                  {(cmsTeam || TEAM).map((member, idx) => (
                    <AnimatedCard key={member.id || idx} delay={idx * 100}>
                      <Link 
                        to={`/site/equipe?member=${member.id || member.name}`}
                        className={cn(
                          "block p-6 rounded-2xl border cursor-pointer transition-all group",
                          copiloteMode 
                            ? "bg-slate-900 border-slate-800 hover:border-violet-500" 
                            : "bg-white border-slate-200 hover:shadow-xl hover:border-transparent"
                        )}
                      >
                        {/* Avatar - ROND */}
                        <div className="flex items-start gap-4 mb-4">
                          <div className={cn(
                            "w-16 h-16 rounded-full bg-gradient-to-br flex items-center justify-center text-white text-2xl font-bold group-hover:scale-110 transition-transform shadow-lg overflow-hidden",
                            member.color || "from-cyan-500 to-blue-500"
                          )}>
                            {member.photo_url ? (
                              <img 
                                src={getImageUrl(member.photo_url)} 
                                alt={member.name} 
                                className="w-full h-full object-cover" 
                              />
                            ) : (
                              member.name.charAt(0)
                            )}
                          </div>
                          <div className="flex-1">
                            <h4 className={cn("font-bold text-lg", copiloteMode ? "text-white" : "text-slate-800")}>
                              {member.name}
                            </h4>
                            <p className={cn("text-sm", copiloteMode ? `text-transparent bg-clip-text bg-gradient-to-r ${copiloteGradient}` : "text-cyan-600")}>
                              {member.role}
                            </p>
                            {member.experience && (
                              <p className={cn("text-xs mt-1", copiloteMode ? "text-slate-500" : "text-slate-400")}>
                                {member.experience} d'expérience
                              </p>
                            )}
                          </div>
                        </div>

                        {/* Bio */}
                        {member.bio && (
                          <p className={cn("text-sm mb-4 line-clamp-2", copiloteMode ? "text-slate-400" : "text-slate-600")}>
                            {member.bio}
                          </p>
                        )}

                        {/* Skills */}
                        {member.skills && member.skills.length > 0 && (
                          <div className="flex flex-wrap gap-2">
                            {member.skills.map((skill, sidx) => (
                              <span 
                                key={sidx}
                                className={cn(
                                  "px-3 py-1 rounded-full text-xs font-medium",
                                  copiloteMode 
                                    ? "bg-slate-800 text-slate-300" 
                                    : "bg-slate-100 text-slate-600"
                                )}
                              >
                                {skill}
                              </span>
                            ))}
                          </div>
                        )}

                        {/* Hover effect */}
                        <div className={cn(
                          "mt-4 pt-4 border-t flex items-center justify-between",
                          copiloteMode ? "border-slate-800" : "border-slate-100"
                        )}>
                          <span className={cn("text-sm", copiloteMode ? "text-slate-500" : "text-slate-400")}>
                            Voir le parcours
                          </span>
                          <ArrowRight className={cn("w-4 h-4 group-hover:translate-x-1 transition-transform", copiloteMode ? "text-violet-400" : "text-cyan-500")} />
                        </div>
                      </Link>
                    </AnimatedCard>
                  ))}
                </div>
              </div>
            </section>

            {/* Process Section - Professional Animated Timeline */}
            <section id="methode" className={cn("py-24 relative overflow-hidden", copiloteMode ? "bg-slate-900" : "bg-gradient-to-br from-slate-50 via-white to-slate-50")}>
              {/* Background decoration */}
              <div className="absolute inset-0 overflow-hidden pointer-events-none">
                <div className={cn(
                  "absolute top-1/4 -right-20 w-80 h-80 rounded-full blur-3xl",
                  copiloteMode ? "bg-violet-500/10" : "bg-cyan-500/10"
                )} />
                <div className={cn(
                  "absolute bottom-1/4 -left-20 w-80 h-80 rounded-full blur-3xl",
                  copiloteMode ? "bg-blue-500/10" : "bg-orange-500/10"
                )} />
              </div>

              <div className="max-w-7xl mx-auto px-4 relative z-10">
                <AnimatedSection className="text-center mb-16">
                  <Badge className={cn("mb-4", copiloteMode ? `bg-gradient-to-r ${copiloteGradient} text-white` : "bg-orange-100 text-orange-700")}>
                    Notre méthode
                  </Badge>
                  <h2 className={cn("text-3xl sm:text-4xl font-bold mb-4", copiloteMode ? "text-white" : "text-slate-800")}>
                    {copiloteMode ? "Protocole de mission" : "Recentrez-vous sur votre métier !"}
                  </h2>
                  <p className={cn("text-lg max-w-2xl mx-auto", copiloteMode ? "text-slate-400" : "text-slate-600")}>
                    Un processus éprouvé pour garantir des résultats à la hauteur de vos ambitions
                  </p>
                </AnimatedSection>

                {/* Modern Timeline */}
                <div className="relative">
                  {/* Horizontal Progress Line - Desktop */}
                  <div className="hidden md:block absolute top-[72px] left-[16.67%] right-[16.67%] h-1.5 bg-slate-200 dark:bg-slate-700 rounded-full overflow-hidden">
                    <div 
                      className={cn(
                        "h-full rounded-full",
                        copiloteMode 
                          ? "bg-gradient-to-r from-blue-500 via-violet-500 to-purple-500" 
                          : "bg-gradient-to-r from-cyan-400 via-cyan-500 to-blue-500"
                      )}
                      style={{ 
                        width: '0%',
                        animation: 'expandWidth 2.5s ease-out forwards',
                        animationDelay: '0.8s'
                      }}
                    />
                  </div>

                  <div className="grid md:grid-cols-3 gap-8 md:gap-12">
                    {[
                      { 
                        icon: Search, 
                        title: copiloteMode ? "Briefing" : "La Recherche", 
                        desc: copiloteMode ? "Analyse de la mission et définition des objectifs" : "Immersion dans votre univers, comprendre vos besoins et vos ambitions.",
                        details: copiloteMode 
                          ? ["Audit complet", "Définition KPIs", "Analyse concurrence"]
                          : ["Entretien découverte", "Étude de marché", "Brief créatif"]
                      },
                      { 
                        icon: PenTool, 
                        title: copiloteMode ? "Stratégie" : "La Conception", 
                        desc: copiloteMode ? "Plan d'action et tactiques déployées" : "Transformation des idées en solutions créatives et efficaces.",
                        details: copiloteMode 
                          ? ["Roadmap détaillée", "Allocation budget", "Planning actions"]
                          : ["Maquettes & prototypes", "Tests utilisateurs", "Validation client"]
                      },
                      { 
                        icon: Rocket, 
                        title: copiloteMode ? "Exécution" : "La Diffusion", 
                        desc: copiloteMode ? "Déploiement et suivi en temps réel" : "Déploiement stratégique sur les canaux adaptés à votre cible.",
                        details: copiloteMode 
                          ? ["Lancement campagnes", "Suivi temps réel", "Optimisation continue"]
                          : ["Mise en production", "Formation équipes", "Suivi performance"]
                      }
                    ].map((step, idx) => (
                      <AnimatedCard key={idx} delay={idx * 300} hover={false}>
                        <div className="relative">
                          {/* Step Number Circle - Animated */}
                          <div className="flex justify-center mb-6">
                            <div 
                              className={cn(
                                "relative w-36 h-36 rounded-full flex items-center justify-center",
                                copiloteMode 
                                  ? "bg-gradient-to-br from-slate-800 to-slate-900 border-2 border-slate-700" 
                                  : "bg-white border-2 border-slate-200 shadow-xl"
                              )}
                              style={{
                                animation: `scaleIn 0.5s ease-out forwards`,
                                animationDelay: `${0.3 + idx * 0.3}s`,
                                opacity: 0,
                                transform: 'scale(0.8)'
                              }}
                            >
                              {/* Rotating border effect */}
                              <div 
                                className={cn(
                                  "absolute inset-0 rounded-full",
                                  copiloteMode 
                                    ? "bg-gradient-to-r from-violet-500 via-transparent to-blue-500" 
                                    : "bg-gradient-to-r from-cyan-400 via-transparent to-orange-400"
                                )}
                                style={{
                                  padding: '3px',
                                  mask: 'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
                                  maskComposite: 'exclude',
                                  animation: 'spin 8s linear infinite'
                                }}
                              />
                              
                              {/* Inner content */}
                              <div className={cn(
                                "w-28 h-28 rounded-full flex flex-col items-center justify-center",
                                copiloteMode 
                                  ? `bg-gradient-to-br ${copiloteGradient}` 
                                  : "bg-gradient-to-br from-cyan-500 to-blue-600"
                              )}>
                                <span className="text-white/50 text-xs font-medium mb-1">ÉTAPE</span>
                                <span className="text-white text-4xl font-black">{idx + 1}</span>
                              </div>
                            </div>
                          </div>
                          
                          {/* Content Card */}
                          <div className={cn(
                            "p-6 rounded-2xl text-center transition-all duration-300 group",
                            copiloteMode 
                              ? "bg-slate-800/50 border border-slate-700 hover:border-violet-500/50 hover:shadow-violet-500/10 hover:shadow-2xl" 
                              : "bg-white border border-slate-200 hover:border-cyan-300 hover:shadow-xl hover:shadow-cyan-500/10"
                          )}>
                            {/* Icon */}
                            <div className={cn(
                              "w-14 h-14 rounded-xl mx-auto mb-4 flex items-center justify-center transition-transform duration-300 group-hover:scale-110 group-hover:-rotate-6",
                              copiloteMode ? "bg-slate-700" : "bg-slate-100"
                            )}>
                              <step.icon className={cn(
                                "w-7 h-7",
                                copiloteMode ? "text-violet-400" : "text-cyan-600"
                              )} />
                            </div>
                            
                            <h4 className={cn(
                              "font-bold text-xl mb-2",
                              copiloteMode ? "text-white" : "text-slate-800"
                            )}>
                              {step.title}
                            </h4>
                            <p className={cn(
                              "text-sm mb-4",
                              copiloteMode ? "text-slate-400" : "text-slate-600"
                            )}>
                              {step.desc}
                            </p>
                            
                            {/* Details list */}
                            <div className="space-y-2">
                              {step.details.map((detail, i) => (
                                <div 
                                  key={i}
                                  className={cn(
                                    "flex items-center justify-center gap-2 text-sm",
                                    copiloteMode ? "text-slate-500" : "text-slate-500"
                                  )}
                                >
                                  <CheckCircle className={cn(
                                    "w-4 h-4",
                                    copiloteMode ? "text-violet-400" : "text-cyan-500"
                                  )} />
                                  <span>{detail}</span>
                                </div>
                              ))}
                            </div>
                          </div>

                          {/* Connecting Arrow - Desktop */}
                          {idx < 2 && (
                            <div className="hidden md:block absolute top-[72px] -right-6 z-20">
                              <div className={cn(
                                "w-12 h-12 rounded-full flex items-center justify-center",
                                copiloteMode ? "bg-slate-800 border border-slate-700" : "bg-white border border-slate-200 shadow-lg"
                              )}>
                                <ArrowRight className={cn(
                                  "w-5 h-5",
                                  copiloteMode ? "text-violet-400" : "text-cyan-500"
                                )} />
                              </div>
                            </div>
                          )}
                        </div>
                      </AnimatedCard>
                    ))}
                  </div>
                </div>
              </div>
            </section>

            {/* Stats Section */}
            <section className={cn(
              "py-24",
              copiloteMode 
                ? `bg-gradient-to-br ${copiloteBg}` 
                : "bg-gradient-to-br from-cyan-500 to-blue-600"
            )}>
              <div className="max-w-7xl mx-auto px-4">
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
                  {keyStats.map((stat, idx) => (
                    <AnimatedCard key={stat.id || idx} delay={idx * 100} hover={false}>
                      <div className="text-center">
                        <div className="text-5xl sm:text-6xl font-bold text-white mb-2">
                          <AnimatedCounter end={stat.value} suffix={stat.suffix} />
                        </div>
                        <p className="text-white/80">{stat.label}</p>
                      </div>
                    </AnimatedCard>
                  ))}
                </div>
              </div>
            </section>

            {/* COPILOTE CTA - Mission Possible Theme - Only when copilote section enabled */}
            {!copiloteMode && siteSettings?.copilote_section?.enabled && (
              <section className="py-24 bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 relative overflow-hidden">
                <div className="absolute inset-0 overflow-hidden">
                  <div className="absolute top-0 right-0 w-96 h-96 bg-orange-600/20 rounded-full blur-3xl animate-pulse" />
                  <div className="absolute bottom-0 left-0 w-96 h-96 bg-red-600/20 rounded-full blur-3xl animate-pulse delay-1000" />
                  {/* Corner decorations */}
                  <div className="absolute top-0 left-0 w-24 h-24 border-l-2 border-t-2 border-orange-500/30" />
                  <div className="absolute top-0 right-0 w-24 h-24 border-r-2 border-t-2 border-orange-500/30" />
                  <div className="absolute bottom-0 left-0 w-24 h-24 border-l-2 border-b-2 border-orange-500/30" />
                  <div className="absolute bottom-0 right-0 w-24 h-24 border-r-2 border-b-2 border-orange-500/30" />
                </div>
                <div className="max-w-4xl mx-auto px-4 relative z-10 text-center">
                  <AnimatedSection>
                    <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gradient-to-r from-orange-500 to-red-600 mb-6">
                      <Flame className="w-5 h-5 text-white" />
                      <span className="font-semibold text-white">MISSION POSSIBLE</span>
                    </div>
                    <h2 className="text-4xl sm:text-5xl font-black text-white mb-6">
                      Activez le mode{' '}
                      <span className="text-transparent bg-clip-text bg-gradient-to-r from-orange-500 to-red-500">
                        COPILOTE
                      </span>
                    </h2>
                    <p className="text-xl text-white/70 mb-4">
                      Une cellule d'accompagnement sur 12 mois pour mettre en place
                      <br />et co-piloter votre stratégie marketing d'acquisition.
                    </p>
                    <p className="text-sm text-white/50 mb-8">
                      Solution sous réserve d'éligibilité
                    </p>
                    <Button 
                      size="lg"
                      onClick={() => setCopiloteMode(true)}
                      className="rounded-full px-10 py-6 text-lg font-bold bg-gradient-to-r from-orange-500 to-red-600 shadow-2xl shadow-orange-500/30 hover:shadow-orange-500/50 hover:scale-105 transition-all"
                    >
                      <Crosshair className="w-6 h-6 mr-3" />
                      Découvrir le programme
                    </Button>
                  </AnimatedSection>
                </div>
              </section>
            )}

            {/* Contact Section */}
            <section id="contact" className={cn("py-24", copiloteMode ? "bg-slate-950" : "bg-white")}>
              <div className="max-w-7xl mx-auto px-4">
                <div className="grid lg:grid-cols-2 gap-16">
                  <AnimatedSection>
                    <Badge className={cn("mb-4", copiloteMode ? `bg-gradient-to-r ${copiloteGradient} text-white` : "bg-cyan-100 text-cyan-700")}>
                      Contact
                    </Badge>
                    <h2 className={cn("text-3xl font-bold mb-6", copiloteMode ? "text-white" : "text-slate-800")}>
                      {copiloteMode ? "Centre de commandement" : "Parlons de votre projet !"}
                    </h2>
                    
                    <div className="space-y-6">
                      {[
                        { icon: Phone, title: "Téléphone", info: agencyInfo?.phone || "04 84 49 27 00", sub: "Lun-Ven 09h-18h" },
                        { icon: Mail, title: "Email", info: agencyInfo?.email || "contact@agence-msc.fr", sub: "Réponse sous 24h" },
                        { icon: MapPin, title: "Adresse", info: agencyInfo?.address || "52 Rue des Pétouses", sub: `${agencyInfo?.postal_code || "13800"} ${agencyInfo?.city || "ISTRES"} — Siège social` }
                      ].map((item, idx) => (
                        <AnimatedCard key={idx} delay={idx * 100} hover={false}>
                          <div className="flex items-start gap-4">
                            <div className={cn(
                              "w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0",
                              copiloteMode ? `bg-gradient-to-br ${copiloteGradient}` : "bg-cyan-100"
                            )}>
                              <item.icon className={cn("w-6 h-6", copiloteMode ? "text-white" : "text-cyan-600")} />
                            </div>
                            <div>
                              <p className={cn("text-sm", copiloteMode ? "text-slate-500" : "text-slate-400")}>{item.sub}</p>
                              <p className={cn("font-semibold", copiloteMode ? "text-white" : "text-slate-800")}>{item.info}</p>
                            </div>
                          </div>
                        </AnimatedCard>
                      ))}
                    </div>
                  </AnimatedSection>

                  <AnimatedSection delay={200}>
                    <div className={cn(
                      "p-8 rounded-2xl",
                      copiloteMode ? "bg-slate-900 border border-slate-800" : "bg-slate-50"
                    )}>
                      <h3 className={cn("text-xl font-bold mb-6", copiloteMode ? "text-white" : "text-slate-800")}>
                        Demande de rappel
                      </h3>
                      <form onSubmit={handleCallbackSubmit} className="space-y-4">
                        <div>
                          <Label className={copiloteMode ? "text-slate-300" : ""}>Votre nom</Label>
                          <Input 
                            value={callbackForm.name}
                            onChange={(e) => setCallbackForm({...callbackForm, name: e.target.value})}
                            className={copiloteMode ? "bg-slate-800 border-slate-700 text-white" : ""}
                            required
                          />
                        </div>
                        <div>
                          <Label className={copiloteMode ? "text-slate-300" : ""}>Téléphone</Label>
                          <Input 
                            type="tel"
                            value={callbackForm.phone}
                            onChange={(e) => setCallbackForm({...callbackForm, phone: e.target.value})}
                            className={copiloteMode ? "bg-slate-800 border-slate-700 text-white" : ""}
                            required
                          />
                        </div>
                        <Button 
                          type="submit" 
                          className={cn(
                            "w-full rounded-full",
                            copiloteMode 
                              ? `bg-gradient-to-r ${copiloteGradient}` 
                              : "bg-cyan-500 hover:bg-cyan-600"
                          )}
                        >
                          <Send className="w-4 h-4 mr-2" />
                          Être rappelé
                        </Button>
                      </form>
                    </div>
                  </AnimatedSection>
                </div>
              </div>
            </section>

            {/* Footer */}
            <footer className={cn("py-16 border-t", copiloteMode ? "bg-slate-950 border-slate-800" : "bg-slate-900 border-slate-800")}>
              <div className="max-w-7xl mx-auto px-4">
                <div className="grid md:grid-cols-6 gap-8 lg:gap-12 mb-12">
                  {/* Company Info */}
                  <div className="md:col-span-2">
                    <div className="flex items-center gap-3 mb-4">
                      {copiloteMode ? (
                        <div className={cn(
                          "w-14 h-14 rounded-full flex items-center justify-center border-2 shadow-lg",
                          `bg-gradient-to-br ${copiloteGradient} border-violet-400`
                        )}>
                          <Rocket className="w-6 h-6 text-white" />
                        </div>
                      ) : themeSettings?.logo?.lightUrl ? (
                        <img 
                          src={themeSettings.logo.lightUrl} 
                          alt={themeSettings.agency?.name || "Logo"}
                          className="h-14 w-auto object-contain"
                        />
                      ) : (
                        <div className={cn(
                          "w-14 h-14 rounded-full flex items-center justify-center border-2 shadow-lg",
                          "bg-gradient-to-br from-[#0f172a] to-[#1e3a5f] border-slate-600"
                        )}>
                          <span className="font-bold text-lg text-white">MSC</span>
                        </div>
                      )}
                      <div>
                        <p className="font-bold text-white text-lg">{copiloteMode ? "COPILOTE" : (themeSettings?.agency?.name || "MSC L'AGENCE")}</p>
                        <p className="text-sm text-slate-400">{copiloteMode ? "Mode Mission" : "Mon Service Com"}</p>
                      </div>
                    </div>
                    <p className="text-slate-400 text-sm mb-6">
                      {siteContent?.footer?.description || "Votre partenaire communication à Istres depuis 2012. Création web, identité visuelle, production vidéo et marketing digital."}
                    </p>
                    
                    {/* Social Links */}
                    <div className="flex items-center gap-3">
                      {socialLinks?.facebook?.trim() && (
                        <a href={socialLinks.facebook} target="_blank" rel="noopener noreferrer" className={cn(
                          "w-10 h-10 rounded-full flex items-center justify-center transition-all",
                          copiloteMode ? "bg-slate-800 hover:bg-violet-600" : "bg-slate-800 hover:bg-cyan-500"
                        )}>
                          <Facebook className="w-5 h-5 text-white" />
                        </a>
                      )}
                      {socialLinks?.instagram?.trim() && (
                        <a href={socialLinks.instagram} target="_blank" rel="noopener noreferrer" className={cn(
                          "w-10 h-10 rounded-full flex items-center justify-center transition-all",
                          copiloteMode ? "bg-slate-800 hover:bg-violet-600" : "bg-slate-800 hover:bg-cyan-500"
                        )}>
                          <Instagram className="w-5 h-5 text-white" />
                        </a>
                      )}
                      {socialLinks?.linkedin?.trim() && (
                        <a href={socialLinks.linkedin} target="_blank" rel="noopener noreferrer" className={cn(
                          "w-10 h-10 rounded-full flex items-center justify-center transition-all",
                          copiloteMode ? "bg-slate-800 hover:bg-violet-600" : "bg-slate-800 hover:bg-cyan-500"
                        )}>
                          <Linkedin className="w-5 h-5 text-white" />
                        </a>
                      )}
                      {socialLinks?.youtube?.trim() && (
                        <a href={socialLinks.youtube} target="_blank" rel="noopener noreferrer" className={cn(
                          "w-10 h-10 rounded-full flex items-center justify-center transition-all",
                          copiloteMode ? "bg-slate-800 hover:bg-violet-600" : "bg-slate-800 hover:bg-cyan-500"
                        )}>
                          <Youtube className="w-5 h-5 text-white" />
                        </a>
                      )}
                      {socialLinks?.twitter?.trim() && (
                        <a href={socialLinks.twitter} target="_blank" rel="noopener noreferrer" className={cn(
                          "w-10 h-10 rounded-full flex items-center justify-center transition-all",
                          copiloteMode ? "bg-slate-800 hover:bg-violet-600" : "bg-slate-800 hover:bg-cyan-500"
                        )}>
                          <Twitter className="w-5 h-5 text-white" />
                        </a>
                      )}
                      {socialLinks?.tiktok?.trim() && (
                        <a href={socialLinks.tiktok} target="_blank" rel="noopener noreferrer" className={cn(
                          "w-10 h-10 rounded-full flex items-center justify-center transition-all",
                          copiloteMode ? "bg-slate-800 hover:bg-violet-600" : "bg-slate-800 hover:bg-cyan-500"
                        )}>
                          <svg className="w-5 h-5 text-white" viewBox="0 0 24 24" fill="currentColor">
                            <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/>
                          </svg>
                        </a>
                      )}
                    </div>
                  </div>
                  
                  {/* Services */}
                  <div>
                    <h4 className="font-semibold text-white mb-4">Services</h4>
                    <ul className="space-y-2 text-sm text-slate-400">
                      {activeServices.map(s => (
                        <li key={s.id}>
                          <Link to={`/site/${s.id}`} className="hover:text-cyan-400 transition-colors">{s.title}</Link>
                        </li>
                      ))}
                    </ul>
                  </div>
                  
                  {/* Agency */}
                  <div>
                    <h4 className="font-semibold text-white mb-4">L'agence</h4>
                    <ul className="space-y-2 text-sm text-slate-400">
                      <li><Link to="/site/equipe" className="hover:text-white transition-colors">Notre équipe</Link></li>
                      <li><button onClick={() => scrollToSection('methode')} className="hover:text-white transition-colors">Notre méthode</button></li>
                      <li><Link to="/site/realisations" className="hover:text-white transition-colors">Réalisations</Link></li>
                      <li><button onClick={() => scrollToSection('contact')} className="hover:text-white transition-colors">Contact</button></li>
                    </ul>
                    
                    {/* Free Tools */}
                    <h4 className="font-semibold text-white mb-3 mt-6">Outils gratuits</h4>
                    <ul className="space-y-2 text-sm text-slate-400">
                      <li>
                        <Link to="/site/timeline-communication" className="flex items-center gap-2 hover:text-cyan-400 transition-colors">
                          <Timer className="w-4 h-4" />
                          Timeline de la Com'
                        </Link>
                      </li>
                      {siteSettings?.game_section?.enabled && (
                      <li>
                        <Link to="/mon-espace-joueur" className="flex items-center gap-2 hover:text-amber-400 transition-colors">
                          <Trophy className="w-4 h-4 text-amber-500" />
                          Espace Jeux Concours
                        </Link>
                      </li>
                      )}
                    </ul>
                  </div>
                  
                  {/* Contact */}
                  <div>
                    <h4 className="font-semibold text-white mb-4">Contact</h4>
                    <ul className="space-y-3 text-sm text-slate-400">
                      <li className="flex items-center gap-2">
                        <Phone className="w-4 h-4 text-cyan-400" />
                        {agencyInfo?.phone || "04 84 49 27 00"}
                      </li>
                      <li className="flex items-center gap-2">
                        <Mail className="w-4 h-4 text-cyan-400" />
                        {agencyInfo?.email || "contact@agence-msc.fr"}
                      </li>
                      <li className="flex items-start gap-2">
                        <MapPin className="w-4 h-4 text-cyan-400 mt-0.5" />
                        <span>{agencyInfo?.address || "52 Rue des Pétouses"}<br/>{agencyInfo?.postal_code || "13800"} {agencyInfo?.city || "Istres"}<br/><em className="text-slate-500 text-xs not-italic">Siège social — Ne reçoit pas de public</em></span>
                      </li>
                    </ul>
                    
                    {/* Resources */}
                    <h4 className="font-semibold text-white mb-3 mt-6">Ressources</h4>
                    <ul className="space-y-2 text-sm text-slate-400">
                      <li><Link to="/site/blog" className="hover:text-white transition-colors">Blog</Link></li>
                      <li><Link to="/site/aide" className="hover:text-white transition-colors">Base de connaissances</Link></li>
                      <li><Link to="/site/cgv" className="hover:text-white transition-colors">CGV</Link></li>
                    </ul>
                  </div>
                </div>
                
                {/* Bottom Footer */}
                <div className="border-t border-slate-800 pt-8">
                  <div className="flex flex-col md:flex-row items-center justify-between gap-4">
                    <p className="text-sm text-slate-500">
                      © {new Date().getFullYear()} MSC L'Agence. Tous droits réservés.
                    </p>
                    <div className="flex flex-wrap items-center justify-center gap-4 text-sm text-slate-500">
                      <Link to="/site/mentions-legales" className="hover:text-white transition-colors">Mentions légales</Link>
                      <span className="hidden md:inline">•</span>
                      <Link to="/site/confidentialite" className="hover:text-white transition-colors">Politique de confidentialité</Link>
                      <span className="hidden md:inline">•</span>
                      <Link to="/site/cgv" className="hover:text-white transition-colors">CGV</Link>
                      <span className="hidden md:inline">•</span>
                      {/* Admin CMS Access - Discreet icon */}
                      <Link 
                        to="/login" 
                        className="inline-flex items-center gap-1 text-slate-600 hover:text-amber-400 transition-colors"
                        title="Administration"
                      >
                        <Shield className="h-3.5 w-3.5" />
                      </Link>
                    </div>
                    <p className="text-xs text-slate-600" title={`Déployé le ${BUILD_DATE}`}>
                      v{APP_VERSION}
                    </p>
                  </div>
                </div>
              </div>
            </footer>
          </>
        )}

        {/* ============================================ */}
        {/* PROJECT CASE STUDY PAGE */}
        {/* ============================================ */}
        {isProjectPage && currentProject && (
          <div className={cn("min-h-screen pt-24 pb-16", copiloteMode ? "bg-slate-900" : "bg-white")}>
            {/* Hero Image */}
            <div className="relative h-[50vh] min-h-[400px] overflow-hidden">
              <img 
                src={getImageUrl(currentProject.image)} 
                alt={currentProject.title}
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-black/20" />
              
              {/* Back Button */}
              <Link 
                to={`/site/creation-${currentProject.category}`}
                className="absolute top-6 left-6 flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 backdrop-blur-sm text-white hover:bg-white/20 transition-colors"
              >
                <ChevronLeft className="w-4 h-4" />
                Retour aux {currentProject.category === 'web' ? 'projets Web' : 
                            currentProject.category === 'graphique' ? 'projets Graphique' : 
                            currentProject.category === 'video' ? 'projets Vidéo' : 'projets Marketing'}
              </Link>
              
              {/* Project Info Overlay */}
              <div className="absolute bottom-0 left-0 right-0 p-8 md:p-12">
                <div className="max-w-5xl mx-auto">
                  <div className={cn(
                    "inline-block px-3 py-1 rounded-full text-sm font-medium mb-4",
                    currentProject.category === 'web' && "bg-cyan-500 text-white",
                    currentProject.category === 'graphique' && "bg-pink-500 text-white",
                    currentProject.category === 'video' && "bg-purple-500 text-white",
                    currentProject.category === 'marketing' && "bg-orange-500 text-white"
                  )}>
                    {currentProject.category === 'web' && 'Création Web'}
                    {currentProject.category === 'graphique' && 'Création Graphique'}
                    {currentProject.category === 'video' && 'Création Vidéo'}
                    {currentProject.category === 'marketing' && 'Marketing Digital'}
                  </div>
                  <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-2">
                    {currentProject.title}
                  </h1>
                  <div className="flex items-center gap-3 text-white/80 text-lg">
                    <span>Client :</span>
                    {currentProject.clientLogo && (
                      <img 
                        src={currentProject.clientLogo} 
                        alt={currentProject.client}
                        className="w-8 h-8 rounded-full object-cover bg-white border-2 border-white/30"
                      />
                    )}
                    <span className="font-semibold text-white">{currentProject.client}</span>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Content */}
            <div className="max-w-5xl mx-auto px-4 py-12">
              {/* Tags */}
              {currentProject.tags?.length > 0 && (
                <div className="flex flex-wrap gap-2 mb-8">
                  {currentProject.tags.map((tag, i) => (
                    <span key={i} className={cn(
                      "px-4 py-2 rounded-full text-sm font-medium",
                      copiloteMode ? "bg-slate-800 text-slate-300" : "bg-slate-100 text-slate-700"
                    )}>
                      {tag}
                    </span>
                  ))}
                </div>
              )}
              
              {/* Description */}
              <div className={cn(
                "prose prose-lg max-w-none mb-12",
                copiloteMode && "prose-invert"
              )}>
                <h2 className={cn("text-2xl font-bold mb-4", copiloteMode ? "text-white" : "text-slate-800")}>
                  À propos du projet
                </h2>
                <p className={cn("text-lg leading-relaxed", copiloteMode ? "text-slate-300" : "text-slate-600")}>
                  {currentProject.description}
                </p>
                
                {/* Additional content sections */}
                <div className="grid md:grid-cols-2 gap-8 mt-12">
                  <div className={cn(
                    "p-6 rounded-2xl",
                    copiloteMode ? "bg-slate-800" : "bg-slate-50"
                  )}>
                    <h3 className={cn("text-lg font-semibold mb-3 flex items-center gap-2", copiloteMode ? "text-white" : "text-slate-800")}>
                      <Target className="w-5 h-5 text-cyan-500" />
                      Objectifs
                    </h3>
                    <ul className={cn("space-y-2", copiloteMode ? "text-slate-300" : "text-slate-600")}>
                      <li className="flex items-start gap-2">
                        <CheckCircle className="w-4 h-4 text-cyan-500 mt-1 flex-shrink-0" />
                        <span>Moderniser l'image de marque</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <CheckCircle className="w-4 h-4 text-cyan-500 mt-1 flex-shrink-0" />
                        <span>Améliorer la visibilité en ligne</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <CheckCircle className="w-4 h-4 text-cyan-500 mt-1 flex-shrink-0" />
                        <span>Générer plus de conversions</span>
                      </li>
                    </ul>
                  </div>
                  
                  <div className={cn(
                    "p-6 rounded-2xl",
                    copiloteMode ? "bg-slate-800" : "bg-slate-50"
                  )}>
                    <h3 className={cn("text-lg font-semibold mb-3 flex items-center gap-2", copiloteMode ? "text-white" : "text-slate-800")}>
                      <Zap className="w-5 h-5 text-amber-500" />
                      Résultats
                    </h3>
                    <ul className={cn("space-y-2", copiloteMode ? "text-slate-300" : "text-slate-600")}>
                      <li className="flex items-start gap-2">
                        <TrendingUp className="w-4 h-4 text-cyan-500 mt-1 flex-shrink-0" />
                        <span>+150% de trafic en 3 mois</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <TrendingUp className="w-4 h-4 text-cyan-500 mt-1 flex-shrink-0" />
                        <span>+80% de demandes de contact</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <TrendingUp className="w-4 h-4 text-cyan-500 mt-1 flex-shrink-0" />
                        <span>ROI positif dès le 2ème mois</span>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
              
              {/* CTA */}
              <div className={cn(
                "p-8 rounded-2xl text-center",
                copiloteMode ? "bg-gradient-to-r from-violet-900/50 to-indigo-900/50" : "bg-gradient-to-r from-cyan-50 to-blue-50"
              )}>
                <h3 className={cn("text-xl font-bold mb-2", copiloteMode ? "text-white" : "text-slate-800")}>
                  Vous souhaitez un projet similaire ?
                </h3>
                <p className={cn("mb-4", copiloteMode ? "text-slate-300" : "text-slate-600")}>
                  Discutons de vos besoins et voyons comment nous pouvons vous aider.
                </p>
                <div className="flex flex-wrap justify-center gap-3">
                  <Button 
                    onClick={() => handleQuoteRequest(`Création ${currentProject.category === 'web' ? 'Web' : currentProject.category === 'graphique' ? 'Graphique' : currentProject.category === 'video' ? 'Vidéo' : 'Marketing'}`, '')}
                    className={cn("rounded-full", copiloteMode ? "bg-violet-600 hover:bg-violet-500" : "bg-cyan-600 hover:bg-cyan-500")}
                  >
                    <FileText className="w-4 h-4 mr-2" />
                    Demander un devis
                  </Button>
                  <Button 
                    onClick={handleCallbackAction}
                    variant="outline"
                    className="rounded-full"
                  >
                    <Phone className="w-4 h-4 mr-2" />
                    Être rappelé
                  </Button>
                </div>
              </div>
              
              {/* Other Projects */}
              <div className="mt-16">
                <h3 className={cn("text-xl font-bold mb-6", copiloteMode ? "text-white" : "text-slate-800")}>
                  Autres réalisations {currentProject.category === 'web' ? 'Web' : currentProject.category === 'graphique' ? 'Graphique' : currentProject.category === 'video' ? 'Vidéo' : 'Marketing'}
                </h3>
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
                  {(cmsPortfolio || PORTFOLIO)
                    .filter(p => p.category === currentProject.category && p.id !== currentProject.id)
                    .slice(0, 3)
                    .map((project) => (
                    <Link
                      key={project.id}
                      to={`/site/realisations/${project.slug || project.id}`}
                      className={cn(
                        "rounded-xl overflow-hidden group transition-all hover:shadow-lg",
                        copiloteMode ? "bg-slate-800" : "bg-white shadow border border-slate-100"
                      )}
                    >
                      <div className="relative aspect-video overflow-hidden">
                        <img 
                          src={getImageUrl(project.image)} 
                          alt={project.title}
                          className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                        />
                      </div>
                      <div className="p-4">
                        <p className={cn("text-xs mb-1", copiloteMode ? "text-slate-500" : "text-slate-400")}>
                          {project.client}
                        </p>
                        <h4 className={cn("font-semibold text-sm", copiloteMode ? "text-white" : "text-slate-800")}>
                          {project.title}
                        </h4>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ============================================ */}
        {/* TIMELINE DE LA COMMUNICATION PAGE */}
        {/* ============================================ */}
        {isTimelinePage && (
          <div className={cn("min-h-screen pt-24 pb-16", copiloteMode ? "bg-slate-900" : "bg-gradient-to-b from-slate-50 to-white")}>
            <div className="max-w-5xl mx-auto px-4">
              {/* Header */}
              <div className="text-center mb-12">
                <div className={cn(
                  "inline-flex items-center gap-2 px-4 py-2 rounded-full mb-4",
                  copiloteMode ? "bg-violet-900/50 text-violet-300" : "bg-cyan-100 text-cyan-700"
                )}>
                  <Timer className="w-4 h-4" />
                  <span className="text-sm font-medium">Outil gratuit</span>
                </div>
                <h1 className={cn("text-3xl sm:text-4xl font-bold mb-4", copiloteMode ? "text-white" : "text-slate-800")}>
                  La Timeline de la Com'
                </h1>
                <p className={cn("text-lg max-w-2xl mx-auto", copiloteMode ? "text-slate-300" : "text-slate-600")}>
                  Découvrez les étapes clés de la communication d'entreprise. 
                  Où en êtes-vous dans votre parcours ? Identifiez vos prochaines priorités.
                </p>
              </div>

              {/* Timeline Visual */}
              <div className="relative">
                {/* Vertical Line */}
                <div className={cn(
                  "absolute left-8 top-0 bottom-0 w-1 rounded-full",
                  copiloteMode ? "bg-slate-700" : "bg-slate-200"
                )} />
                
                {/* Timeline Items */}
                <div className="space-y-6">
                  {(cmsTimeline || TIMELINE_COM).map((step, idx) => {
                    // Find which service/solution corresponds to this step
                    const stepId = step.id || step.order || idx + 1;
                    const relatedService = activeServices.find(s => 
                      s.solutions?.some(sol => sol.timelinePosition === stepId)
                    );
                    const relatedSolution = relatedService?.solutions?.find(sol => sol.timelinePosition === stepId);
                    
                    // Get phase color
                    const phaseColors = {
                      'Fondation': 'from-amber-500 to-orange-500',
                      'Identité': 'from-pink-500 to-rose-500',
                      'Digital': 'from-cyan-500 to-blue-500',
                      'Déploiement': 'from-cyan-500 to-blue-500',
                      'Croissance': 'from-violet-500 to-purple-500'
                    };
                    
                    return (
                      <div key={step.id || idx} className="relative pl-20">
                        {/* Step Number */}
                        <div className={cn(
                          "absolute left-4 w-10 h-10 rounded-full flex items-center justify-center font-bold text-white shadow-lg bg-gradient-to-br",
                          phaseColors[step.phase] || "from-slate-500 to-slate-600"
                        )}>
                          {step.order || stepId}
                        </div>
                        
                        {/* Card */}
                        <div className={cn(
                          "p-5 rounded-xl border transition-all hover:shadow-lg",
                          copiloteMode ? "bg-slate-800 border-slate-700 hover:border-slate-600" : "bg-white border-slate-200 hover:border-slate-300"
                        )}>
                          <div className="flex flex-wrap items-start justify-between gap-3">
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-1">
                                <span className={cn(
                                  "px-2 py-0.5 rounded text-xs font-medium",
                                  copiloteMode ? "bg-slate-700 text-slate-300" : "bg-slate-100 text-slate-600"
                                )}>
                                  Phase {step.phase}
                                </span>
                              </div>
                              <h3 className={cn("text-lg font-semibold mb-1", copiloteMode ? "text-white" : "text-slate-800")}>
                                {step.name}
                              </h3>
                              <p className={cn("text-sm", copiloteMode ? "text-slate-400" : "text-slate-500")}>
                                {step.description}
                              </p>
                              
                              {/* External Link if present */}
                              {step.link_url && (
                                <a 
                                  href={step.link_url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className={cn(
                                    "inline-flex items-center gap-1.5 mt-2 text-sm font-medium transition-colors",
                                    copiloteMode 
                                      ? "text-cyan-400 hover:text-cyan-300" 
                                      : "text-cyan-600 hover:text-cyan-700"
                                  )}
                                >
                                  <ExternalLink className="w-3.5 h-3.5" />
                                  {step.link_text || 'En savoir plus'}
                                </a>
                              )}
                            </div>
                            
                            {/* Link to related service */}
                            {relatedService && (
                              <Link 
                                to={`/site/${relatedService.id}`}
                                className={cn(
                                  "flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-colors",
                                  copiloteMode 
                                    ? "bg-violet-900/50 text-violet-300 hover:bg-violet-900" 
                                    : "bg-cyan-50 text-cyan-700 hover:bg-cyan-100"
                                )}
                              >
                                <relatedService.icon className="w-4 h-4" />
                                {relatedSolution ? relatedSolution.name : relatedService.shortTitle}
                                <ArrowRight className="w-3 h-3" />
                              </Link>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* CTA */}
              <div className={cn(
                "mt-12 p-8 rounded-2xl text-center",
                copiloteMode ? "bg-gradient-to-r from-violet-900/50 to-indigo-900/50" : "bg-gradient-to-r from-cyan-50 to-blue-50"
              )}>
                <h3 className={cn("text-xl font-bold mb-2", copiloteMode ? "text-white" : "text-slate-800")}>
                  Besoin d'aide pour identifier vos priorités ?
                </h3>
                <p className={cn("mb-4", copiloteMode ? "text-slate-300" : "text-slate-600")}>
                  Nos experts analysent votre situation et vous recommandent les meilleures actions.
                </p>
                <div className="flex flex-wrap justify-center gap-3">
                  <Button 
                    onClick={handleCallbackAction}
                    className={cn("rounded-full", copiloteMode ? "bg-violet-600 hover:bg-violet-500" : "bg-cyan-600 hover:bg-cyan-500")}
                  >
                    <Phone className="w-4 h-4 mr-2" />
                    Être rappelé gratuitement
                  </Button>
                  <Button 
                    onClick={() => handleQuoteAction()}
                    variant="outline"
                    className="rounded-full"
                  >
                    <FileText className="w-4 h-4 mr-2" />
                    Demander un devis
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ============================================ */}
        {/* SERVICE PAGE */}
        {/* ============================================ */}
        {currentService && (
          <div className="relative">
            {/* Service Content - Full width without fixed sidebar */}
            <div className="w-full">
              {/* Service Hero - Compact with minimal back button */}
              <section className={cn(
                "pt-20 sm:pt-24 pb-4 sm:pb-6 relative overflow-hidden",
                copiloteMode ? "bg-slate-900" : currentService.bgColor
              )}>
                {/* Background Media (Image or Video) */}
                {currentService.heroMedia && (
                  <div className="absolute inset-0 overflow-hidden">
                    {currentService.heroMedia.type === 'video' ? (
                      <video 
                        autoPlay 
                        loop 
                        muted 
                        playsInline
                        className="absolute inset-0 w-full h-full object-cover opacity-15"
                      >
                        <source src={currentService.heroMedia.url} type="video/mp4" />
                      </video>
                    ) : (
                      <img 
                        src={currentService.heroMedia.url} 
                        alt=""
                        className="absolute inset-0 w-full h-full object-cover opacity-10"
                      />
                    )}
                    {/* Gradient overlay for better text readability */}
                    <div className={cn(
                      "absolute inset-0",
                      copiloteMode 
                        ? "bg-gradient-to-r from-slate-900/95 via-slate-900/80 to-slate-900/95" 
                        : `bg-gradient-to-r from-white/95 via-transparent to-white/95`
                    )} />
                  </div>
                )}
                
                {/* Decorative blur circle */}
                <div className="absolute inset-0 overflow-hidden pointer-events-none">
                  <div className={cn("absolute top-0 right-0 w-64 sm:w-96 h-64 sm:h-96 rounded-full blur-3xl opacity-20", `bg-gradient-to-br ${currentService.color}`)} />
                </div>
                
                <div className="max-w-7xl mx-auto px-4 sm:px-6 relative z-10">
                  <AnimatedSection>
                    {/* Minimal back button */}
                    <button 
                      onClick={() => { navigate('/site'); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
                      className={cn(
                        "inline-flex items-center gap-1 text-xs mb-3 opacity-70 hover:opacity-100 transition-opacity",
                        copiloteMode ? "text-slate-400" : "text-slate-500"
                      )}
                    >
                      <Home className="w-3 h-3" /> Accueil
                    </button>
                    
                    {/* Header with title left, actions right */}
                    <div className="flex items-start sm:items-center justify-between gap-3">
                      <div className="flex-1 min-w-0">
                        <div className={cn("inline-flex items-center gap-2 px-3 py-1 rounded-full mb-2 text-sm", `bg-gradient-to-r ${currentService.color}`)}>
                          <currentService.icon className="w-4 h-4 text-white" />
                          <span className="text-white font-medium">{currentService.shortTitle}</span>
                        </div>
                        <h1 className={cn("text-xl sm:text-3xl lg:text-4xl font-bold leading-tight", copiloteMode ? "text-white" : "text-slate-800")}>
                          {SEO_SERVICES[currentService.id]?.heroTitle || currentService.title}
                        </h1>
                      </div>
                      {/* Actions - Right aligned */}
                      <div className="flex items-center gap-2 flex-shrink-0">
                        <Button 
                          size="sm"
                          onClick={() => handleQuoteRequest(currentService.title)}
                          className={cn("rounded-full text-sm px-4", `bg-gradient-to-r ${currentService.color}`)}
                        >
                          <span className="hidden sm:inline">Devis gratuit</span>
                          <span className="sm:hidden">Devis</span>
                        </Button>
                        <a 
                          href="tel:0484492700"
                          className={cn(
                            "flex items-center justify-center w-9 h-9 rounded-full transition-colors",
                            copiloteMode ? "bg-slate-700 text-white hover:bg-slate-600" : "bg-slate-100 text-slate-700 hover:bg-slate-200"
                          )}
                        >
                          <Phone className="w-4 h-4" />
                        </a>
                      </div>
                    </div>
                  </AnimatedSection>
                </div>
              </section>

              {/* Horizontal Services Navigation */}
              <section className={cn(
                "py-4 border-b sticky top-16 lg:top-20 z-20",
                copiloteMode ? "bg-slate-900/95 backdrop-blur-lg border-slate-800" : "bg-white/95 backdrop-blur-lg border-slate-200"
              )}>
                <div className="max-w-7xl mx-auto px-4">
                  <div className="flex items-center gap-2 overflow-x-auto scrollbar-hide pb-1 -mx-4 px-4">
                    {activeServices.map(service => (
                      <Link
                        key={service.id}
                        to={`/site/${service.id}`}
                        className={cn(
                          "flex items-center gap-2 px-3 lg:px-4 py-2 rounded-full whitespace-nowrap transition-all flex-shrink-0 text-xs lg:text-sm",
                          currentService?.id === service.id
                            ? `bg-gradient-to-r ${service.color} text-white shadow-lg`
                            : copiloteMode 
                              ? "bg-slate-800 text-slate-300 hover:bg-slate-700" 
                              : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                        )}
                      >
                        <service.icon className="w-3.5 h-3.5 lg:w-4 lg:h-4" />
                        <span className="font-medium">{service.shortTitle}</span>
                      </Link>
                    ))}
                  </div>
                </div>
              </section>

              {/* Solutions Menu - Special Layout for Subscription Services */}
              {currentService.hasSolutions && currentService.solutions && (
                currentService.id === 'hebergement' || currentService.solutions.some(s => s.is_subscription) ? (
                  /* Subscription Plans - Pricing Table Layout */
                  <section className={cn(
                    "py-12",
                    copiloteMode ? "bg-slate-900" : "bg-gradient-to-b from-cyan-50 to-white"
                  )}>
                    <div className="max-w-7xl mx-auto px-4">
                      <div className="text-center mb-10">
                        <h2 className={cn(
                          "text-2xl md:text-3xl font-bold mb-3",
                          copiloteMode ? "text-white" : "text-slate-900"
                        )}>
                          Nos Forfaits {currentService.shortTitle}
                        </h2>
                        <p className={cn(
                          "text-base max-w-2xl mx-auto",
                          copiloteMode ? "text-slate-400" : "text-slate-600"
                        )}>
                          Choisissez le forfait adapté à vos besoins. <span className="text-cyan-600 font-semibold">-10% sur le paiement annuel !</span>
                        </p>
                      </div>
                      
                      {/* Pricing Cards Grid */}
                      <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
                        {[...currentService.solutions]
                          .sort((a, b) => (a.order || a.priceFrom) - (b.order || b.priceFrom))
                          .map((plan, idx) => {
                            const monthlyPrice = plan.priceFrom || 0;
                            const yearlyPrice = Math.round(monthlyPrice * 12 * 0.9); // 10% discount
                            const savings = monthlyPrice * 12 - yearlyPrice;
                            
                            return (
                              <Card 
                                key={plan.id}
                                className={cn(
                                  "relative overflow-hidden transition-all hover:shadow-xl",
                                  plan.recommended 
                                    ? "border-2 border-cyan-500 shadow-lg scale-[1.02]"
                                    : copiloteMode ? "bg-slate-800 border-slate-700" : "bg-white border-slate-200"
                                )}
                              >
                                {plan.recommended && (
                                  <div className="absolute top-0 left-0 right-0 bg-cyan-500 text-white text-center py-1 text-xs font-semibold">
                                    ⭐ RECOMMANDÉ
                                  </div>
                                )}
                                
                                <CardContent className={cn("p-6", plan.recommended ? "pt-10" : "pt-6")}>
                                  {/* Plan Header */}
                                  <div className="text-center mb-6">
                                    <div className={cn(
                                      "w-14 h-14 rounded-full mx-auto mb-3 flex items-center justify-center",
                                      plan.recommended ? "bg-cyan-100" : "bg-slate-100"
                                    )}>
                                      <plan.icon className={cn(
                                        "w-7 h-7",
                                        plan.recommended ? "text-cyan-600" : "text-slate-600"
                                      )} />
                                    </div>
                                    <h3 className={cn(
                                      "text-lg font-bold",
                                      copiloteMode ? "text-white" : "text-slate-900"
                                    )}>
                                      {plan.name}
                                    </h3>
                                    <p className={cn(
                                      "text-sm mt-1",
                                      copiloteMode ? "text-slate-400" : "text-slate-500"
                                    )}>
                                      {plan.description || plan.desc}
                                    </p>
                                  </div>
                                  
                                  {/* Pricing */}
                                  <div className="text-center mb-6">
                                    <div className={cn(
                                      "text-4xl font-bold",
                                      plan.recommended ? "text-cyan-600" : copiloteMode ? "text-white" : "text-slate-900"
                                    )}>
                                      {monthlyPrice}€
                                      <span className={cn(
                                        "text-base font-normal",
                                        copiloteMode ? "text-slate-400" : "text-slate-500"
                                      )}>/mois</span>
                                    </div>
                                    <div className={cn(
                                      "mt-2 p-2 rounded-lg text-sm",
                                      plan.recommended ? "bg-cyan-50 text-cyan-700" : "bg-slate-50 text-slate-600"
                                    )}>
                                      <span className="font-semibold">{yearlyPrice}€/an</span>
                                      <span className="text-cyan-600 ml-1">(économisez {savings}€)</span>
                                    </div>
                                  </div>
                                  
                                  {/* Features */}
                                  <div className="space-y-3 mb-6">
                                    {(plan.features || []).map((feature, i) => (
                                      <div key={i} className={cn(
                                        "flex items-start gap-2 text-sm",
                                        copiloteMode ? "text-slate-300" : "text-slate-600"
                                      )}>
                                        <CheckCircle className="w-5 h-5 text-cyan-500 flex-shrink-0 mt-0.5" />
                                        <span>{feature}</span>
                                      </div>
                                    ))}
                                  </div>
                                  
                                  {/* CTA Button */}
                                  <Button 
                                    onClick={() => handleQuoteRequest(currentService.title, plan.name)}
                                    className={cn(
                                      "w-full",
                                      plan.recommended 
                                        ? "bg-cyan-600 hover:bg-cyan-700 text-white"
                                        : "bg-slate-900 hover:bg-slate-800 text-white"
                                    )}
                                  >
                                    Souscrire
                                  </Button>
                                </CardContent>
                              </Card>
                            );
                          })}
                      </div>
                      
                      {/* Footer Note */}
                      <div className={cn(
                        "mt-8 text-center text-sm",
                        copiloteMode ? "text-slate-400" : "text-slate-500"
                      )}>
                        <p>💡 Tous les forfaits sont sans engagement. Paiement annuel = 10% de remise.</p>
                        <p className="mt-2">
                          Besoin d'un forfait sur mesure ?{" "}
                          <button 
                            onClick={handleCallbackAction}
                            className="text-cyan-600 font-semibold hover:underline"
                          >
                            Contactez-nous
                          </button>
                        </p>
                      </div>
                    </div>
                  </section>
                ) : (
                /* Standard Solutions Display for non-subscription services */
                <section className={cn(
                  "py-8",
                  copiloteMode ? "bg-slate-900" : "bg-slate-50"
                )}>
                  <div className="max-w-7xl mx-auto px-4">
                    <div className="grid lg:grid-cols-3 gap-8">
                      {/* Left: Solutions List (Vertical Menu sorted by price) */}
                      <div className="lg:col-span-1">
                        <h3 className={cn("text-sm font-semibold uppercase tracking-wider mb-4", copiloteMode ? "text-slate-400" : "text-slate-500")}>
                          Nos offres {currentService.shortTitle}
                        </h3>
                        <div className="space-y-2">
                          {[...currentService.solutions]
                            .sort((a, b) => a.priceFrom - b.priceFrom)
                            .map((solution, idx) => (
                            <button
                              key={solution.id}
                              onClick={() => setActiveSolution(solution.id)}
                              className={cn(
                                "w-full p-4 rounded-xl text-left transition-all group",
                                activeSolution === solution.id
                                  ? copiloteMode
                                    ? `bg-gradient-to-r ${copiloteGradient} text-white shadow-lg`
                                    : `bg-gradient-to-r ${currentService.color} text-white shadow-lg`
                                  : copiloteMode
                                    ? "bg-slate-800 hover:bg-slate-750 text-slate-300"
                                    : "bg-white hover:bg-slate-100 text-slate-700 shadow-sm"
                              )}
                            >
                              <div className="flex items-center gap-3">
                                <div className={cn(
                                  "w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 transition-all",
                                  activeSolution === solution.id
                                    ? "bg-white/20"
                                    : `bg-gradient-to-br ${currentService.color}`
                                )}>
                                  <solution.icon className={cn("w-5 h-5", activeSolution === solution.id ? "text-white" : "text-white")} />
                                </div>
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center gap-2">
                                    <span className="font-semibold">{solution.name}</span>
                                    {solution.badge && (
                                      <span className={cn(
                                        "text-xs px-2 py-0.5 rounded-full",
                                        activeSolution === solution.id
                                          ? "bg-white/20 text-white"
                                          : copiloteMode 
                                            ? "bg-violet-500/20 text-violet-300"
                                            : "bg-cyan-100 text-cyan-700"
                                      )}>
                                        {solution.badge}
                                      </span>
                                    )}
                                  </div>
                                  <div className={cn(
                                    "text-sm",
                                    activeSolution === solution.id ? "text-white/80" : copiloteMode ? "text-slate-400" : "text-slate-500"
                                  )}>
                                    À partir de <span className="font-semibold">{solution.priceFrom.toLocaleString()}€</span>
                                    {solution.priceUnit && ` ${solution.priceUnit}`}
                                  </div>
                                </div>
                                <ChevronRight className={cn(
                                  "w-5 h-5 transition-transform",
                                  activeSolution === solution.id ? "rotate-90" : ""
                                )} />
                              </div>
                            </button>
                          ))}
                        </div>
                        
                        {/* Quick Actions */}
                        <div className="mt-6 space-y-2">
                          <Button 
                            onClick={() => handleQuoteRequest(currentService.title, activeSolution ? currentService.solutions.find(s => s.id === activeSolution)?.name : null)}
                            className={cn("w-full rounded-xl", `bg-gradient-to-r ${currentService.color}`)}
                          >
                            <FileText className="w-4 h-4 mr-2" />
                            Demander un devis
                          </Button>
                          <Button 
                            onClick={handleCallbackAction}
                            variant="outline"
                            className={cn("w-full rounded-xl", copiloteMode ? "border-slate-700" : "")}
                          >
                            <Phone className="w-4 h-4 mr-2" />
                            Être rappelé
                          </Button>
                        </div>
                      </div>
                      
                      {/* Right: Solution Details */}
                      <div className="lg:col-span-2">
                        {currentService.solutions.map(solution => (
                          activeSolution === solution.id && (
                            <div key={solution.id} className="animate-in fade-in duration-300">
                              <Card className={cn(
                                "overflow-hidden",
                                copiloteMode ? "bg-slate-800 border-slate-700" : "bg-white border-slate-200"
                              )}>
                                {/* Solution Header */}
                                <div className={cn("p-6 bg-gradient-to-r text-white", currentService.color)}>
                                  <div className="flex items-start justify-between">
                                    <div>
                                      <div className="flex items-center gap-3 mb-2">
                                        <solution.icon className="w-8 h-8" />
                                        <h3 className="text-2xl font-bold">{solution.name}</h3>
                                        {solution.badge && (
                                          <span className="bg-white/20 px-3 py-1 rounded-full text-sm">{solution.badge}</span>
                                        )}
                                      </div>
                                      <p className="text-white/80">{solution.desc}</p>
                                    </div>
                                    <div className="text-right">
                                      <p className="text-sm text-white/70">À partir de</p>
                                      <p className="text-3xl font-bold">{solution.priceFrom.toLocaleString()}€</p>
                                      {solution.priceUnit && <p className="text-sm text-white/70">{solution.priceUnit}</p>}
                                    </div>
                                  </div>
                                </div>
                                
                                {/* Solution Content */}
                                <CardContent className="p-6 space-y-6">
                                  {/* Features Grid */}
                                  <div>
                                    <h4 className={cn("font-semibold mb-3", copiloteMode ? "text-white" : "text-slate-800")}>
                                      Ce qui est inclus
                                    </h4>
                                    <div className="grid sm:grid-cols-2 gap-2">
                                      {solution.features?.map((feature, i) => (
                                        <div key={i} className={cn("flex items-center gap-2 text-sm", copiloteMode ? "text-slate-300" : "text-slate-600")}>
                                          <CheckCircle className={cn("w-4 h-4 flex-shrink-0", copiloteMode ? "text-violet-400" : "text-cyan-500")} />
                                          {feature}
                                        </div>
                                      ))}
                                    </div>
                                  </div>
                                  
                                  {/* Timeline Position Badge - Clickable */}
                                  {solution.timelinePosition && (
                                    <Link 
                                      to="/site/timeline-communication"
                                      className={cn(
                                        "p-4 rounded-xl border block group transition-all hover:shadow-md",
                                        copiloteMode ? "bg-violet-900/30 border-violet-700 hover:border-violet-500" : "bg-gradient-to-r from-cyan-50 to-blue-50 border-cyan-200 hover:border-cyan-400"
                                      )}
                                    >
                                      <div className="flex items-center justify-between">
                                        <div className="flex items-center gap-3">
                                          <div className={cn(
                                            "w-10 h-10 rounded-full flex items-center justify-center font-bold text-lg",
                                            copiloteMode ? "bg-violet-500 text-white" : "bg-cyan-500 text-white"
                                          )}>
                                            {solution.timelinePosition}
                                          </div>
                                          <div>
                                            <p className={cn("text-xs uppercase tracking-wider", copiloteMode ? "text-violet-400" : "text-cyan-600")}>
                                              Position dans la Timeline de la Com'
                                            </p>
                                            <p className={cn("font-semibold", copiloteMode ? "text-white" : "text-slate-800")}>
                                              Étape {solution.timelinePosition} • Phase {solution.timelinePhase}
                                            </p>
                                          </div>
                                        </div>
                                        <div className={cn(
                                          "flex items-center gap-1 text-xs font-medium opacity-0 group-hover:opacity-100 transition-opacity",
                                          copiloteMode ? "text-violet-400" : "text-cyan-600"
                                        )}>
                                          Voir la timeline
                                          <ExternalLink className="w-3 h-3" />
                                        </div>
                                      </div>
                                    </Link>
                                  )}
                                  
                                  {/* Why & When - Rich Content */}
                                  {(solution.whyDoIt || solution.whenToDoIt) && (
                                    <div className="grid md:grid-cols-2 gap-4">
                                      {/* Pourquoi */}
                                      {solution.whyDoIt && (
                                        <div className={cn(
                                          "p-4 rounded-xl",
                                          copiloteMode ? "bg-slate-700/50" : "bg-amber-50"
                                        )}>
                                          <div className="flex items-center gap-2 mb-2">
                                            <div className={cn(
                                              "w-8 h-8 rounded-lg flex items-center justify-center",
                                              copiloteMode ? "bg-amber-500/20" : "bg-amber-100"
                                            )}>
                                              <Lightbulb className={cn("w-4 h-4", copiloteMode ? "text-amber-400" : "text-amber-600")} />
                                            </div>
                                            <h5 className={cn("font-semibold", copiloteMode ? "text-white" : "text-slate-800")}>
                                              Pourquoi le faire ?
                                            </h5>
                                          </div>
                                          <p className={cn("text-sm leading-relaxed", copiloteMode ? "text-slate-300" : "text-slate-600")}>
                                            {solution.whyDoIt}
                                          </p>
                                        </div>
                                      )}
                                      
                                      {/* Quand */}
                                      {solution.whenToDoIt && (
                                        <div className={cn(
                                          "p-4 rounded-xl",
                                          copiloteMode ? "bg-slate-700/50" : "bg-cyan-50"
                                        )}>
                                          <div className="flex items-center gap-2 mb-2">
                                            <div className={cn(
                                              "w-8 h-8 rounded-lg flex items-center justify-center",
                                              copiloteMode ? "bg-cyan-500/20" : "bg-cyan-100"
                                            )}>
                                              <Clock className={cn("w-4 h-4", copiloteMode ? "text-cyan-400" : "text-cyan-600")} />
                                            </div>
                                            <h5 className={cn("font-semibold", copiloteMode ? "text-white" : "text-slate-800")}>
                                              Quand le faire ?
                                            </h5>
                                          </div>
                                          <p className={cn("text-sm leading-relaxed", copiloteMode ? "text-slate-300" : "text-slate-600")}>
                                            {solution.whenToDoIt}
                                          </p>
                                        </div>
                                      )}
                                    </div>
                                  )}
                                  
                                  {/* Ideal For / Not Ideal For */}
                                  {(solution.idealFor || solution.notIdealFor) && (
                                    <div className="grid md:grid-cols-2 gap-4">
                                      {/* Idéal pour */}
                                      {solution.idealFor && (
                                        <div>
                                          <h5 className={cn("text-sm font-semibold mb-2 flex items-center gap-2", copiloteMode ? "text-cyan-400" : "text-cyan-600")}>
                                            <CheckCircle className="w-4 h-4" />
                                            Idéal pour
                                          </h5>
                                          <ul className="space-y-1">
                                            {solution.idealFor.map((item, i) => (
                                              <li key={i} className={cn("text-sm flex items-center gap-2", copiloteMode ? "text-slate-300" : "text-slate-600")}>
                                                <span className="w-1.5 h-1.5 rounded-full bg-cyan-500" />
                                                {item}
                                              </li>
                                            ))}
                                          </ul>
                                        </div>
                                      )}
                                      
                                      {/* Moins adapté pour */}
                                      {solution.notIdealFor && (
                                        <div>
                                          <h5 className={cn("text-sm font-semibold mb-2 flex items-center gap-2", copiloteMode ? "text-orange-400" : "text-orange-600")}>
                                            <HelpCircle className="w-4 h-4" />
                                            Moins adapté pour
                                          </h5>
                                          <ul className="space-y-1">
                                            {solution.notIdealFor.map((item, i) => (
                                              <li key={i} className={cn("text-sm flex items-center gap-2", copiloteMode ? "text-slate-400" : "text-slate-500")}>
                                                <span className="w-1.5 h-1.5 rounded-full bg-orange-400" />
                                                {item}
                                              </li>
                                            ))}
                                          </ul>
                                        </div>
                                      )}
                                    </div>
                                  )}
                                  
                                  {/* Long Description */}
                                  {solution.longDesc && (
                                    <p className={cn("text-sm", copiloteMode ? "text-slate-400" : "text-slate-500")}>
                                      {solution.longDesc}
                                    </p>
                                  )}
                                  
                                  {/* Case Study Banner - For Jeux Concours - Only when game section enabled */}
                                  {solution.id === 'sol-jeux-concours' && siteSettings?.game_section?.enabled && (
                                    <Link 
                                      to="/site/jeux-concours"
                                      className={cn(
                                        "block rounded-xl overflow-hidden border-2 group transition-all hover:shadow-xl",
                                        copiloteMode ? "border-red-700 hover:border-red-500" : "border-red-200 hover:border-red-400"
                                      )}
                                    >
                                      <div className="bg-gradient-to-r from-red-900 via-red-800 to-slate-900 p-5">
                                        <div className="flex items-center gap-4">
                                          {/* Coffre Icon */}
                                          <div className="w-16 h-16 rounded-xl bg-gradient-to-br from-slate-700 to-slate-800 border-2 border-red-500/50 flex items-center justify-center shadow-lg flex-shrink-0">
                                            <Lock className="w-8 h-8 text-red-500" />
                                          </div>
                                          
                                          <div className="flex-1">
                                            <div className="flex items-center gap-2 mb-1">
                                              <span className="px-2 py-0.5 rounded-full bg-yellow-500/20 text-yellow-400 text-xs font-medium">
                                                ⭐ Étude de cas
                                              </span>
                                            </div>
                                            <h4 className="text-lg font-bold text-white">Le Coffre MSC 2026</h4>
                                            <p className="text-sm text-white/70">Découvrez comment nous avons créé un jeu-concours annuel engageant avec quiz mensuels et grand tirage final.</p>
                                          </div>
                                          
                                          <div className="flex-shrink-0">
                                            <div className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center group-hover:bg-white/20 transition-colors">
                                              <ExternalLink className="w-5 h-5 text-white" />
                                            </div>
                                          </div>
                                        </div>
                                        
                                        {/* Stats */}
                                        <div className="grid grid-cols-4 gap-3 mt-4 pt-4 border-t border-white/10">
                                          <div className="text-center">
                                            <p className="text-xl font-bold text-white">12</p>
                                            <p className="text-xs text-white/50">Quiz mensuels</p>
                                          </div>
                                          <div className="text-center">
                                            <p className="text-xl font-bold text-white">24</p>
                                            <p className="text-xs text-white/50">Indices</p>
                                          </div>
                                          <div className="text-center">
                                            <p className="text-xl font-bold text-white">1</p>
                                            <p className="text-xs text-white/50">Coffre</p>
                                          </div>
                                          <div className="text-center">
                                            <p className="text-xl font-bold text-white">∞</p>
                                            <p className="text-xs text-white/50">Engagement</p>
                                          </div>
                                        </div>
                                      </div>
                                      
                                      <div className={cn(
                                        "px-5 py-3 flex items-center justify-between",
                                        copiloteMode ? "bg-slate-800" : "bg-red-50"
                                      )}>
                                        <span className={cn("text-sm font-medium", copiloteMode ? "text-white" : "text-red-700")}>
                                          Voir l'étude de cas complète
                                        </span>
                                        <ArrowRight className={cn("w-4 h-4 group-hover:translate-x-1 transition-transform", copiloteMode ? "text-red-400" : "text-red-600")} />
                                      </div>
                                    </Link>
                                  )}
                                  
                                  {/* Comparison Tables for Premium Solutions */}
                                  {solution.vsWordPress && (
                                    <div className={cn(
                                      "rounded-xl overflow-hidden border",
                                      copiloteMode ? "border-slate-700" : "border-slate-200"
                                    )}>
                                      <div className={cn(
                                        "px-4 py-3 font-semibold text-sm",
                                        copiloteMode ? "bg-slate-700 text-white" : "bg-slate-100 text-slate-800"
                                      )}>
                                        React vs WordPress - Comparatif
                                      </div>
                                      <div className="divide-y divide-slate-200 dark:divide-slate-700">
                                        {solution.vsWordPress.map((row, i) => (
                                          <div key={i} className="grid grid-cols-3 text-sm">
                                            <div className={cn("px-4 py-2 font-medium", copiloteMode ? "text-slate-300" : "text-slate-700")}>
                                              {row.aspect}
                                            </div>
                                            <div className={cn("px-4 py-2 text-center", copiloteMode ? "text-cyan-400 bg-slate-800/50" : "text-cyan-600 bg-cyan-50/50")}>
                                              {row.react}
                                            </div>
                                            <div className={cn("px-4 py-2 text-center", copiloteMode ? "text-slate-400" : "text-slate-500")}>
                                              {row.wordpress}
                                            </div>
                                          </div>
                                        ))}
                                      </div>
                                    </div>
                                  )}
                                  
                                  {solution.vsNative && (
                                    <div className={cn(
                                      "rounded-xl overflow-hidden border",
                                      copiloteMode ? "border-slate-700" : "border-slate-200"
                                    )}>
                                      <div className={cn(
                                        "px-4 py-3 font-semibold text-sm",
                                        copiloteMode ? "bg-slate-700 text-white" : "bg-slate-100 text-slate-800"
                                      )}>
                                        PWA vs Application Native - Comparatif
                                      </div>
                                      <div className="divide-y divide-slate-200 dark:divide-slate-700">
                                        {solution.vsNative.map((row, i) => (
                                          <div key={i} className="grid grid-cols-3 text-sm">
                                            <div className={cn("px-4 py-2 font-medium", copiloteMode ? "text-slate-300" : "text-slate-700")}>
                                              {row.aspect}
                                            </div>
                                            <div className={cn("px-4 py-2 text-center", copiloteMode ? "text-cyan-400 bg-slate-800/50" : "text-cyan-600 bg-cyan-50/50")}>
                                              {row.pwa}
                                            </div>
                                            <div className={cn("px-4 py-2 text-center", copiloteMode ? "text-slate-400" : "text-slate-500")}>
                                              {row.native}
                                            </div>
                                          </div>
                                        ))}
                                      </div>
                                    </div>
                                  )}
                                  
                                  {/* CTA */}
                                  <div className="flex gap-3 pt-4 border-t border-slate-200 dark:border-slate-700">
                                    <Button 
                                      onClick={() => handleQuoteRequest(currentService.title, solution.name)}
                                      className={cn("flex-1 rounded-xl", `bg-gradient-to-r ${currentService.color}`)}
                                    >
                                      Démarrer ce projet
                                      <ArrowRight className="w-4 h-4 ml-2" />
                                    </Button>
                                    <Button 
                                      variant="outline" 
                                      onClick={handleCallbackAction}
                                      className="rounded-xl"
                                    >
                                      <Phone className="w-4 h-4" />
                                    </Button>
                                  </div>
                                </CardContent>
                              </Card>
                            </div>
                          )
                        ))}
                      </div>
                    </div>
                  </div>
                </section>
                )
              )}
              
              {/* ============================================ */}
              {/* PORTFOLIO GALLERY - Auto-scrolling Ticker */}
              {/* ============================================ */}
              {currentService && (() => {
                const serviceCategory = currentService.id.replace('creation-', '').replace('-digital', '');
                const categoryProjects = (cmsPortfolio || PORTFOLIO).filter(p => p.category === serviceCategory);
                // Duplicate for infinite loop
                const projectsToShow = categoryProjects.length > 0 
                  ? [...categoryProjects, ...categoryProjects, ...categoryProjects]
                  : [];
                
                return projectsToShow.length > 0 && (
                  <section className={cn("py-12", copiloteMode ? "bg-slate-900" : "bg-slate-50")}>
                    <div className="max-w-7xl mx-auto px-4">
                      {/* Header */}
                      <div className="flex items-center justify-between mb-6">
                        <div>
                          <h3 className={cn("text-xl sm:text-2xl font-bold", copiloteMode ? "text-white" : "text-slate-800")}>
                            Nos réalisations {currentService.shortTitle}
                          </h3>
                          <p className={cn("text-sm mt-1", copiloteMode ? "text-slate-400" : "text-slate-500")}>
                            Découvrez nos projets et études de cas
                          </p>
                        </div>
                        <Link 
                          to="/site/realisations" 
                          className={cn(
                            "flex items-center gap-1 text-sm font-medium",
                            copiloteMode ? "text-violet-400 hover:text-violet-300" : "text-cyan-600 hover:text-cyan-500"
                          )}
                        >
                          Voir tout
                          <ArrowRight className="w-4 h-4" />
                        </Link>
                      </div>
                      
                      {/* Auto-scrolling gallery - Boxed with blur overlay */}
                      <div className="relative overflow-hidden rounded-2xl">
                        {/* Gradient blur overlays */}
                        <div className={cn(
                          "absolute left-0 top-0 bottom-0 w-24 z-10 pointer-events-none",
                          copiloteMode 
                            ? "bg-gradient-to-r from-slate-900 via-slate-900/80 to-transparent" 
                            : "bg-gradient-to-r from-slate-50 via-slate-50/80 to-transparent"
                        )} />
                        <div className={cn(
                          "absolute right-0 top-0 bottom-0 w-24 z-10 pointer-events-none",
                          copiloteMode 
                            ? "bg-gradient-to-l from-slate-900 via-slate-900/80 to-transparent" 
                            : "bg-gradient-to-l from-slate-50 via-slate-50/80 to-transparent"
                        )} />
                        
                        {/* Auto-scrolling container */}
                        <div className="flex gap-5 animate-scroll-slow hover:pause-animation">
                          {projectsToShow.map((project, idx) => (
                            <Link
                              key={`${project.id}-${idx}`}
                              to={`/site/realisations/${project.slug || project.id}`}
                              className={cn(
                                "flex-shrink-0 w-[260px] rounded-xl overflow-hidden group/card transition-all duration-300",
                                copiloteMode 
                                  ? "bg-slate-800 hover:bg-slate-700 shadow-lg hover:shadow-xl" 
                                  : "bg-white shadow-md hover:shadow-lg border border-slate-200 hover:border-slate-300"
                              )}
                            >
                              {/* Image/Video Container with hover animations */}
                              <div className="relative aspect-[16/10] overflow-hidden">
                                {/* Video projects: show video on hover */}
                                {project.videoUrl ? (
                                  <>
                                    <img 
                                      src={getImageUrl(project.image)} 
                                      alt={project.title}
                                      className="w-full h-full object-cover transition-opacity duration-300 group-hover/card:opacity-0"
                                    />
                                    <video
                                      src={project.videoUrl}
                                      muted
                                      loop
                                      playsInline
                                      className="absolute inset-0 w-full h-full object-cover opacity-0 group-hover/card:opacity-100 transition-opacity duration-300"
                                      onMouseEnter={(e) => e.target.play()}
                                      onMouseLeave={(e) => { e.target.pause(); e.target.currentTime = 0; }}
                                    />
                                    {/* Play icon indicator */}
                                    <div className="absolute top-2 right-2 w-8 h-8 rounded-full bg-black/50 flex items-center justify-center opacity-70 group-hover/card:opacity-0 transition-opacity">
                                      <Play className="w-4 h-4 text-white fill-white" />
                                    </div>
                                  </>
                                ) : project.category === 'web' ? (
                                  /* Web projects: scroll animation on hover */
                                  <>
                                    <div className="w-full h-full overflow-hidden">
                                      <img 
                                        src={getImageUrl(project.fullImage || project.image)} 
                                        alt={project.title}
                                        className="w-full object-cover object-top transition-all duration-[3s] ease-linear group-hover/card:object-bottom"
                                        style={{ height: '200%' }}
                                      />
                                    </div>
                                    {/* Browser mockup indicator */}
                                    <div className="absolute top-2 left-2 flex items-center gap-1 px-2 py-1 rounded-full bg-black/50 opacity-70">
                                      <div className="w-2 h-2 rounded-full bg-red-400" />
                                      <div className="w-2 h-2 rounded-full bg-yellow-400" />
                                      <div className="w-2 h-2 rounded-full bg-green-400" />
                                    </div>
                                  </>
                                ) : (
                                  /* Default: simple zoom on hover */
                                  <img 
                                    src={getImageUrl(project.image)} 
                                    alt={project.title}
                                    className="w-full h-full object-cover transition-transform duration-500 group-hover/card:scale-110"
                                  />
                                )}
                                
                                {/* Overlay on hover */}
                                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent opacity-0 group-hover/card:opacity-100 transition-opacity flex items-end p-3">
                                  <div className="flex items-center gap-2 text-white text-xs font-medium">
                                    <Eye className="w-3 h-3" />
                                    Voir le projet
                                  </div>
                                </div>
                              </div>
                              
                              {/* Content */}
                              <div className="p-3">
                                <p className={cn("text-xs mb-0.5", copiloteMode ? "text-slate-500" : "text-slate-400")}>
                                  {project.client}
                                </p>
                                <h4 className={cn("font-semibold text-sm line-clamp-1", copiloteMode ? "text-white" : "text-slate-800")}>
                                  {project.title}
                                </h4>
                              </div>
                            </Link>
                          ))}
                        </div>
                      </div>
                    </div>
                  </section>
                );
              })()}
              
              {/* ============================================ */}
              {/* COMPARISON TABLE - All Offers of This Service */}
              {/* ============================================ */}
              {currentService.hasSolutions && currentService.solutions && currentService.solutions.length > 1 && (
                <section className={cn("py-16", copiloteMode ? "bg-slate-800" : "bg-white")}>
                  <div className="max-w-7xl mx-auto px-4">
                    <div className="text-center mb-10">
                      <div className={cn(
                        "inline-flex items-center gap-2 px-4 py-2 rounded-full mb-4",
                        copiloteMode ? "bg-violet-900/50 text-violet-300" : "bg-cyan-100 text-cyan-700"
                      )}>
                        <Sparkles className="w-4 h-4" />
                        <span className="text-sm font-medium">Comparez nos offres</span>
                      </div>
                      <h2 className={cn("text-2xl sm:text-3xl font-bold mb-3", copiloteMode ? "text-white" : "text-slate-800")}>
                        Quelle offre {currentService.shortTitle} choisir ?
                      </h2>
                      <p className={cn("text-base max-w-2xl mx-auto", copiloteMode ? "text-slate-300" : "text-slate-600")}>
                        Comparez les caractéristiques de nos offres pour trouver celle qui correspond à vos besoins.
                      </p>
                      {/* Mobile scroll hint */}
                      <p className={cn("text-xs mt-2 md:hidden", copiloteMode ? "text-slate-400" : "text-slate-500")}>
                        ← Glissez pour voir toutes les offres →
                      </p>
                    </div>
                    
                    {/* Comparison Table - Scrollable on mobile */}
                    <div className="overflow-x-auto -mx-4 px-4 md:mx-0 md:px-0">
                      <div className={cn(
                        "rounded-2xl overflow-hidden border shadow-lg min-w-[600px] md:min-w-0",
                        copiloteMode ? "border-slate-700 bg-slate-900" : "border-slate-200 bg-white"
                      )}>
                      {/* Table Header */}
                      <div className={cn(
                        "grid gap-px",
                        copiloteMode ? "bg-slate-700" : "bg-slate-200"
                      )} style={{ gridTemplateColumns: `minmax(120px, 180px) repeat(${currentService.solutions.length}, minmax(140px, 1fr))` }}>
                        <div className={cn("p-3 md:p-4", copiloteMode ? "bg-slate-800" : "bg-slate-50")} />
                        {[...currentService.solutions].sort((a, b) => a.priceFrom - b.priceFrom).map((solution, idx) => (
                          <div 
                            key={solution.id} 
                            className={cn(
                              "p-4 text-center",
                              idx === 1 
                                ? copiloteMode ? "bg-violet-900/50" : "bg-cyan-50" 
                                : copiloteMode ? "bg-slate-800" : "bg-slate-50"
                            )}
                          >
                            {solution.badge && (
                              <span className={cn(
                                "inline-block px-2 py-0.5 rounded text-xs font-semibold mb-2",
                                copiloteMode ? "bg-violet-500 text-white" : "bg-cyan-500 text-white"
                              )}>
                                {solution.badge}
                              </span>
                            )}
                            <h3 className={cn("font-bold text-sm sm:text-base", copiloteMode ? "text-white" : "text-slate-800")}>
                              {solution.name}
                            </h3>
                            <div className={cn("mt-1", copiloteMode ? "text-violet-300" : "text-cyan-600")}>
                              <span className="text-lg sm:text-xl font-bold">À partir de {solution.priceFrom}€</span>
                              <span className="text-xs"> HT</span>
                            </div>
                          </div>
                        ))}
                      </div>
                      
                      {/* Table Body - Features Comparison */}
                      <div className={cn("divide-y", copiloteMode ? "divide-slate-700" : "divide-slate-200")}>
                        {/* Row: Délai */}
                        <div className={cn(
                          "grid gap-px",
                          copiloteMode ? "bg-slate-700" : "bg-slate-200"
                        )} style={{ gridTemplateColumns: `minmax(120px, 180px) repeat(${currentService.solutions.length}, minmax(140px, 1fr))` }}>
                          <div className={cn("p-3 md:p-4 flex items-center", copiloteMode ? "bg-slate-800" : "bg-white")}>
                            <span className={cn("font-medium text-sm", copiloteMode ? "text-slate-300" : "text-slate-700")}>
                              <Clock className="w-4 h-4 inline mr-2 opacity-50" />
                              Délai de livraison
                            </span>
                          </div>
                          {[...currentService.solutions].sort((a, b) => a.priceFrom - b.priceFrom).map((solution, idx) => (
                            <div key={solution.id} className={cn(
                              "p-3 sm:p-4 text-center flex items-center justify-center",
                              idx === 1 
                                ? copiloteMode ? "bg-violet-900/30" : "bg-cyan-50/50" 
                                : copiloteMode ? "bg-slate-800" : "bg-white"
                            )}>
                              <span className={cn("text-sm", copiloteMode ? "text-slate-300" : "text-slate-600")}>
                                {solution.deliveryTime || '-'}
                              </span>
                            </div>
                          ))}
                        </div>
                        
                        {/* Row: Idéal pour */}
                        <div className={cn(
                          "grid gap-px",
                          copiloteMode ? "bg-slate-700" : "bg-slate-200"
                        )} style={{ gridTemplateColumns: `minmax(120px, 180px) repeat(${currentService.solutions.length}, minmax(140px, 1fr))` }}>
                          <div className={cn("p-3 md:p-4 flex items-center", copiloteMode ? "bg-slate-800" : "bg-white")}>
                            <span className={cn("font-medium text-sm", copiloteMode ? "text-slate-300" : "text-slate-700")}>
                              <Users className="w-4 h-4 inline mr-2 opacity-50" />
                              Idéal pour
                            </span>
                          </div>
                          {[...currentService.solutions].sort((a, b) => a.priceFrom - b.priceFrom).map((solution, idx) => (
                            <div key={solution.id} className={cn(
                              "p-3 sm:p-4",
                              idx === 1 
                                ? copiloteMode ? "bg-violet-900/30" : "bg-cyan-50/50" 
                                : copiloteMode ? "bg-slate-800" : "bg-white"
                            )}>
                              <div className="flex flex-wrap justify-center gap-1">
                                {solution.idealFor?.slice(0, 2).map((item, i) => (
                                  <span key={i} className={cn(
                                    "text-xs px-2 py-0.5 rounded-full",
                                    copiloteMode ? "bg-slate-700 text-slate-300" : "bg-slate-100 text-slate-600"
                                  )}>
                                    {item}
                                  </span>
                                )) || <span className="text-slate-400">-</span>}
                              </div>
                            </div>
                          ))}
                        </div>
                        
                        {/* Row: Timeline Position */}
                        <div className={cn(
                          "grid gap-px",
                          copiloteMode ? "bg-slate-700" : "bg-slate-200"
                        )} style={{ gridTemplateColumns: `minmax(120px, 180px) repeat(${currentService.solutions.length}, minmax(140px, 1fr))` }}>
                          <div className={cn("p-3 md:p-4 flex items-center", copiloteMode ? "bg-slate-800" : "bg-white")}>
                            <span className={cn("font-medium text-sm", copiloteMode ? "text-slate-300" : "text-slate-700")}>
                              <Timer className="w-4 h-4 inline mr-2 opacity-50" />
                              Position Timeline
                            </span>
                          </div>
                          {[...currentService.solutions].sort((a, b) => a.priceFrom - b.priceFrom).map((solution, idx) => (
                            <div key={solution.id} className={cn(
                              "p-3 sm:p-4 text-center flex items-center justify-center",
                              idx === 1 
                                ? copiloteMode ? "bg-violet-900/30" : "bg-cyan-50/50" 
                                : copiloteMode ? "bg-slate-800" : "bg-white"
                            )}>
                              {solution.timelinePosition ? (
                                <span className={cn(
                                  "inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium",
                                  copiloteMode ? "bg-violet-500/20 text-violet-300" : "bg-cyan-100 text-cyan-700"
                                )}>
                                  Étape {solution.timelinePosition}
                                </span>
                              ) : '-'}
                            </div>
                          ))}
                        </div>
                        
                        {/* Row: Nb de features */}
                        <div className={cn(
                          "grid gap-px",
                          copiloteMode ? "bg-slate-700" : "bg-slate-200"
                        )} style={{ gridTemplateColumns: `minmax(120px, 180px) repeat(${currentService.solutions.length}, minmax(140px, 1fr))` }}>
                          <div className={cn("p-3 md:p-4 flex items-center", copiloteMode ? "bg-slate-800" : "bg-white")}>
                            <span className={cn("font-medium text-sm", copiloteMode ? "text-slate-300" : "text-slate-700")}>
                              <CheckCircle className="w-4 h-4 inline mr-2 opacity-50" />
                              Fonctionnalités
                            </span>
                          </div>
                          {[...currentService.solutions].sort((a, b) => a.priceFrom - b.priceFrom).map((solution, idx) => (
                            <div key={solution.id} className={cn(
                              "p-3 sm:p-4 text-center flex items-center justify-center",
                              idx === 1 
                                ? copiloteMode ? "bg-violet-900/30" : "bg-cyan-50/50" 
                                : copiloteMode ? "bg-slate-800" : "bg-white"
                            )}>
                              <span className={cn("text-sm font-semibold", copiloteMode ? "text-cyan-400" : "text-cyan-600")}>
                                {solution.features?.length || 0} incluses
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>
                      
                      {/* Table Footer - CTAs */}
                      <div className={cn(
                        "grid gap-px",
                        copiloteMode ? "bg-slate-700" : "bg-slate-200"
                      )} style={{ gridTemplateColumns: `minmax(120px, 180px) repeat(${currentService.solutions.length}, minmax(140px, 1fr))` }}>
                        <div className={cn("p-3 md:p-4", copiloteMode ? "bg-slate-800" : "bg-slate-50")} />
                        {[...currentService.solutions].sort((a, b) => a.priceFrom - b.priceFrom).map((solution, idx) => (
                          <div key={solution.id} className={cn(
                            "p-4 text-center",
                            idx === 1 
                              ? copiloteMode ? "bg-violet-900/50" : "bg-cyan-50" 
                              : copiloteMode ? "bg-slate-800" : "bg-slate-50"
                          )}>
                            <Button
                              onClick={() => handleQuoteRequest(currentService.title, solution.name)}
                              className={cn(
                                "w-full rounded-lg text-sm",
                                idx === 1 
                                  ? `bg-gradient-to-r ${currentService.color}` 
                                  : "bg-slate-600 hover:bg-slate-500"
                              )}
                              size="sm"
                            >
                              Choisir
                            </Button>
                          </div>
                        ))}
                      </div>
                    </div>
                    </div>{/* End scrollable wrapper */}
                    
                    {/* Help Text */}
                    <div className="mt-6 text-center">
                      <p className={cn("text-sm", copiloteMode ? "text-slate-400" : "text-slate-500")}>
                        Besoin d'aide pour choisir ?{' '}
                        <button 
                          onClick={handleCallbackAction}
                          className={cn("font-medium underline", copiloteMode ? "text-violet-400" : "text-cyan-600")}
                        >
                          Nos experts vous conseillent gratuitement
                        </button>
                      </p>
                    </div>
                  </div>
                </section>
              )}
              
              {/* ============================================ */}
              {/* TESTIMONIALS SECTION */}
              {/* ============================================ */}
              {currentService && (
                <section className={cn("py-16", copiloteMode ? "bg-slate-900" : "bg-gradient-to-b from-slate-50 to-white")}>
                  <div className="max-w-7xl mx-auto px-4">
                    <div className="text-center mb-10">
                      <div className={cn(
                        "inline-flex items-center gap-2 px-4 py-2 rounded-full mb-4",
                        copiloteMode ? "bg-cyan-900/50 text-cyan-300" : "bg-cyan-100 text-cyan-700"
                      )}>
                        <Star className="w-4 h-4 fill-current" />
                        <span className="text-sm font-medium">Témoignages clients</span>
                      </div>
                      <h2 className={cn("text-2xl sm:text-3xl font-bold mb-3", copiloteMode ? "text-white" : "text-slate-800")}>
                        Ils nous font confiance
                      </h2>
                      <p className={cn("text-base max-w-2xl mx-auto", copiloteMode ? "text-slate-300" : "text-slate-600")}>
                        Découvrez les retours de nos clients sur leurs projets {currentService.shortTitle.toLowerCase()}.
                      </p>
                    </div>
                    
                    {/* Testimonials Grid */}
                    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                      {(cmsTestimonials || TESTIMONIALS)
                        .filter(t => t.service === currentService.id.replace('creation-', ''))
                        .slice(0, 3)
                        .map((testimonial) => (
                        <div 
                          key={testimonial.id}
                          className={cn(
                            "rounded-2xl overflow-hidden transition-all hover:shadow-xl group",
                            copiloteMode ? "bg-slate-800 border border-slate-700" : "bg-white shadow-lg border border-slate-100"
                          )}
                        >
                          {/* Video Testimonial */}
                          {testimonial.videoUrl ? (
                            <div className="relative aspect-video bg-slate-900">
                              <iframe
                                src={testimonial.videoUrl}
                                title={`Témoignage ${testimonial.name}`}
                                className="absolute inset-0 w-full h-full"
                                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                allowFullScreen
                              />
                              {/* Play overlay for thumbnail mode */}
                              <div className="absolute inset-0 flex items-center justify-center bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
                                <div className="w-16 h-16 rounded-full bg-white/90 flex items-center justify-center">
                                  <Play className="w-6 h-6 text-slate-800 ml-1" />
                                </div>
                              </div>
                            </div>
                          ) : (
                            /* Quote Icon for Text Testimonials */
                            <div className={cn(
                              "px-6 pt-6",
                              copiloteMode ? "text-violet-400" : "text-cyan-400"
                            )}>
                              <Quote className="w-10 h-10 opacity-30" />
                            </div>
                          )}
                          
                          {/* Testimonial Content */}
                          <div className="p-6">
                            {/* Rating */}
                            <div className="flex gap-1 mb-3">
                              {[...Array(testimonial.rating)].map((_, i) => (
                                <Star key={i} className="w-4 h-4 text-amber-400 fill-amber-400" />
                              ))}
                            </div>
                            
                            {/* Quote */}
                            <p className={cn(
                              "text-sm leading-relaxed mb-4",
                              copiloteMode ? "text-slate-300" : "text-slate-600"
                            )}>
                              "{testimonial.quote}"
                            </p>
                            
                            {/* Project Type Badge */}
                            <div className="mb-4">
                              <span className={cn(
                                "inline-block px-3 py-1 rounded-full text-xs font-medium",
                                copiloteMode ? "bg-slate-700 text-slate-300" : "bg-slate-100 text-slate-600"
                              )}>
                                {testimonial.projectType}
                              </span>
                            </div>
                            
                            {/* Author Info */}
                            <div className="flex items-center gap-3 pt-4 border-t border-slate-100 dark:border-slate-700">
                              {/* Avatar */}
                              <img 
                                src={getImageUrl(testimonial.avatar)} 
                                alt={testimonial.name}
                                className="w-12 h-12 rounded-full object-cover ring-2 ring-white dark:ring-slate-700 shadow"
                              />
                              
                              {/* Name & Role */}
                              <div className="flex-1 min-w-0">
                                <p className={cn("font-semibold text-sm truncate", copiloteMode ? "text-white" : "text-slate-800")}>
                                  {testimonial.name}
                                </p>
                                <p className={cn("text-xs truncate", copiloteMode ? "text-slate-400" : "text-slate-500")}>
                                  {testimonial.role}
                                </p>
                              </div>
                              
                              {/* Company Logo */}
                              <div className={cn(
                                "w-10 h-10 rounded-lg overflow-hidden flex-shrink-0",
                                copiloteMode ? "bg-slate-700" : "bg-slate-100"
                              )}>
                                <img 
                                  src={testimonial.companyLogo} 
                                  alt={testimonial.company}
                                  className="w-full h-full object-cover"
                                />
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                    
                    {/* Show more / All testimonials CTA */}
                    {TESTIMONIALS.filter(t => t.service === currentService.id.replace('creation-', '')).length === 0 && (
                      <div className="grid md:grid-cols-3 gap-6">
                        {TESTIMONIALS.slice(0, 3).map((testimonial) => (
                          <div 
                            key={testimonial.id}
                            className={cn(
                              "rounded-2xl overflow-hidden transition-all hover:shadow-xl",
                              copiloteMode ? "bg-slate-800 border border-slate-700" : "bg-white shadow-lg border border-slate-100"
                            )}
                          >
                            <div className={cn("px-6 pt-6", copiloteMode ? "text-violet-400" : "text-cyan-400")}>
                              <Quote className="w-10 h-10 opacity-30" />
                            </div>
                            <div className="p-6">
                              <div className="flex gap-1 mb-3">
                                {[...Array(testimonial.rating)].map((_, i) => (
                                  <Star key={i} className="w-4 h-4 text-amber-400 fill-amber-400" />
                                ))}
                              </div>
                              <p className={cn("text-sm leading-relaxed mb-4", copiloteMode ? "text-slate-300" : "text-slate-600")}>
                                "{testimonial.quote}"
                              </p>
                              <div className="mb-4">
                                <span className={cn(
                                  "inline-block px-3 py-1 rounded-full text-xs font-medium",
                                  copiloteMode ? "bg-slate-700 text-slate-300" : "bg-slate-100 text-slate-600"
                                )}>
                                  {testimonial.projectType}
                                </span>
                              </div>
                              <div className="flex items-center gap-3 pt-4 border-t border-slate-100 dark:border-slate-700">
                                <img src={getImageUrl(testimonial.avatar)} alt={testimonial.name} className="w-12 h-12 rounded-full object-cover ring-2 ring-white shadow" />
                                <div className="flex-1 min-w-0">
                                  <p className={cn("font-semibold text-sm truncate", copiloteMode ? "text-white" : "text-slate-800")}>{testimonial.name}</p>
                                  <p className={cn("text-xs truncate", copiloteMode ? "text-slate-400" : "text-slate-500")}>{testimonial.role}</p>
                                </div>
                                <div className={cn("w-10 h-10 rounded-lg overflow-hidden flex-shrink-0", copiloteMode ? "bg-slate-700" : "bg-slate-100")}>
                                  <img src={getImageUrl(testimonial.companyLogo)} alt={testimonial.company} className="w-full h-full object-cover" />
                                </div>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                    
                    {/* CTA */}
                    <div className="mt-10 text-center">
                      <p className={cn("text-sm mb-4", copiloteMode ? "text-slate-400" : "text-slate-500")}>
                        Rejoignez nos {TESTIMONIALS.length * 50}+ clients satisfaits
                      </p>
                      <Button 
                        onClick={() => handleQuoteAction()}
                        className={cn("rounded-full", `bg-gradient-to-r ${currentService.color}`)}
                      >
                        Démarrer mon projet
                        <ArrowRight className="w-4 h-4 ml-2" />
                      </Button>
                    </div>
                  </div>
                </section>
              )}
              
              {/* Hébergement & Maintenance - Comparatif Section */}
              {currentService?.hasSubscriptions && currentService?.subscriptions && (
                <section className={cn("py-16", copiloteMode ? "bg-slate-800" : "bg-white")}>
                  <div className="max-w-7xl mx-auto px-4">
                    <div className="text-center mb-12">
                      <h2 className={cn("text-3xl font-bold mb-4", copiloteMode ? "text-white" : "text-slate-800")}>
                        Nos forfaits Hébergement & Maintenance
                      </h2>
                      <p className={cn("text-lg max-w-2xl mx-auto", copiloteMode ? "text-slate-300" : "text-slate-600")}>
                        Choisissez la formule adaptée à vos besoins. Tous nos forfaits incluent un support technique et des sauvegardes automatiques.
                      </p>
                    </div>
                    
                    {/* Why it matters */}
                    <div className={cn("p-6 rounded-2xl mb-12", copiloteMode ? "bg-slate-700" : "bg-slate-50")}>
                      <h3 className={cn("text-lg font-semibold mb-4 flex items-center gap-2", copiloteMode ? "text-white" : "text-slate-800")}>
                        <Shield className="w-5 h-5 text-cyan-500" />
                        Pourquoi c'est important ?
                      </h3>
                      <div className="grid md:grid-cols-3 gap-6">
                        <div>
                          <div className="flex items-center gap-2 mb-2">
                            <div className="w-8 h-8 rounded-lg bg-red-100 flex items-center justify-center">
                              <Shield className="w-4 h-4 text-red-500" />
                            </div>
                            <span className={cn("font-medium", copiloteMode ? "text-white" : "text-slate-700")}>Sécurité</span>
                          </div>
                          <p className={cn("text-sm", copiloteMode ? "text-slate-400" : "text-slate-500")}>
                            43% des cyberattaques ciblent les petites entreprises. Un site non mis à jour est une porte ouverte aux hackers.
                          </p>
                        </div>
                        <div>
                          <div className="flex items-center gap-2 mb-2">
                            <div className="w-8 h-8 rounded-lg bg-amber-100 flex items-center justify-center">
                              <Zap className="w-4 h-4 text-amber-500" />
                            </div>
                            <span className={cn("font-medium", copiloteMode ? "text-white" : "text-slate-700")}>Performance</span>
                          </div>
                          <p className={cn("text-sm", copiloteMode ? "text-slate-400" : "text-slate-500")}>
                            1 seconde de chargement en plus = -7% de conversions. Un hébergement performant impacte directement vos ventes.
                          </p>
                        </div>
                        <div>
                          <div className="flex items-center gap-2 mb-2">
                            <div className="w-8 h-8 rounded-lg bg-cyan-100 flex items-center justify-center">
                              <Clock className="w-4 h-4 text-cyan-500" />
                            </div>
                            <span className={cn("font-medium", copiloteMode ? "text-white" : "text-slate-700")}>Disponibilité</span>
                          </div>
                          <p className={cn("text-sm", copiloteMode ? "text-slate-400" : "text-slate-500")}>
                            Un site hors ligne = clients perdus. Nos forfaits garantissent 99.9% de disponibilité avec monitoring 24/7.
                          </p>
                        </div>
                      </div>
                    </div>
                    
                    {/* Pricing Cards - Scrollable on mobile */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
                      {currentService.subscriptions.map((plan) => (
                        <Card 
                          key={plan.id}
                          className={cn(
                            "relative overflow-hidden transition-all hover:shadow-xl",
                            plan.recommended 
                              ? copiloteMode 
                                ? "border-2 border-violet-500 bg-slate-700" 
                                : "border-2 border-cyan-500 bg-white shadow-lg"
                              : copiloteMode
                                ? "bg-slate-700 border-slate-600"
                                : "bg-white"
                          )}
                        >
                          {plan.recommended && (
                            <div className={cn(
                              "absolute top-0 inset-x-0 py-1 text-center text-xs font-semibold text-white",
                              copiloteMode ? "bg-violet-500" : "bg-cyan-500"
                            )}>
                              Recommandé
                            </div>
                          )}
                          <CardContent className={cn("pt-6 sm:pt-8 pb-4 sm:pb-6", plan.recommended && "pt-10 sm:pt-12")}>
                            <div className="flex items-center gap-2 sm:gap-3 mb-3 sm:mb-4">
                              <div className={cn(
                                "w-8 h-8 sm:w-10 sm:h-10 rounded-xl flex items-center justify-center flex-shrink-0",
                                copiloteMode ? "bg-violet-500/20" : "bg-cyan-500/20"
                              )}>
                                <plan.icon className={cn("w-4 h-4 sm:w-5 sm:h-5", copiloteMode ? "text-violet-400" : "text-cyan-600")} />
                              </div>
                              <div className="min-w-0">
                                <h4 className={cn("font-semibold text-sm sm:text-base truncate", copiloteMode ? "text-white" : "text-slate-800")}>
                                  {plan.name}
                                </h4>
                                <p className={cn("text-xs truncate", copiloteMode ? "text-slate-400" : "text-slate-500")}>
                                  {plan.description}
                                </p>
                              </div>
                            </div>
                            
                            <div className="mb-4 sm:mb-6">
                              <div className="flex items-baseline gap-1">
                                <span className={cn("text-2xl sm:text-3xl font-bold", copiloteMode ? "text-white" : "text-slate-800")}>
                                  {plan.priceMonthly}€
                                </span>
                                <span className={cn("text-xs sm:text-sm", copiloteMode ? "text-slate-400" : "text-slate-500")}>/mois</span>
                              </div>
                              <p className={cn("text-xs", copiloteMode ? "text-slate-500" : "text-slate-400")}>
                                ou {plan.priceYearly}€/an (2 mois offerts)
                              </p>
                            </div>
                            
                            <ul className="space-y-1.5 sm:space-y-2 mb-4 sm:mb-6">
                              {plan.features.slice(0, 4).map((feature, i) => (
                                <li key={i} className="flex items-start gap-1.5 sm:gap-2">
                                  <CheckCircle className={cn("w-3.5 h-3.5 sm:w-4 sm:h-4 mt-0.5 flex-shrink-0", copiloteMode ? "text-violet-400" : "text-cyan-500")} />
                                  <span className={cn("text-xs sm:text-sm leading-tight", copiloteMode ? "text-slate-300" : "text-slate-600")}>
                                    {feature}
                                  </span>
                                </li>
                              ))}
                              {plan.features.length > 4 && (
                                <li className={cn("text-xs pl-5 sm:pl-6", copiloteMode ? "text-slate-500" : "text-slate-400")}>
                                  +{plan.features.length - 4} autres avantages
                                </li>
                              )}
                            </ul>
                            
                            <Button 
                              onClick={() => openQuoteModal('Hébergement & Maintenance', plan.name)}
                              className={cn(
                                "w-full rounded-xl text-sm h-9 sm:h-10",
                                plan.recommended
                                  ? copiloteMode ? "bg-violet-500 hover:bg-violet-600" : "bg-cyan-500 hover:bg-cyan-600"
                                  : copiloteMode ? "bg-slate-600 hover:bg-slate-500" : "bg-slate-800 hover:bg-slate-700"
                              )}
                            >
                              Choisir ce forfait
                            </Button>
                          </CardContent>
                        </Card>
                      ))}
                    </div>
                    
                    {/* Comparison Table */}
                    <div className="mt-16">
                      <h3 className={cn("text-xl font-bold mb-6 text-center", copiloteMode ? "text-white" : "text-slate-800")}>
                        Comparatif détaillé
                      </h3>
                      <div className={cn("rounded-xl overflow-hidden", copiloteMode ? "bg-slate-700" : "bg-white shadow-lg")}>
                        <table className="w-full">
                          <thead>
                            <tr className={cn(copiloteMode ? "bg-slate-600" : "bg-slate-50")}>
                              <th className={cn("text-left py-4 px-6 font-semibold", copiloteMode ? "text-white" : "text-slate-800")}>
                                Fonctionnalité
                              </th>
                              {currentService.subscriptions.map((plan) => (
                                <th key={plan.id} className={cn("text-center py-4 px-4 font-semibold", copiloteMode ? "text-white" : "text-slate-800")}>
                                  {plan.name.replace('Hébergement ', '').replace('Maintenance ', '')}
                                </th>
                              ))}
                            </tr>
                          </thead>
                          <tbody className={cn("divide-y", copiloteMode ? "divide-slate-600" : "divide-slate-100")}>
                            {['SSL Gratuit', 'Sauvegardes', 'Support', 'Uptime garanti', 'CDN', 'Modifications'].map((feature, idx) => (
                              <tr key={idx}>
                                <td className={cn("py-4 px-6", copiloteMode ? "text-slate-300" : "text-slate-600")}>
                                  {feature}
                                </td>
                                {currentService.subscriptions.map((plan, planIdx) => (
                                  <td key={planIdx} className="text-center py-4 px-4">
                                    {plan.features.some(f => f.toLowerCase().includes(feature.toLowerCase().split(' ')[0])) ? (
                                      <CheckCircle className={cn("w-5 h-5 mx-auto", copiloteMode ? "text-violet-400" : "text-cyan-500")} />
                                    ) : (
                                      <X className="w-5 h-5 mx-auto text-slate-300" />
                                    )}
                                  </td>
                                ))}
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                    
                    {/* FAQ */}
                    <div className="mt-16 text-center">
                      <p className={cn("text-lg mb-4", copiloteMode ? "text-slate-300" : "text-slate-600")}>
                        Besoin d'aide pour choisir ?
                      </p>
                      <Button 
                        onClick={handleCallbackAction}
                        variant="outline"
                        className="rounded-xl"
                      >
                        <Phone className="w-4 h-4 mr-2" />
                        Parler à un conseiller
                      </Button>
                    </div>
                  </div>
                </section>
              )}
              
              {/* Service Page Footer */}
              <footer className={cn("py-16 border-t", copiloteMode ? "bg-slate-950 border-slate-800" : "bg-slate-900 border-slate-800")}>
                <div className="max-w-7xl mx-auto px-4">
                  <div className="grid md:grid-cols-6 gap-8 lg:gap-12 mb-12">
                    {/* Company Info */}
                    <div className="md:col-span-2">
                      <div className="flex items-center gap-3 mb-4">
                        {copiloteMode ? (
                          <div className={cn(
                            "w-14 h-14 rounded-full flex items-center justify-center border-2 shadow-lg",
                            `bg-gradient-to-br ${copiloteGradient} border-violet-400`
                          )}>
                            <Rocket className="w-6 h-6 text-white" />
                          </div>
                        ) : themeSettings?.logo?.lightUrl ? (
                          <img 
                            src={themeSettings.logo.lightUrl} 
                            alt={themeSettings.agency?.name || "Logo"}
                            className="h-14 w-auto object-contain"
                          />
                        ) : (
                          <div className={cn(
                            "w-14 h-14 rounded-full flex items-center justify-center border-2 shadow-lg",
                            "bg-gradient-to-br from-[#0f172a] to-[#1e3a5f] border-slate-600"
                          )}>
                            <span className="font-bold text-lg text-white">MSC</span>
                          </div>
                        )}
                        <div>
                          <p className="font-bold text-white text-lg">{copiloteMode ? "COPILOTE" : (themeSettings?.agency?.name || "MSC L'AGENCE")}</p>
                          <p className="text-sm text-slate-400">{copiloteMode ? "Mode Mission" : "Mon Service Com"}</p>
                        </div>
                      </div>
                      <p className="text-slate-400 text-sm mb-6">
                        {siteContent?.footer?.description || "Votre partenaire communication à Istres depuis 2012."}
                      </p>
                      
                      {/* Social Links */}
                      <div className="flex items-center gap-3">
                        {socialLinks?.facebook?.trim() && (
                          <a href={socialLinks.facebook} target="_blank" rel="noopener noreferrer" className={cn(
                            "w-10 h-10 rounded-full flex items-center justify-center transition-all",
                            copiloteMode ? "bg-slate-800 hover:bg-violet-600" : "bg-slate-800 hover:bg-cyan-500"
                          )}>
                            <Facebook className="w-5 h-5 text-white" />
                          </a>
                        )}
                        {socialLinks?.instagram?.trim() && (
                          <a href={socialLinks.instagram} target="_blank" rel="noopener noreferrer" className={cn(
                            "w-10 h-10 rounded-full flex items-center justify-center transition-all",
                            copiloteMode ? "bg-slate-800 hover:bg-violet-600" : "bg-slate-800 hover:bg-cyan-500"
                          )}>
                            <Instagram className="w-5 h-5 text-white" />
                          </a>
                        )}
                        {socialLinks?.linkedin?.trim() && (
                          <a href={socialLinks.linkedin} target="_blank" rel="noopener noreferrer" className={cn(
                            "w-10 h-10 rounded-full flex items-center justify-center transition-all",
                            copiloteMode ? "bg-slate-800 hover:bg-violet-600" : "bg-slate-800 hover:bg-cyan-500"
                          )}>
                            <Linkedin className="w-5 h-5 text-white" />
                          </a>
                        )}
                        {socialLinks?.youtube?.trim() && (
                          <a href={socialLinks.youtube} target="_blank" rel="noopener noreferrer" className={cn(
                            "w-10 h-10 rounded-full flex items-center justify-center transition-all",
                            copiloteMode ? "bg-slate-800 hover:bg-violet-600" : "bg-slate-800 hover:bg-cyan-500"
                          )}>
                            <Youtube className="w-5 h-5 text-white" />
                          </a>
                        )}
                        {socialLinks?.twitter?.trim() && (
                          <a href={socialLinks.twitter} target="_blank" rel="noopener noreferrer" className={cn(
                            "w-10 h-10 rounded-full flex items-center justify-center transition-all",
                            copiloteMode ? "bg-slate-800 hover:bg-violet-600" : "bg-slate-800 hover:bg-cyan-500"
                          )}>
                            <Twitter className="w-5 h-5 text-white" />
                          </a>
                        )}
                        {socialLinks?.tiktok?.trim() && (
                          <a href={socialLinks.tiktok} target="_blank" rel="noopener noreferrer" className={cn(
                            "w-10 h-10 rounded-full flex items-center justify-center transition-all",
                            copiloteMode ? "bg-slate-800 hover:bg-violet-600" : "bg-slate-800 hover:bg-cyan-500"
                          )}>
                            <svg className="w-5 h-5 text-white" viewBox="0 0 24 24" fill="currentColor">
                              <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/>
                            </svg>
                          </a>
                        )}
                      </div>
                    </div>
                    
                    {/* Services */}
                    <div>
                      <h4 className="font-semibold text-white mb-4">Services</h4>
                      <ul className="space-y-2 text-sm text-slate-400">
                        {activeServices.map(s => (
                          <li key={s.id}>
                            <Link to={`/site/${s.id}`} className="hover:text-cyan-400 transition-colors">{s.title}</Link>
                          </li>
                        ))}
                      </ul>
                    </div>
                    
                    {/* Agency */}
                    <div>
                      <h4 className="font-semibold text-white mb-4">L'agence</h4>
                      <ul className="space-y-2 text-sm text-slate-400">
                        <li><Link to="/site" className="hover:text-white transition-colors">Accueil</Link></li>
                        <li><Link to="/site#equipe" className="hover:text-white transition-colors">Notre équipe</Link></li>
                        <li><Link to="/site#realisations" className="hover:text-white transition-colors">Réalisations</Link></li>
                        <li><Link to="/site#contact" className="hover:text-white transition-colors">Contact</Link></li>
                      </ul>
                    </div>
                    
                    {/* Contact */}
                    <div>
                      <h4 className="font-semibold text-white mb-4">Contact</h4>
                      <ul className="space-y-3 text-sm text-slate-400">
                        <li className="flex items-center gap-2">
                          <Phone className="w-4 h-4 text-cyan-400" />
                          {agencyInfo?.phone || "04 84 49 27 00"}
                        </li>
                        <li className="flex items-center gap-2">
                          <Mail className="w-4 h-4 text-cyan-400" />
                          {agencyInfo?.email || "contact@agence-msc.fr"}
                        </li>
                        <li className="flex items-start gap-2">
                          <MapPin className="w-4 h-4 text-cyan-400 mt-0.5" />
                          <span>{agencyInfo?.address || "52 Rue des Pétouses"}<br/>{agencyInfo?.postal_code || "13800"} {agencyInfo?.city || "Istres"}<br/><em className="text-slate-500 text-xs not-italic">Siège social — Ne reçoit pas de public</em></span>
                        </li>
                      </ul>
                    </div>
                  </div>
                  
                  {/* Bottom Footer */}
                  <div className="border-t border-slate-800 pt-8">
                    <div className="flex flex-col md:flex-row items-center justify-between gap-4">
                      <p className="text-sm text-slate-500">
                        © {new Date().getFullYear()} MSC L'Agence. Tous droits réservés.
                      </p>
                      <div className="flex flex-wrap items-center justify-center gap-4 text-sm text-slate-500">
                        <Link to="/site/mentions-legales" className="hover:text-white transition-colors">Mentions légales</Link>
                        <span className="hidden md:inline">•</span>
                        <Link to="/site/confidentialite" className="hover:text-white transition-colors">Politique de confidentialité</Link>
                        <span className="hidden md:inline">•</span>
                        <Link to="/site/cgv" className="hover:text-white transition-colors">CGV</Link>
                      </div>
                      <p className="text-xs text-slate-600" title={`Déployé le ${BUILD_DATE}`}>
                        v{APP_VERSION}
                      </p>
                    </div>
                  </div>
                </div>
              </footer>
            </div>
          </div>
        )}
      </main>

      {/* ============================================ */}
      {/* MODALS */}
      {/* ============================================ */}
      
      {/* Callback Modal */}
      <Dialog open={showCallbackModal} onOpenChange={setShowCallbackModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Phone className="w-5 h-5 text-cyan-500" />
              Demande de rappel
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCallbackSubmit} className="space-y-4">
            <div>
              <Label>Votre nom *</Label>
              <Input value={callbackForm.name} onChange={(e) => setCallbackForm({...callbackForm, name: e.target.value})} required />
            </div>
            <div>
              <Label>Email *</Label>
              <Input type="email" value={callbackForm.email} onChange={(e) => setCallbackForm({...callbackForm, email: e.target.value})} placeholder="votre@email.com" required />
            </div>
            <div>
              <Label>Téléphone *</Label>
              <Input type="tel" value={callbackForm.phone} onChange={(e) => setCallbackForm({...callbackForm, phone: e.target.value})} required />
            </div>
            <div>
              <Label>Message</Label>
              <Textarea rows={3} value={callbackForm.message} onChange={(e) => setCallbackForm({...callbackForm, message: e.target.value})} />
            </div>
            <div className="flex gap-3">
              <Button type="button" variant="outline" onClick={() => setShowCallbackModal(false)} className="flex-1">Annuler</Button>
              <Button type="submit" className="flex-1 bg-cyan-500">Envoyer</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Hosting/Maintenance Popup - Shown before quote for web services */}
      <Dialog open={showHostingPopup} onOpenChange={setShowHostingPopup}>
        <DialogContent className="sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Server className="w-5 h-5 text-cyan-500" />
              Votre site aura besoin d'être hébergé
            </DialogTitle>
            <DialogDescription>
              Pour que votre site soit accessible en ligne, il doit être hébergé sur un serveur. 
              Souhaitez-vous ajouter une offre d'hébergement et/ou de maintenance à votre demande ?
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-4">
            {/* Hosting Plans Grid */}
            <div className="grid sm:grid-cols-3 gap-4 mb-6">
              {SUBSCRIPTION_OFFERS.map((plan) => (
                <div 
                  key={plan.id}
                  onClick={() => setSelectedHostingPlan(selectedHostingPlan === plan.id ? null : plan.id)}
                  className={cn(
                    "relative p-4 rounded-xl border-2 cursor-pointer transition-all",
                    selectedHostingPlan === plan.id 
                      ? "border-cyan-500 bg-cyan-50 shadow-lg" 
                      : "border-slate-200 hover:border-slate-300 hover:shadow"
                  )}
                >
                  {plan.recommended && (
                    <div className="absolute -top-2 left-1/2 -translate-x-1/2 px-2 py-0.5 bg-cyan-500 text-white text-xs rounded-full">
                      Recommandé
                    </div>
                  )}
                  
                  {/* Checkbox indicator */}
                  <div className={cn(
                    "absolute top-3 right-3 w-5 h-5 rounded-full border-2 flex items-center justify-center",
                    selectedHostingPlan === plan.id 
                      ? "border-cyan-500 bg-cyan-500" 
                      : "border-slate-300"
                  )}>
                    {selectedHostingPlan === plan.id && (
                      <CheckCircle className="w-4 h-4 text-white" />
                    )}
                  </div>
                  
                  <div className="text-center">
                    <plan.icon className={cn(
                      "w-8 h-8 mx-auto mb-2",
                      selectedHostingPlan === plan.id ? "text-cyan-500" : "text-slate-400"
                    )} />
                    <h4 className="font-semibold text-slate-800">{plan.name}</h4>
                    <div className="mt-1">
                      <span className="text-2xl font-bold text-cyan-600">{plan.priceMonthly}€</span>
                      <span className="text-slate-500 text-sm">/mois</span>
                    </div>
                    <p className="text-xs text-slate-500 mt-2">{plan.description}</p>
                  </div>
                  
                  {/* Features */}
                  <ul className="mt-3 space-y-1">
                    {plan.features.slice(0, 3).map((feature, i) => (
                      <li key={i} className="text-xs text-slate-600 flex items-center gap-1">
                        <CheckCircle className="w-3 h-3 text-cyan-500 flex-shrink-0" />
                        <span className="truncate">{feature}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
            
            {/* Info note */}
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
              <div className="text-sm">
                <p className="text-amber-800 font-medium">Pas obligatoire pour obtenir votre devis</p>
                <p className="text-amber-700 text-xs mt-1">
                  Vous pouvez continuer sans sélectionner d'offre. Nous pourrons en discuter lors de notre échange.
                </p>
              </div>
            </div>
          </div>
          
          <DialogFooter className="flex gap-2">
            <Button 
              type="button" 
              variant="outline" 
              onClick={() => {
                setSelectedHostingPlan(null);
                continueToQuote();
              }}
              className="flex-1"
            >
              Continuer sans hébergement
            </Button>
            <Button 
              type="button" 
              onClick={continueToQuote}
              className="flex-1 bg-gradient-to-r from-cyan-500 to-blue-600"
            >
              {selectedHostingPlan ? 'Ajouter et continuer' : 'Continuer'}
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Quote Modal */}
      <Dialog open={showQuoteModal} onOpenChange={setShowQuoteModal}>
        <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader className="sticky top-0 bg-white pb-2 z-10">
            <DialogTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5 text-cyan-500" />
              Demande de devis
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleQuoteSubmit} className="space-y-3 sm:space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
              <div>
                <Label className="text-sm">Nom *</Label>
                <Input value={quoteForm.name} onChange={(e) => setQuoteForm({...quoteForm, name: e.target.value})} required className="h-10" />
              </div>
              <div>
                <Label className="text-sm">Entreprise</Label>
                <Input value={quoteForm.company} onChange={(e) => setQuoteForm({...quoteForm, company: e.target.value})} className="h-10" />
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
              <div>
                <Label className="text-sm">Email *</Label>
                <Input type="email" value={quoteForm.email} onChange={(e) => setQuoteForm({...quoteForm, email: e.target.value})} required className="h-10" />
              </div>
              <div>
                <Label className="text-sm">Téléphone *</Label>
                <Input type="tel" value={quoteForm.phone} onChange={(e) => setQuoteForm({...quoteForm, phone: e.target.value})} required className="h-10" />
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
              <div>
                <Label className="text-sm">Service</Label>
                <select className="w-full border rounded-md px-3 py-2 bg-white h-10 text-sm" value={quoteForm.service} onChange={(e) => setQuoteForm({...quoteForm, service: e.target.value, solution: ''})}>
                  <option value="">Sélectionner...</option>
                  {activeServices.map(s => <option key={s.id} value={s.title}>{s.title}</option>)}
                </select>
              </div>
              <div>
                <Label className="text-sm">Prestation</Label>
                <Input 
                  value={quoteForm.solution} 
                  onChange={(e) => setQuoteForm({...quoteForm, solution: e.target.value})}
                  placeholder="Ex: Site Vitrine, Logo Premium..."
                  className="h-10"
                />
              </div>
            </div>
            {/* Show hosting selection if present */}
            {quoteForm.hosting && (
              <div className="bg-cyan-50 border border-cyan-200 rounded-lg p-2 sm:p-3 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Server className="w-4 h-4 text-cyan-600 flex-shrink-0" />
                  <span className="text-xs sm:text-sm text-cyan-800">
                    <strong>Hébergement :</strong> {quoteForm.hosting}
                  </span>
                </div>
                <button 
                  type="button"
                  onClick={() => setQuoteForm({...quoteForm, hosting: ''})}
                  className="text-cyan-600 hover:text-cyan-800 p-1"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            )}
            <div>
              <Label className="text-sm">Description du projet</Label>
              <Textarea rows={2} value={quoteForm.message} onChange={(e) => setQuoteForm({...quoteForm, message: e.target.value})} placeholder="Décrivez votre projet, vos besoins, votre budget approximatif..." className="resize-none text-sm" />
            </div>
            <div className="flex gap-2 sm:gap-3 pt-2 sticky bottom-0 bg-white pb-1">
              <Button type="button" variant="outline" onClick={() => setShowQuoteModal(false)} className="flex-1 h-10">Annuler</Button>
              <Button type="submit" className="flex-1 bg-gradient-to-r from-cyan-500 to-blue-600 h-10">Envoyer</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Brochure Download Modal */}
      <Dialog open={showBrochureModal} onOpenChange={setShowBrochureModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Download className="w-5 h-5 text-cyan-500" />
              Télécharger notre brochure
            </DialogTitle>
            <DialogDescription>
              Recevez notre brochure de présentation par email
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleBrochureSubmit} className="space-y-4">
            <div className="p-4 rounded-xl bg-gradient-to-r from-cyan-50 to-blue-50 border border-cyan-200">
              <div className="flex items-start gap-3">
                <div className="w-12 h-16 bg-white rounded shadow-md flex items-center justify-center flex-shrink-0">
                  <FileText className="w-6 h-6 text-cyan-500" />
                </div>
                <div>
                  <p className="font-semibold text-slate-800">Brochure MSC L'Agence</p>
                  <p className="text-sm text-slate-600">12 pages • PDF • 2.4 MB</p>
                  <p className="text-xs text-slate-500 mt-1">Services, réalisations, équipe...</p>
                </div>
              </div>
            </div>
            
            <div>
              <Label>Votre nom *</Label>
              <Input 
                value={brochureForm.name} 
                onChange={(e) => setBrochureForm({...brochureForm, name: e.target.value})} 
                placeholder="Jean Dupont"
                required 
              />
            </div>
            <div>
              <Label>Email *</Label>
              <Input 
                type="email"
                value={brochureForm.email} 
                onChange={(e) => setBrochureForm({...brochureForm, email: e.target.value})} 
                placeholder="jean@entreprise.fr"
                required 
              />
            </div>
            <div>
              <Label>Téléphone</Label>
              <Input 
                value={brochureForm.phone} 
                onChange={(e) => setBrochureForm({...brochureForm, phone: e.target.value})} 
                placeholder="06 12 34 56 78"
              />
            </div>
            <div>
              <Label>Entreprise</Label>
              <Input 
                value={brochureForm.company} 
                onChange={(e) => setBrochureForm({...brochureForm, company: e.target.value})} 
                placeholder="Nom de votre entreprise"
              />
            </div>
            
            <div className="flex gap-3 pt-2">
              <Button type="button" variant="outline" onClick={() => setShowBrochureModal(false)} className="flex-1">
                Annuler
              </Button>
              <Button type="submit" className="flex-1 bg-gradient-to-r from-cyan-500 to-blue-500 hover:from-cyan-600 hover:to-blue-600">
                <Mail className="w-4 h-4 mr-2" />
                Recevoir la brochure
              </Button>
            </div>
            
            <p className="text-xs text-slate-500 text-center">
              En soumettant ce formulaire, vous acceptez de recevoir des communications de notre part.
            </p>
          </form>
        </DialogContent>
      </Dialog>

      {/* Team Video Fullscreen Modal */}
      <Dialog open={showTeamVideoModal} onOpenChange={setShowTeamVideoModal}>
        <DialogContent className="sm:max-w-4xl p-0 bg-black border-none">
          <div className="relative aspect-video w-full">
            <button 
              onClick={() => setShowTeamVideoModal(false)}
              className="absolute top-4 right-4 z-50 w-10 h-10 rounded-full bg-black/50 hover:bg-black/70 flex items-center justify-center transition-all"
            >
              <X className="w-6 h-6 text-white" />
            </button>
            {teamPageConfig?.video_url && getYouTubeEmbedUrl(teamPageConfig.video_url) && (
              <iframe
                src={`${getYouTubeEmbedUrl(teamPageConfig.video_url)}?autoplay=1&rel=0`}
                className="w-full h-full"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                title="Team Video"
              />
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* COPILOTE Mission Modal - Multi-step Form */}
      <Dialog open={showMissionModal} onOpenChange={(open) => { setShowMissionModal(open); if (!open) setCopiloteStep(0); }}>
        <DialogContent className="sm:max-w-2xl bg-slate-950 border-orange-500/30 text-white max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-white">
              <Flame className="w-6 h-6 text-orange-500" />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-orange-500 to-red-500 font-bold">
                MISSION POSSIBLE - Demande d'éligibilité
              </span>
            </DialogTitle>
          </DialogHeader>
          
          {/* Step Indicator */}
          <div className="flex items-center justify-center gap-2 py-4">
            {[
              { num: 1, title: 'Entreprise', icon: Building },
              { num: 2, title: 'Communication', icon: Megaphone },
              { num: 3, title: 'Contact & Objectifs', icon: Target }
            ].map((step, idx) => (
              <React.Fragment key={idx}>
                <button
                  onClick={() => idx < copiloteStep && setCopiloteStep(idx)}
                  className={cn(
                    "flex items-center gap-2 px-3 py-2 rounded-full text-sm transition-all",
                    idx === copiloteStep
                      ? "bg-gradient-to-r from-orange-500 to-red-600 text-white"
                      : idx < copiloteStep
                        ? "bg-cyan-500/20 text-cyan-400 cursor-pointer hover:bg-cyan-500/30"
                        : "bg-slate-800 text-slate-500"
                  )}
                >
                  {idx < copiloteStep ? (
                    <CheckCircle className="w-4 h-4" />
                  ) : (
                    <step.icon className="w-4 h-4" />
                  )}
                  <span className="hidden sm:inline">{step.title}</span>
                  <span className="sm:hidden">{step.num}</span>
                </button>
                {idx < 2 && <ChevronRight className="w-4 h-4 text-slate-600" />}
              </React.Fragment>
            ))}
          </div>

          {/* Step 1: Company Info */}
          {copiloteStep === 0 && (
            <div className="space-y-5">
              <div className="p-4 rounded-xl bg-slate-900 border border-slate-800">
                <h3 className="text-lg font-semibold text-white mb-1 flex items-center gap-2">
                  <Building className="w-5 h-5 text-orange-500" />
                  Parlez-nous de votre entreprise
                </h3>
                <p className="text-sm text-slate-400">Ces informations nous permettent d'évaluer votre éligibilité</p>
              </div>
              
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <Label className="text-slate-300">Nom de l'entreprise *</Label>
                  <Input 
                    value={copiloteForm.companyName} 
                    onChange={(e) => setCopiloteForm({...copiloteForm, companyName: e.target.value})} 
                    className="bg-slate-900 border-slate-700 text-white focus:border-orange-500" 
                    placeholder="Votre entreprise"
                    required 
                  />
                </div>
                <div>
                  <Label className="text-slate-300">Secteur d'activité *</Label>
                  <select 
                    className="w-full h-10 border rounded-md px-3 py-2 bg-slate-900 border-slate-700 text-white focus:border-orange-500" 
                    value={copiloteForm.sector} 
                    onChange={(e) => setCopiloteForm({...copiloteForm, sector: e.target.value})} 
                    required
                  >
                    <option value="">Sélectionner...</option>
                    <option value="ecommerce">E-commerce / Retail</option>
                    <option value="services_b2b">Services aux entreprises (B2B)</option>
                    <option value="services_b2c">Services aux particuliers (B2C)</option>
                    <option value="industrie">Industrie / Manufacturing</option>
                    <option value="sante">Santé / Bien-être</option>
                    <option value="immobilier">Immobilier</option>
                    <option value="restauration">Restauration / Hôtellerie</option>
                    <option value="tech">Technologie / SaaS</option>
                    <option value="artisanat">Artisanat / Métiers manuels</option>
                    <option value="autre">Autre secteur</option>
                  </select>
                </div>
              </div>

              <div>
                <Label className="text-slate-300">Votre cible principale *</Label>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-2">
                  {[
                    { id: 'B2C', label: 'B2C', desc: 'Particuliers' },
                    { id: 'B2B', label: 'B2B', desc: 'Entreprises' },
                    { id: 'both', label: 'Les deux', desc: 'B2B et B2C' },
                    { id: 'BtoBtoC', label: 'BtoBtoC', desc: 'Distributeurs' }
                  ].map((opt) => (
                    <button
                      key={opt.id}
                      type="button"
                      onClick={() => setCopiloteForm({...copiloteForm, clientType: opt.id})}
                      className={cn(
                        "p-3 rounded-xl border-2 text-left transition-all",
                        copiloteForm.clientType === opt.id
                          ? "border-orange-500 bg-orange-500/10"
                          : "border-slate-700 hover:border-slate-600 bg-slate-900"
                      )}
                    >
                      <p className="font-medium text-white text-sm">{opt.label}</p>
                      <p className="text-xs text-slate-400">{opt.desc}</p>
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <Label className="text-slate-300">Panier moyen de vos clients</Label>
                <Input 
                  value={copiloteForm.avgBasket} 
                  onChange={(e) => setCopiloteForm({...copiloteForm, avgBasket: e.target.value})} 
                  className="bg-slate-900 border-slate-700 text-white" 
                  placeholder="Ex: 150€, 500-1000€, > 5000€..."
                />
              </div>

              <div className="flex justify-end pt-4">
                <Button 
                  onClick={() => setCopiloteStep(1)}
                  disabled={!copiloteForm.companyName || !copiloteForm.sector || !copiloteForm.clientType}
                  className="bg-gradient-to-r from-orange-500 to-red-600 hover:from-orange-600 hover:to-red-700"
                >
                  Continuer
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </div>
            </div>
          )}

          {/* Step 2: Communication */}
          {copiloteStep === 1 && (
            <div className="space-y-5">
              <div className="p-4 rounded-xl bg-slate-900 border border-slate-800">
                <h3 className="text-lg font-semibold text-white mb-1 flex items-center gap-2">
                  <Megaphone className="w-5 h-5 text-orange-500" />
                  Votre communication actuelle
                </h3>
                <p className="text-sm text-slate-400">Quels canaux utilisez-vous et comment gérez-vous votre communication ?</p>
              </div>

              <div>
                <Label className="text-slate-300 mb-2 block">Canaux de communication actifs</Label>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 max-h-[200px] overflow-y-auto p-1">
                  {[
                    { id: 'website', label: 'Site web', icon: Globe },
                    { id: 'google_ads', label: 'Google Ads', icon: Target },
                    { id: 'facebook', label: 'Facebook/Meta', icon: Facebook },
                    { id: 'instagram', label: 'Instagram', icon: Instagram },
                    { id: 'linkedin', label: 'LinkedIn', icon: Linkedin },
                    { id: 'email', label: 'Email marketing', icon: Mail },
                    { id: 'sms', label: 'SMS marketing', icon: MessageCircle },
                    { id: 'print', label: 'Print / Flyers', icon: FileText },
                    { id: 'aucun', label: 'Aucun canal', icon: XCircle }
                  ].map((channel) => (
                    <button
                      key={channel.id}
                      type="button"
                      onClick={() => {
                        const newChannels = copiloteForm.currentChannels.includes(channel.id)
                          ? copiloteForm.currentChannels.filter(c => c !== channel.id)
                          : [...copiloteForm.currentChannels, channel.id];
                        setCopiloteForm({...copiloteForm, currentChannels: newChannels});
                      }}
                      className={cn(
                        "flex items-center gap-2 p-2.5 rounded-lg border text-sm transition-all",
                        copiloteForm.currentChannels.includes(channel.id)
                          ? "bg-orange-500/20 border-orange-500/50 text-orange-400"
                          : "border-slate-700 text-slate-400 hover:border-slate-600"
                      )}
                    >
                      {copiloteForm.currentChannels.includes(channel.id) ? (
                        <CheckCircle className="w-4 h-4 flex-shrink-0" />
                      ) : (
                        <channel.icon className="w-4 h-4 flex-shrink-0" />
                      )}
                      <span className="truncate">{channel.label}</span>
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <Label className="text-slate-300">Qui gère votre communication ? *</Label>
                <select 
                  className="w-full h-10 border rounded-md px-3 py-2 bg-slate-900 border-slate-700 text-white mt-2" 
                  value={copiloteForm.whoManages} 
                  onChange={(e) => setCopiloteForm({...copiloteForm, whoManages: e.target.value})}
                  required
                >
                  <option value="">Sélectionner...</option>
                  <option value="moi">Je gère moi-même</option>
                  <option value="employe">Un employé en interne</option>
                  <option value="agence">Une agence</option>
                  <option value="freelance">Un freelance</option>
                  <option value="personne">Personne actuellement</option>
                </select>
              </div>

              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <Label className="text-slate-300">Force commerciale ?</Label>
                  <div className="flex gap-3 mt-2">
                    <Button
                      type="button"
                      variant={copiloteForm.hasCommercialTeam === true ? "default" : "outline"}
                      onClick={() => setCopiloteForm({...copiloteForm, hasCommercialTeam: true})}
                      className={copiloteForm.hasCommercialTeam === true ? "bg-orange-500" : "border-slate-700 text-slate-300"}
                      size="sm"
                    >
                      Oui
                    </Button>
                    <Button
                      type="button"
                      variant={copiloteForm.hasCommercialTeam === false ? "default" : "outline"}
                      onClick={() => setCopiloteForm({...copiloteForm, hasCommercialTeam: false})}
                      className={copiloteForm.hasCommercialTeam === false ? "bg-slate-600" : "border-slate-700 text-slate-300"}
                      size="sm"
                    >
                      Non
                    </Button>
                  </div>
                </div>
                <div>
                  <Label className="text-slate-300">1h/semaine disponible ?</Label>
                  <p className="text-xs text-slate-500 mb-2">Pour les points réguliers</p>
                  <div className="flex gap-3">
                    <Button
                      type="button"
                      variant={copiloteForm.availableTime === true ? "default" : "outline"}
                      onClick={() => setCopiloteForm({...copiloteForm, availableTime: true})}
                      className={copiloteForm.availableTime === true ? "bg-cyan-500" : "border-slate-700 text-slate-300"}
                      size="sm"
                    >
                      <CheckCircle className="w-3 h-3 mr-1" /> Oui
                    </Button>
                    <Button
                      type="button"
                      variant={copiloteForm.availableTime === false ? "default" : "outline"}
                      onClick={() => setCopiloteForm({...copiloteForm, availableTime: false})}
                      className={copiloteForm.availableTime === false ? "bg-amber-500" : "border-slate-700 text-slate-300"}
                      size="sm"
                    >
                      Difficile
                    </Button>
                  </div>
                </div>
              </div>

              <div className="flex justify-between pt-4">
                <Button variant="outline" onClick={() => setCopiloteStep(0)} className="border-slate-700 text-slate-300">
                  <ChevronLeft className="w-4 h-4 mr-2" />
                  Retour
                </Button>
                <Button 
                  onClick={() => setCopiloteStep(2)}
                  disabled={!copiloteForm.whoManages}
                  className="bg-gradient-to-r from-orange-500 to-red-600"
                >
                  Continuer
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </div>
            </div>
          )}

          {/* Step 3: Contact & Objectives */}
          {copiloteStep === 2 && (
            <div className="space-y-5">
              <div className="p-4 rounded-xl bg-slate-900 border border-slate-800">
                <h3 className="text-lg font-semibold text-white mb-1 flex items-center gap-2">
                  <Target className="w-5 h-5 text-orange-500" />
                  Vos coordonnées & objectifs
                </h3>
                <p className="text-sm text-slate-400">Dernière étape pour soumettre votre demande d'éligibilité</p>
              </div>

              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <Label className="text-slate-300">Votre nom *</Label>
                  <Input 
                    value={copiloteForm.contactName} 
                    onChange={(e) => setCopiloteForm({...copiloteForm, contactName: e.target.value})} 
                    className="bg-slate-900 border-slate-700 text-white" 
                    placeholder="Jean Dupont"
                    required 
                  />
                </div>
                <div>
                  <Label className="text-slate-300">Email *</Label>
                  <Input 
                    type="email"
                    value={copiloteForm.email} 
                    onChange={(e) => setCopiloteForm({...copiloteForm, email: e.target.value})} 
                    className="bg-slate-900 border-slate-700 text-white" 
                    placeholder="jean@entreprise.fr"
                    required 
                  />
                </div>
              </div>

              <div>
                <Label className="text-slate-300">Téléphone</Label>
                <Input 
                  value={copiloteForm.phone} 
                  onChange={(e) => setCopiloteForm({...copiloteForm, phone: e.target.value})} 
                  className="bg-slate-900 border-slate-700 text-white" 
                  placeholder="06 12 34 56 78"
                />
              </div>

              <div>
                <Label className="text-slate-300 mb-2 block">Vos objectifs principaux</Label>
                <div className="grid grid-cols-2 gap-2">
                  {[
                    { id: 'traffic', label: 'Plus de trafic web', icon: Globe },
                    { id: 'clients', label: 'Plus de clients/leads', icon: Users },
                    { id: 'basket', label: 'Augmenter panier moyen', icon: ShoppingBag },
                    { id: 'notoriety', label: 'Développer notoriété', icon: Megaphone },
                    { id: 'retention', label: 'Fidéliser clients', icon: Award },
                    { id: 'conversion', label: 'Améliorer conversion', icon: Target }
                  ].map((obj) => (
                    <button
                      key={obj.id}
                      type="button"
                      onClick={() => {
                        const newObjs = copiloteForm.objectives.includes(obj.id)
                          ? copiloteForm.objectives.filter(o => o !== obj.id)
                          : [...copiloteForm.objectives, obj.id];
                        setCopiloteForm({...copiloteForm, objectives: newObjs});
                      }}
                      className={cn(
                        "flex items-center gap-2 p-2.5 rounded-lg border text-sm transition-all text-left",
                        copiloteForm.objectives.includes(obj.id)
                          ? "bg-orange-500/20 border-orange-500/50 text-orange-400"
                          : "border-slate-700 text-slate-400 hover:border-slate-600"
                      )}
                    >
                      {copiloteForm.objectives.includes(obj.id) ? (
                        <CheckCircle className="w-4 h-4 flex-shrink-0" />
                      ) : (
                        <obj.icon className="w-4 h-4 flex-shrink-0" />
                      )}
                      <span>{obj.label}</span>
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <Label className="text-slate-300">Budget mensuel envisagé: <span className="text-orange-400 font-bold">{copiloteForm.budget.toLocaleString('fr-FR')}€</span></Label>
                <div className="mt-3 px-2">
                  <input 
                    type="range"
                    min={1000}
                    max={10000}
                    step={500}
                    value={copiloteForm.budget}
                    onChange={(e) => setCopiloteForm({...copiloteForm, budget: parseInt(e.target.value)})}
                    className="w-full accent-orange-500"
                  />
                  <div className="flex justify-between text-xs text-slate-500 mt-1">
                    <span>1 000€</span>
                    <span>10 000€+</span>
                  </div>
                </div>
              </div>

              <div>
                <Label className="text-slate-300">Vos attentes (optionnel)</Label>
                <Textarea 
                  rows={2} 
                  value={copiloteForm.expectations} 
                  onChange={(e) => setCopiloteForm({...copiloteForm, expectations: e.target.value})} 
                  className="bg-slate-900 border-slate-700 text-white" 
                  placeholder="Décrivez vos attentes, défis actuels, questions..."
                />
              </div>

              <div className="flex justify-between pt-4">
                <Button variant="outline" onClick={() => setCopiloteStep(1)} className="border-slate-700 text-slate-300">
                  <ChevronLeft className="w-4 h-4 mr-2" />
                  Retour
                </Button>
                <Button 
                  onClick={() => {
                    toast.success('✅ Demande COPILOTE envoyée ! Notre équipe évaluera votre éligibilité sous 48h.');
                    setShowMissionModal(false);
                    setCopiloteStep(0);
                    setCopiloteForm({
                      companyName: '', sector: '', clientType: '', avgBasket: '',
                      currentChannels: [], whoManages: '', hasCommercialTeam: null, availableTime: null,
                      contactName: '', email: '', phone: '', objectives: [], budget: 3000, expectations: ''
                    });
                  }}
                  disabled={!copiloteForm.contactName || !copiloteForm.email}
                  className="bg-gradient-to-r from-orange-500 to-red-600 hover:from-orange-600 hover:to-red-700"
                >
                  <Rocket className="w-4 h-4 mr-2" />
                  Soumettre ma demande
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Team Member Modal */}
      <Dialog open={!!selectedTeamMember} onOpenChange={() => setSelectedTeamMember(null)}>
        <DialogContent className="sm:max-w-lg">
          {selectedTeamMember && (
            <>
              <div className="flex items-start gap-4 mb-6">
                <div className={cn(
                  "w-20 h-20 rounded-2xl bg-gradient-to-br flex items-center justify-center text-white text-3xl font-bold shadow-lg",
                  selectedTeamMember.color
                )}>
                  {selectedTeamMember.name.charAt(0)}
                </div>
                <div>
                  <h3 className="text-xl font-bold text-slate-800">{selectedTeamMember.name}</h3>
                  <p className="text-cyan-600 font-medium">{selectedTeamMember.role}</p>
                  <p className="text-sm text-slate-500">{selectedTeamMember.experience} d'expérience</p>
                </div>
              </div>
              
              <div className="mb-6">
                <h4 className="font-semibold text-slate-800 mb-2">À propos</h4>
                <p className="text-slate-600">{selectedTeamMember.bio}</p>
              </div>
              
              {selectedTeamMember.skills?.length > 0 && (
                <div className="mb-6">
                  <h4 className="font-semibold text-slate-800 mb-2">Compétences</h4>
                  <div className="flex flex-wrap gap-2">
                    {selectedTeamMember.skills.map((skill, idx) => (
                      <span key={idx} className="px-3 py-1 rounded-full bg-slate-100 text-slate-700 text-sm font-medium">
                        {skill}
                      </span>
                    ))}
                  </div>
                </div>
              )}
              
              <div className="flex gap-3 pt-4 border-t">
                <Button variant="outline" className="flex-1" onClick={() => setSelectedTeamMember(null)}>
                  Fermer
                </Button>
                <Button className="flex-1 bg-cyan-500" onClick={() => { setSelectedTeamMember(null); handleCallbackAction(); }}>
                  <Mail className="w-4 h-4 mr-2" />
                  Contacter
                </Button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Schedule Modal */}
      <Dialog open={showScheduleModal} onOpenChange={setShowScheduleModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Clock className="w-5 h-5 text-cyan-500" />
              Horaires d'ouverture
              {hasSpecialHours() && (
                <Badge className="bg-amber-100 text-amber-700 ml-2">
                  <Sparkles className="w-3 h-3 mr-1" />
                  Horaires spéciales
                </Badge>
              )}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            {/* Current Status */}
            <div className={cn("p-4 rounded-xl flex items-center gap-3", agencyStatus.bg)}>
              <span className={cn("w-3 h-3 rounded-full", agencyStatus.dot)} />
              <div>
                <span className={cn("font-semibold", agencyStatus.color)}>{agencyStatus.label}</span>
                <span className="text-slate-500 text-sm ml-2">• {agencyStatus.sublabel}</span>
              </div>
            </div>

            {/* Weekly Schedule */}
            <div className="space-y-2">
              <h4 className="font-semibold text-slate-700 text-sm uppercase tracking-wider">Semaine type</h4>
              {Object.entries(agencySchedule.regular).map(([dayNum, dayInfo]) => {
                const isToday = new Date().getDay() === parseInt(dayNum);
                return (
                  <div 
                    key={dayNum} 
                    className={cn(
                      "flex items-center justify-between py-2 px-3 rounded-lg",
                      isToday ? "bg-cyan-50 border border-cyan-200" : "hover:bg-slate-50"
                    )}
                  >
                    <span className={cn("font-medium flex-shrink-0", isToday ? "text-cyan-700" : "text-slate-700")}>
                      {dayInfo.name}
                      {isToday && <span className="text-xs ml-1 text-cyan-500 hidden sm:inline">(Auj.)</span>}
                    </span>
                    <span className={cn("text-sm text-right", dayInfo.hours.length === 0 ? "text-red-500" : "text-slate-600")}>
                      {dayInfo.hours.length === 0 
                        ? "Fermé" 
                        : dayInfo.hours.map((h, i) => `${h.open}-${h.close}`).join(' / ')}
                    </span>
                  </div>
                );
              })}
            </div>

            {/* Special Hours */}
            {(agencySchedule.special || []).length > 0 && (
              <div className="space-y-2 pt-2 border-t">
                <h4 className="font-semibold text-amber-600 text-sm uppercase tracking-wider flex items-center gap-2">
                  <Sparkles className="w-4 h-4" />
                  Horaires spéciales à venir
                </h4>
                {(agencySchedule.special || [])
                  .filter(s => new Date(s.date) >= new Date())
                  .slice(0, 5)
                  .map((special, idx) => (
                    <div 
                      key={idx} 
                      className="flex items-center justify-between py-2 px-3 rounded-lg bg-amber-50 border border-amber-200"
                    >
                      <div>
                        <span className="font-medium text-amber-800">{special.name}</span>
                        <span className="text-xs text-amber-600 ml-2">
                          ({new Date(special.date).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' })})
                        </span>
                      </div>
                      <span className={cn("text-sm", special.hours.length === 0 ? "text-red-500" : "text-amber-700")}>
                        {special.hours.length === 0 
                          ? "Fermé" 
                          : special.hours.map((h, i) => `${h.open} - ${h.close}`).join(' / ')}
                      </span>
                    </div>
                  ))}
              </div>
            )}

            {/* Contact Info */}
            <div className="pt-4 border-t text-center">
              <p className="text-sm text-slate-500 mb-3">Besoin d'un rendez-vous en dehors de ces horaires ?</p>
              <Button 
                onClick={() => { setShowScheduleModal(false); handleCallbackAction(); }}
                className="bg-cyan-500 hover:bg-cyan-600"
              >
                <Phone className="w-4 h-4 mr-2" />
                Demander un rappel
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Onboarding Modal - For new clients */}
      <Dialog open={showOnboardingModal} onOpenChange={setShowOnboardingModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-center text-xl">
              Mon Espace Client
            </DialogTitle>
          </DialogHeader>
          
          <div className="py-6 space-y-6">
            {/* New Client Section */}
            <div className={cn(
              "p-6 rounded-2xl border-2 border-dashed transition-all hover:border-cyan-400 hover:bg-cyan-50/50 cursor-pointer group",
              "border-slate-200 bg-slate-50"
            )}
            onClick={() => { setShowOnboardingModal(false); navigate('/onboarding/client'); }}
            >
              <div className="text-center">
                <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-gradient-to-br from-cyan-500 to-blue-600 flex items-center justify-center group-hover:scale-110 transition-transform">
                  <Sparkles className="w-8 h-8 text-white" />
                </div>
                <h3 className="font-bold text-lg text-slate-800 mb-2">
                  Vous n'êtes pas encore client ?
                </h3>
                <p className="text-slate-600 text-sm mb-4">
                  Vous avez un projet ? Créez votre compte gratuitement et accédez à votre espace personnalisé !
                </p>
                <Button className="bg-gradient-to-r from-cyan-500 to-blue-600 text-white rounded-full px-6">
                  <Plus className="w-4 h-4 mr-2" />
                  Créer mon compte gratuit
                </Button>
              </div>
            </div>

            {/* Separator */}
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <span className="w-full border-t border-slate-200" />
              </div>
              <div className="relative flex justify-center text-xs uppercase">
                <span className="bg-white px-4 text-slate-500">ou</span>
              </div>
            </div>

            {/* Existing Client Section */}
            <div className={cn(
              "p-6 rounded-2xl border transition-all hover:border-slate-300 hover:shadow-md cursor-pointer group",
              "border-slate-200 bg-white"
            )}
            onClick={() => { setShowOnboardingModal(false); navigate('/login'); }}
            >
              <div className="text-center">
                <div className="w-14 h-14 mx-auto mb-3 rounded-full bg-slate-100 flex items-center justify-center group-hover:bg-slate-200 transition-colors">
                  <Users className="w-7 h-7 text-slate-600" />
                </div>
                <h3 className="font-semibold text-slate-800 mb-1">
                  Déjà client ?
                </h3>
                <p className="text-slate-500 text-sm mb-3">
                  Connectez-vous à votre espace client
                </p>
                <Button variant="outline" className="rounded-full px-6">
                  Se connecter
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </div>
            </div>

            {/* Contact option */}
            <div className="text-center pt-2">
              <p className="text-sm text-slate-500 mb-2">
                Besoin d'aide pour accéder à votre compte ?
              </p>
              <button 
                onClick={() => { setShowOnboardingModal(false); handleCallbackAction(); }}
                className="text-cyan-600 hover:text-cyan-700 font-medium text-sm inline-flex items-center gap-1"
              >
                <Phone className="w-4 h-4" />
                Nous contacter
              </button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* ============================================ */}
      {/* WELCOME POPUP - Dynamic announcement/greetings */}
      {/* ============================================ */}
      {welcomePopup.show && welcomePopup.config && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4">
          {/* Backdrop */}
          <div 
            className="absolute inset-0 bg-black/60 backdrop-blur-sm animate-in fade-in duration-300"
            onClick={handleClosePopup}
          />
          
          {/* Popup Content */}
          <div className="relative bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden animate-in zoom-in-95 fade-in duration-300">
            {/* Close button */}
            <button 
              onClick={handleClosePopup}
              className="absolute top-3 right-3 z-10 p-2 rounded-full bg-white/90 hover:bg-white shadow-lg transition-all hover:scale-110"
            >
              <X className="w-5 h-5 text-slate-600" />
            </button>

            {/* Media Section */}
            {welcomePopup.config.media_url && (
              <div className="aspect-video bg-slate-100">
                {(welcomePopup.config.media_type === 'youtube' || 
                  welcomePopup.config.media_type === 'video' ||
                  welcomePopup.config.media_url?.includes('youtube.com') || 
                  welcomePopup.config.media_url?.includes('youtu.be')) ? (
                  <iframe
                    src={`${getYouTubeEmbedUrl(welcomePopup.config.media_url)}?autoplay=0`}
                    className="w-full h-full"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                    title="Welcome video"
                  />
                ) : (
                  <img 
                    src={welcomePopup.config.media_url} 
                    alt="Welcome" 
                    className="w-full h-full object-cover"
                  />
                )}
              </div>
            )}

            {/* Text Content */}
            <div className="p-6 text-center">
              <h3 className="text-2xl font-bold text-slate-900 mb-3">
                {welcomePopup.config.title}
              </h3>
              <p className="text-slate-600 mb-6 leading-relaxed">
                {welcomePopup.config.text}
              </p>

              {/* CTA Button */}
              {welcomePopup.config.cta_text && (
                <Button 
                  onClick={handlePopupCTAClick}
                  size="lg"
                  className="bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-600 hover:to-blue-700 rounded-full px-8"
                >
                  {welcomePopup.config.cta_text}
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              )}

              {/* Skip link */}
              <button 
                onClick={handleClosePopup}
                className="block mx-auto mt-4 text-sm text-slate-400 hover:text-slate-600 transition-colors"
              >
                Fermer
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Beautiful Centered Confirmation Modal */}
      <ConfirmationModal 
        isOpen={confirmationModal.isOpen}
        onClose={() => setConfirmationModal({ ...confirmationModal, isOpen: false })}
        type={confirmationModal.type}
        userName={confirmationModal.userName}
      />

      {/* ============================================ */}
      {/* GAME CONTEST POPUP - Promotional popup for main game or annual game - Only when game section enabled */}
      {/* ============================================ */}
      {siteSettings?.game_section?.enabled && gamePopup.show && gamePopup.game && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center p-2 sm:p-4">
          {/* Backdrop */}
          <div 
            className="absolute inset-0 bg-black/80 backdrop-blur-sm animate-in fade-in duration-300"
            onClick={handleCloseGamePopup}
          />
          
          {/* Popup Content - Responsive */}
          <div className={`relative rounded-2xl sm:rounded-3xl shadow-2xl w-full max-w-[95vw] sm:max-w-lg max-h-[90vh] overflow-y-auto overflow-x-hidden animate-in zoom-in-95 fade-in duration-300 border-2 ${
            gamePopup.isAnnualGame 
              ? 'bg-gradient-to-br from-black via-red-950/50 to-black border-red-500/50' 
              : 'bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 border-cyan-500/20'
          }`}>
            {/* Decorative elements - Hidden on small screens */}
            <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none hidden sm:block">
              <div className={`absolute -top-24 -left-24 w-48 h-48 rounded-full blur-3xl ${gamePopup.isAnnualGame ? 'bg-red-600/30' : 'bg-cyan-500/20'}`} />
              <div className={`absolute -bottom-24 -right-24 w-48 h-48 rounded-full blur-3xl ${gamePopup.isAnnualGame ? 'bg-red-800/30' : 'bg-blue-600/20'}`} />
            </div>
            
            {/* Close button */}
            <button 
              onClick={handleCloseGamePopup}
              className={`absolute top-2 right-2 sm:top-4 sm:right-4 z-10 p-1.5 sm:p-2 rounded-full transition-all hover:scale-110 ${
                gamePopup.isAnnualGame 
                  ? 'bg-red-900/80 hover:bg-red-800' 
                  : 'bg-slate-700/80 hover:bg-slate-600'
              }`}
            >
              <X className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
            </button>

            {/* Content - Responsive padding */}
            <div className="relative p-4 sm:p-6 md:p-8 text-center">
              {/* Winner banner if game has a winner */}
              {gamePopup.isAnnualGame && gamePopup.game.winner_pseudo && (
                <div className="bg-gradient-to-r from-yellow-400 to-amber-500 -mx-4 sm:-mx-6 md:-mx-8 -mt-4 sm:-mt-6 md:-mt-8 mb-4 sm:mb-6 p-3 sm:p-4 animate-pulse">
                  <div className="flex items-center justify-center gap-2">
                    <Trophy className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
                    <span className="text-white font-bold text-sm sm:text-lg">
                      🏆 GAGNANT : {gamePopup.game.winner_pseudo}
                    </span>
                    <Trophy className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
                  </div>
                  <p className="text-white/80 text-xs sm:text-sm mt-1">Le coffre a été ouvert !</p>
                </div>
              )}
              
              {/* Badge - Responsive */}
              {gamePopup.isAnnualGame ? (
                <div className="inline-flex items-center gap-1.5 sm:gap-2 bg-gradient-to-r from-red-600 to-red-700 px-3 sm:px-4 py-1 sm:py-1.5 rounded-full text-xs sm:text-sm font-bold text-white mb-4 sm:mb-6">
                  <Lock className="w-3 h-3 sm:w-4 sm:h-4" />
                  {gamePopup.game.name || 'Le Coffre MSC'} {gamePopup.game.year}
                </div>
              ) : gamePopup.game.is_main_game && (
                <div className="inline-flex items-center gap-1.5 sm:gap-2 bg-gradient-to-r from-amber-500 to-orange-500 px-3 sm:px-4 py-1 sm:py-1.5 rounded-full text-xs sm:text-sm font-bold text-white mb-4 sm:mb-6">
                  <Trophy className="w-3 h-3 sm:w-4 sm:h-4" />
                  Grand Jeu Concours
                </div>
              )}

              {/* Vault Image for Annual Game or Icon for others */}
              {gamePopup.isAnnualGame ? (
                <div className="mb-4 sm:mb-6 relative">
                  {/* Realistic Vault Image */}
                  <div className="relative w-24 h-24 sm:w-32 sm:h-32 mx-auto">
                    <div className="absolute inset-0 bg-red-600/30 rounded-full blur-2xl scale-150" />
                    <img 
                      src="https://static.prod-images.emergentagent.com/jobs/8dc15090-04bb-47d6-96fa-05b7e2f22442/images/058d4c2372c7aef375491f9ceed35bc098d0b1753e8dfb0dc96bd76ef580d664.png" 
                      alt="Le Coffre MSC" 
                      className="w-full h-full object-contain relative z-10 drop-shadow-2xl"
                      style={{ filter: 'drop-shadow(0 0 20px rgba(220, 38, 38, 0.5))' }}
                    />
                  </div>
                </div>
              ) : (
                <div className={`w-16 h-16 sm:w-20 sm:h-20 md:w-24 md:h-24 mx-auto mb-4 sm:mb-6 rounded-full flex items-center justify-center shadow-xl bg-gradient-to-br from-cyan-500 to-blue-600 shadow-cyan-500/30`}>
                  <Gamepad2 className="w-8 h-8 sm:w-10 sm:h-10 md:w-12 md:h-12 text-white" />
                </div>
              )}

              {/* Title - Responsive */}
              <h3 className={`text-xl sm:text-2xl md:text-3xl font-bold mb-2 sm:mb-3 ${
                gamePopup.isAnnualGame ? 'text-white' : 'text-white'
              }`}>
                {gamePopup.isAnnualGame ? (
                  <>
                    <span className="text-white">LE </span>
                    <span className="text-red-500">COFFRE</span>
                    <span className="text-white"> MSC</span>
                  </>
                ) : gamePopup.game.title}
              </h3>
              
              {/* Description - Responsive */}
              <p className="text-white/80 text-sm sm:text-base mb-2 leading-relaxed px-2">
                {gamePopup.isAnnualGame 
                  ? "12 mois. 12 indices. 1 combinaison secrète à découvrir."
                  : (gamePopup.game.description || "Participez et tentez de gagner des lots exceptionnels !")
                }
              </p>
              
              {/* Legal notice for annual game */}
              {gamePopup.isAnnualGame && (
                <p className="text-white/50 text-xs mb-3">
                  🎯 Jeu sans obligation d'achat • Réservé aux professionnels
                </p>
              )}

              {/* Prize highlight - Compact */}
              <div className={`rounded-xl p-3 mb-4 ${
                gamePopup.isAnnualGame 
                  ? 'bg-gradient-to-br from-red-600/20 to-red-900/20 border border-red-500/40' 
                  : 'bg-amber-500/10 border border-amber-500/30'
              }`}>
                <div className={`flex items-center justify-center gap-2 mb-1 ${gamePopup.isAnnualGame ? 'text-red-400' : 'text-amber-300'}`}>
                  <Gift className="w-4 h-4" />
                  <span className="text-xs font-semibold">{gamePopup.isAnnualGame ? 'Gros lot du 24 décembre' : 'Lot à gagner'}</span>
                </div>
                <p className="text-base font-bold text-white">{gamePopup.game.prize}</p>
              </div>

              {/* Quick info row - Annual Game only */}
              {gamePopup.isAnnualGame && (
                <p className="text-center text-xs text-white/60 mb-4">
                  <span className="text-red-400">12 Quiz</span> • <span className="text-white">12 Indices</span> • <span className="text-red-400">1 Coffre à ouvrir</span>
                </p>
              )}

              {/* CTA Buttons - 3 options */}
              <div className="space-y-2 sm:space-y-3">
                {/* Mon Compte Joueur */}
                <Button 
                  onClick={() => {
                    handleCloseGamePopup();
                    window.location.href = '/mon-espace-joueur';
                  }}
                  size="lg"
                  className="w-full rounded-full py-3 sm:py-4 text-sm sm:text-base font-bold shadow-lg bg-gradient-to-r from-amber-500 to-orange-600 hover:from-amber-600 hover:to-orange-700 shadow-amber-500/30"
                >
                  <User className="w-4 h-4 sm:w-5 sm:h-5 mr-2" />
                  Mon Compte Joueur
                  <ArrowRight className="w-4 h-4 sm:w-5 sm:h-5 ml-2" />
                </Button>
                
                {/* Le jeu LE COFFRE */}
                <Button 
                  onClick={() => {
                    handleCloseGamePopup();
                    window.location.href = '/le-coffre-msc';
                  }}
                  size="lg"
                  className="w-full rounded-full py-3 sm:py-4 text-sm sm:text-base font-bold shadow-lg bg-gradient-to-r from-red-600 to-red-700 hover:from-red-500 hover:to-red-600 shadow-red-500/30"
                >
                  <Lock className="w-4 h-4 sm:w-5 sm:h-5 mr-2" />
                  Le jeu LE COFFRE
                  <ArrowRight className="w-4 h-4 sm:w-5 sm:h-5 ml-2" />
                </Button>
                
                {/* Les jeux ponctuels */}
                <Button 
                  onClick={() => {
                    handleCloseGamePopup();
                    window.location.href = '/jeux-concours';
                  }}
                  size="lg"
                  variant="outline"
                  className="w-full rounded-full py-3 sm:py-4 text-sm sm:text-base font-bold border-2 border-cyan-500/50 text-cyan-400 hover:bg-cyan-500/10 hover:border-cyan-400"
                >
                  <Gamepad2 className="w-4 h-4 sm:w-5 sm:h-5 mr-2" />
                  Les Jeux Ponctuels
                  <ArrowRight className="w-4 h-4 sm:w-5 sm:h-5 ml-2" />
                </Button>
              </div>

              {/* Don't show for a week checkbox */}
              <label className="flex items-center justify-center gap-2 mt-4 cursor-pointer group">
                <input 
                  type="checkbox"
                  checked={dontShowGamePopupWeek}
                  onChange={(e) => setDontShowGamePopupWeek(e.target.checked)}
                  className="w-4 h-4 rounded border-slate-500 bg-slate-700/50 text-cyan-500 focus:ring-cyan-500 focus:ring-offset-0 cursor-pointer"
                />
                <span className={`text-xs sm:text-sm transition-colors ${
                  gamePopup.isAnnualGame 
                    ? 'text-red-400/70 group-hover:text-red-300' 
                    : 'text-slate-400 group-hover:text-slate-300'
                }`}>
                  Ne plus afficher pendant 1 semaine
                </span>
              </label>

              {/* Skip link */}
              <button 
                onClick={handleCloseGamePopup}
                className={`block mx-auto mt-3 sm:mt-4 text-xs sm:text-sm transition-colors ${
                  gamePopup.isAnnualGame 
                    ? 'text-red-400/50 hover:text-red-300' 
                    : 'text-slate-500 hover:text-slate-300'
                }`}
              >
                Plus tard
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AgencyWebsitePage;
